import equal from "fast-deep-equal";
import { v4 as uuidv4 } from "uuid";
import type { FSWatcher } from "node:fs";
import { exec } from "node:child_process";
import { promisify } from "util";
import { join, resolve, sep } from "path";
import { homedir, hostname as osHostname } from "node:os";
import { z } from "zod";
import { loadPersistedConfig, savePersistedConfig } from "./persisted-config.js";
import { scanLenses } from "./lenses/scanner.js";
import { getOrCreateServerId } from "./server-id.js";
import { loadOrCreateDaemonKeyPair } from "./daemon-keypair.js";
import { getClaudeUsageProbe } from "./agent/providers/claude/usage-probe.js";
import type { ToolSet } from "ai";
import {
  isLegacyEditorTargetId,
  serializeAgentStreamEvent,
  type AgentSnapshotPayload,
  type AgentAttachment,
  type FileAttachment,
  type SessionUpload,
  type SessionInboundMessage,
  type SessionOutboundMessage,
  InitialAgentSnapshotPageLimit,
  type FileExplorerRequest,
  type WorkspaceSearchFilesRequest,
  type FileWriteRequest,
  type FileDeleteRequest,
  type FileExplorerDeleteRequest,
  type FileMkdirRequest,
  type FileMoveRequest,
  type FileDownloadTokenRequest,
  type GitSetupOptions,
  type ListTerminalsRequest,
  type SubscribeTerminalsRequest,
  type UnsubscribeTerminalsRequest,
  type CreateTerminalRequest,
  type StartWorkspaceScriptRequest,
  type SubscribeTerminalRequest,
  type UnsubscribeTerminalRequest,
  type TerminalInput,
  type CloseItemsRequest,
  type KillTerminalRequest,
  type CaptureTerminalRequest,
  type SubscribeCheckoutDiffRequest,
  type UnsubscribeCheckoutDiffRequest,
  LinkAccountRequestMessageSchema,
  type DirectorySuggestionsRequest,
  type ListDirectoryRequest,
  type ListFilesystemRootsRequest,
  type EditorTargetDescriptorPayload,
  type EditorTargetId,
  type ProjectPlacementPayload,
  type WorkspaceSetupSnapshot,
  type WorkspaceDescriptorPayload,
  type WorkspaceStateBucket,
} from "./messages.js";
import type { TerminalManager, TerminalsChangedEvent } from "../terminal/terminal-manager.js";
import { captureTerminalLines, type TerminalSession } from "../terminal/terminal.js";
import {
  TerminalStreamOpcode,
  encodeTerminalSnapshotPayload,
  encodeTerminalStreamFrame,
  decodeTerminalResizePayload,
  type TerminalStreamFrame,
} from "../shared/terminal-stream-protocol.js";
import { TTSManager } from "./agent/tts-manager.js";
import { STTManager } from "./agent/stt-manager.js";
import type { SpeechToTextProvider, TextToSpeechProvider } from "./speech/speech-provider.js";
import type { TurnDetectionProvider } from "./speech/turn-detection-provider.js";
import { maybePersistTtsDebugAudio } from "./agent/tts-debug.js";
import { isAppostleDictationDebugEnabled } from "./agent/recordings-debug.js";
import { listAvailableEditorTargets, openInEditorTarget } from "./editor-targets.js";
import { seedProjectBrand } from "./brand/seed.js";
import {
  DictationStreamManager,
  type DictationStreamOutboundMessage,
} from "./dictation/dictation-stream-manager.js";
import {
  createVoiceTurnController,
  type VoiceTurnController,
} from "./voice/voice-turn-controller.js";
import {
  buildConfigOverrides,
  extractTimestamps,
  toAgentPersistenceHandle,
} from "./persistence-hooks.js";
import { ensureAgentLoaded } from "./agent/agent-loading.js";
import { sendPromptToAgent, unarchiveAgentState } from "./agent/mcp-shared.js";
import { renderFileAttachmentFooter } from "./agent/prompt-attachments.js";
import {
  MAX_UPLOAD_BYTES_PER_MESSAGE,
  SessionUploadStore,
  UploadSizeError,
  originalUploadFileName,
} from "./uploads/session-upload-store.js";
import { SessionImageStore, type SessionImage } from "./uploads/session-image-store.js";
import { experimental_createMCPClient } from "ai";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp.js";
import type { VoiceCallerContext, VoiceSpeakHandler } from "./voice-types.js";
import { buildWorkspaceScriptPayloads } from "./script-status-projection.js";
import type { ScriptHealthState } from "./script-health-monitor.js";
import { spawnWorkspaceScript } from "./worktree-bootstrap.js";
import { deriveProjectSlug, readGitCommand } from "./workspace-git-metadata.js";
import type { WorkspaceScriptRuntimeStore } from "./workspace-script-runtime-store.js";
import type { DaemonConfigStore } from "./daemon-config-store.js";
import type { WorkspaceGitRuntimeSnapshot, WorkspaceGitService } from "./workspace-git-service.js";

import { buildProviderRegistry } from "./agent/provider-registry.js";
import type {
  AgentProviderRuntimeSettingsMap,
  ProviderOverride,
} from "./agent/provider-launch-config.js";
import { AgentManager, canUserAccessAgent } from "./agent/agent-manager.js";
import { ProviderSnapshotManager } from "./agent/provider-snapshot-manager.js";
import type {
  AgentTimelineCursor,
  AgentTimelineFetchDirection,
  ManagedAgent,
} from "./agent/agent-manager.js";
import { scheduleAgentMetadataGeneration } from "./agent/agent-metadata-generator.js";
import {
  buildStoredAgentPayload,
  resolveEffectiveThinkingOptionId,
  resolveStoredAgentPayloadUpdatedAt,
  toAgentPayload,
} from "./agent/agent-projections.js";
import { MAX_EXPLICIT_AGENT_TITLE_CHARS } from "./agent/agent-title-limits.js";
import {
  appendTimelineItemIfAgentKnown,
  emitLiveTimelineItemIfAgentKnown,
} from "./agent/timeline-append.js";
import {
  projectTimelineRows,
  selectTimelineWindowByProjectedLimit,
  type TimelineProjectionMode,
} from "./agent/timeline-projection.js";
import {
  DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
  StructuredAgentFallbackError,
  StructuredAgentResponseError,
  generateStructuredAgentResponseWithFallback,
} from "./agent/agent-response-loop.js";
import type {
  AgentPersistenceHandle,
  AgentPermissionResponse,
  AgentProvider,
  AgentPromptContentBlock,
  AgentPromptInput,
  AgentRunOptions,
  AgentSessionConfig,
  AgentStreamEvent,
  ProviderSnapshotEntry,
} from "./agent/agent-sdk-types.js";
import type { StoredAgentRecord } from "./agent/agent-storage.js";
import type { AgentStorage } from "./agent/agent-storage.js";
import {
  checkoutLiteFromGitSnapshot,
  normalizeWorkspaceId as normalizePersistedWorkspaceId,
  deriveWorkspaceId,
  deriveProjectRootPath,
  deriveProjectKind,
  deriveWorkspaceKind,
  deriveWorkspaceDisplayName,
  buildProjectPlacementForCwd as buildProjectPlacementForCwdStandalone,
} from "./workspace-registry-model.js";
import {
  createPersistedProjectRecord,
  createPersistedWorkspaceRecord,
  type PersistedProjectRecord,
  type PersistedWorkspaceRecord,
  type ProjectRegistry,
  type WorkspaceRegistry,
} from "./workspace-registry.js";
import { WorkspaceStacksService } from "./workspace-stacks-service.js";
import type { TodoStore, TodoRecord } from "./todo-store.js";
import type { TodoItem } from "../shared/messages.js";

function todoRecordToItem(record: TodoRecord): TodoItem {
  return {
    id: record.id,
    text: record.text,
    notes: record.notes,
    cwd: record.createdByCwd,
    createdByAgentId: record.createdByAgentId,
    createdAt: record.createdAt,
    completedAt: record.completedAt,
    position: record.position,
    subtodos: record.subtodos.map((s) => ({
      id: s.id,
      todoId: s.todoId,
      text: s.text,
      position: s.position,
      completedAt: s.completedAt,
      notes: s.notes,
      status: s.status,
      scheduledAt: s.scheduledAt,
      dueAt: s.dueAt,
      createdByUserId: s.createdByUserId,
      createdByUserName: s.createdByUserName,
      createdByUserImage: s.createdByUserImage,
      createdByAgentId: s.createdByAgentId,
      createdByHostId: s.createdByHostId,
      assignedToUserId: s.assignedToUserId,
      assignedToUserName: s.assignedToUserName,
      assignedToUserImage: s.assignedToUserImage,
      priority: s.priority,
      scheduleTargetHostId: s.scheduleTargetHostId,
      blockedBySubtodoId: s.blockedBySubtodoId,
    })),
    linkedAgentHostId: record.linkedAgentHostId,
    linkedAgentId: record.linkedAgentId,
    createdByUserId: record.createdByUserId,
    createdByUserName: record.createdByUserName,
    createdByUserImage: record.createdByUserImage,
    createdByHostId: record.createdByHostId,
    assignedToUserId: record.assignedToUserId,
    assignedToUserName: record.assignedToUserName,
    assignedToUserImage: record.assignedToUserImage,
    status: record.status,
    priority: record.priority,
    scheduledAt: record.scheduledAt,
    dueAt: record.dueAt,
    scheduleTargetHostId: record.scheduleTargetHostId,
    blockedByTodoId: record.blockedByTodoId,
    resultReport: record.resultReport,
  };
}
import {
  buildVoiceModeSystemPrompt,
  stripVoiceModeSystemPrompt,
  wrapSpokenInput,
} from "./voice-config.js";
import { isVoicePermissionAllowed } from "./voice-permission-policy.js";
import {
  listDirectoryEntries,
  readExplorerFile,
  searchWorkspaceFiles,
  writeTextFile,
  deleteFile,
  deleteEntry,
  createDirectory,
  moveEntry,
  getDownloadableFileInfo,
} from "./file-explorer/service.js";
import { DownloadTokenStore } from "./file-download/token-store.js";
import { PushTokenStore } from "./push/token-store.js";
import { WebPushStore } from "./push/web-push-store.js";
import { WebPushService } from "./push/web-push-service.js";
import { PushService } from "./push/push-service.js";
import { type WorktreeConfig } from "../utils/worktree.js";
import { runAsyncWorktreeBootstrap } from "./worktree-bootstrap.js";
import type { ScriptRouteStore } from "./script-proxy.js";
import {
  getCheckoutDiff,
  getCheckoutStatus,
  listUncommittedFiles,
  listBranchSuggestions,
  checkoutResolvedBranch,
  resolveBranchCheckout,
  type CheckoutExistingBranchResult,
  commitChanges,
  deleteBranch,
  mergeToBase,
  mergeFromBase,
  pullCurrentBranch,
  pushCurrentBranch,
  createPullRequest,
  searchGitHubIssuesAndPrs,
  searchGitLabIssuesAndMrs,
  createGitHubIssue,
  createGitLabIssue,
} from "../utils/checkout-git.js";
import { getProjectIcon } from "../utils/project-icon.js";
import { expandTilde } from "../utils/path.js";
import { createSkill, listSkills, writeSkillFrontmatter } from "./skills/scanner.js";
import { searchHomeDirectories, searchWorkspaceEntries } from "../utils/directory-suggestions.js";
import { listDirectoryContents } from "../utils/directory-listing.js";
import { cloneRepo, initRepoWithEmptyCommit } from "../utils/git-create.js";
import { listFilesystemRoots } from "../utils/list-filesystem-roots.js";
import { READ_ONLY_GIT_ENV, toCheckoutError } from "./checkout-git-utils.js";
import { CheckoutDiffManager } from "./checkout-diff-manager.js";
import type { LocalSpeechModelId } from "./speech/providers/local/models.js";
import { toResolver, type Resolvable } from "./speech/provider-resolver.js";
import type { SpeechReadinessSnapshot, SpeechReadinessState } from "./speech/speech-runtime.js";
import type pino from "pino";
import { resolveClientMessageId } from "./client-message-id.js";
import { ChatServiceError, FileBackedChatService } from "./chat/chat-service.js";
import { PreviewService, PreviewServiceError } from "./preview/preview-service.js";
import { notifyChatMentions } from "./chat/chat-mentions.js";
import { LoopService } from "./loop-service.js";
import { QuestService } from "./quest/service.js";
import { listRoles, createRole, writeRoleFrontmatter, moveRole } from "./roles/scanner.js";
import {
  listBrands,
  createBrand,
  writeBrandFrontmatter,
  copyBrandAsset,
  uploadBrandAsset,
  deleteBrandAsset,
  renameBrandAsset,
} from "./brands/scanner.js";
import { tokensJsonExists } from "./brands/tokens-adapter.js";
import { writeBrandTokens } from "./brands/tokens-writer.js";
import { generateAndApplyBrandTokens } from "./brand/token-generator.js";
import { analyzeAndApplyPhotographyStyle } from "./brand/photography-analyzer.js";
import {
  generateAndApplyLayout,
  generateWireframe,
  layoutQaNext,
} from "./brand/layout-generator.js";
import { ScheduleService } from "./schedule/service.js";
import { execCommand } from "../utils/spawn.js";
import type { ClaudeTokenStore } from "./claude-token-store.js";
import {
  createGitHubService,
  type GitHubService,
  type PullRequestTimelineItem,
} from "../services/github-service.js";
import { createGitLabService, type GitLabService } from "../services/gitlab-service.js";
import { createOAuthService, type OAuthService } from "../services/oauth-service.js";
import {
  createAppostleWorktree,
  type CreateAppostleWorktreeInput,
  type CreateAppostleWorktreeResult,
} from "./appostle-worktree-service.js";
import { createWorktreeCoreDeps } from "./worktree-core.js";
import {
  assertSafeGitRef as assertWorktreeSafeGitRef,
  buildAgentSessionConfig as buildWorktreeAgentSessionConfig,
  runWorktreeSetupInBackground as runWorktreeSetupInBackgroundSession,
  handleCreateAppostleWorktreeRequest as handleCreateWorktreeRequest,
  handleAppostleWorktreeArchiveRequest as handleWorktreeArchiveRequest,
  handleAppostleWorktreeListRequest as handleWorktreeListRequest,
  handleWorkspaceSetupStatusRequest as handleWorkspaceSetupStatusRequestMessage,
  killTerminalsUnderPath as killWorktreeTerminalsUnderPath,
} from "./worktree-session.js";
import { toWorktreeWireError } from "./worktree-errors.js";

const execAsync = promisify(exec);
const MAX_INITIAL_AGENT_TITLE_CHARS = Math.min(60, MAX_EXPLICIT_AGENT_TITLE_CHARS);
const WORKSPACE_GIT_WATCH_REMOVED_FINGERPRINT = "__removed__";

// TODO: Remove once all app store clients are on >=0.1.45 and understand arbitrary provider strings.
// Clients before 0.1.45 validate providers with z.enum(["claude", "codex", "opencode"]) and reject
// the entire session message if they encounter an unknown provider.
const LEGACY_PROVIDER_IDS = new Set(["claude", "codex", "opencode"]);
const MIN_VERSION_ALL_PROVIDERS = "0.1.45";
const MIN_VERSION_FLEXIBLE_EDITOR_IDS = "0.1.50";

function isAppVersionAtLeast(appVersion: string | null, minVersion: string): boolean {
  if (!appVersion) return false;
  // Strip RC/prerelease suffix: "0.1.45-rc.4" → "0.1.45"
  const base = appVersion.replace(/-.*$/, "");
  const parts = base.split(".").map(Number);
  const minParts = minVersion.split(".").map(Number);
  for (let i = 0; i < minParts.length; i++) {
    const a = parts[i] ?? 0;
    const b = minParts[i] ?? 0;
    if (a > b) return true;
    if (a < b) return false;
  }
  return true;
}

function clientSupportsAllProviders(appVersion: string | null): boolean {
  return isAppVersionAtLeast(appVersion, MIN_VERSION_ALL_PROVIDERS);
}

function clientSupportsFlexibleEditorIds(appVersion: string | null): boolean {
  return isAppVersionAtLeast(appVersion, MIN_VERSION_FLEXIBLE_EDITOR_IDS);
}

const MAX_TERMINAL_STREAM_SLOTS = 256;

type DeleteFencedAgentStorage = AgentStorage & {
  beginDelete(agentId: string): void;
};

function beginAgentDeleteIfSupported(agentStorage: AgentStorage, agentId: string): void {
  if ("beginDelete" in agentStorage && typeof agentStorage.beginDelete === "function") {
    (agentStorage as DeleteFencedAgentStorage).beginDelete(agentId);
  }
}

function deriveInitialAgentTitle(prompt: string): string | null {
  const firstContentLine = prompt
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  if (!firstContentLine) {
    return null;
  }
  const normalized = firstContentLine.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return null;
  }
  const clamped = normalized.slice(0, MAX_INITIAL_AGENT_TITLE_CHARS).trim();
  return clamped.length > 0 ? clamped : null;
}

export function resolveCreateAgentTitles(options: {
  configTitle?: string | null;
  initialPrompt?: string | null;
}): { explicitTitle: string | null; provisionalTitle: string | null } {
  const explicitTitle =
    typeof options.configTitle === "string" && options.configTitle.trim().length > 0
      ? options.configTitle.trim()
      : null;
  const trimmedPrompt = options.initialPrompt?.trim();
  const provisionalTitle =
    explicitTitle ?? (trimmedPrompt ? deriveInitialAgentTitle(trimmedPrompt) : null);

  return {
    explicitTitle,
    provisionalTitle,
  };
}

export function resolveWaitForFinishError(options: {
  status: "permission" | "error" | "idle";
  final: AgentSnapshotPayload | null;
}): string | null {
  if (options.status !== "error") {
    return null;
  }
  const message = options.final?.lastError;
  return typeof message === "string" && message.trim().length > 0 ? message : "Agent failed";
}

type ProcessingPhase = "idle" | "transcribing";

type WorkspaceGitWatchTarget = {
  cwd: string;
  watchers: FSWatcher[];
  debounceTimer: ReturnType<typeof setTimeout> | null;
  refreshPromise: Promise<void> | null;
  refreshQueued: boolean;
  latestFingerprint: string | null;
  lastBranchName: string | null;
};

type ActiveTerminalStream = {
  terminalId: string;
  slot: number;
  unsubscribe: () => void;
  needsSnapshot: boolean;
};

export type SessionRuntimeMetrics = {
  terminalDirectorySubscriptionCount: number;
  terminalSubscriptionCount: number;
  inflightRequests: number;
  peakInflightRequests: number;
};

type FetchAgentsRequestMessage = Extract<SessionInboundMessage, { type: "fetch_agents_request" }>;
type FetchAgentsRequestFilter = NonNullable<FetchAgentsRequestMessage["filter"]>;
type FetchAgentsRequestSort = NonNullable<FetchAgentsRequestMessage["sort"]>[number];
type FetchAgentsResponsePayload = Extract<
  SessionOutboundMessage,
  { type: "fetch_agents_response" }
>["payload"];
type FetchAgentsResponseEntry = FetchAgentsResponsePayload["entries"][number];
type FetchAgentsResponsePageInfo = FetchAgentsResponsePayload["pageInfo"];
type AgentUpdatePayload = Extract<SessionOutboundMessage, { type: "agent_update" }>["payload"];
type AgentUpdatesFilter = FetchAgentsRequestFilter;
type AgentUpdatesSubscriptionState = {
  subscriptionId: string;
  filter?: AgentUpdatesFilter;
  isBootstrapping: boolean;
  pendingUpdatesByAgentId: Map<string, AgentUpdatePayload>;
};
type FetchAgentsCursor = {
  sort: FetchAgentsRequestSort[];
  values: Record<string, string | number | null>;
  id: string;
};
type FetchWorkspacesRequestMessage = Extract<
  SessionInboundMessage,
  { type: "fetch_workspaces_request" }
>;
type FetchWorkspacesRequestFilter = NonNullable<FetchWorkspacesRequestMessage["filter"]>;
type FetchWorkspacesRequestSort = NonNullable<FetchWorkspacesRequestMessage["sort"]>[number];
type FetchWorkspacesResponsePayload = Extract<
  SessionOutboundMessage,
  { type: "fetch_workspaces_response" }
>["payload"];
type FetchWorkspacesResponseEntry = FetchWorkspacesResponsePayload["entries"][number];
type FetchWorkspacesResponsePageInfo = FetchWorkspacesResponsePayload["pageInfo"];
type WorkspaceUpdatePayload = Extract<
  SessionOutboundMessage,
  { type: "workspace_update" }
>["payload"];
type WorkspaceUpdatesFilter = FetchWorkspacesRequestFilter;
type WorkspaceUpdatesSubscriptionState = {
  subscriptionId: string;
  filter?: WorkspaceUpdatesFilter;
  isBootstrapping: boolean;
  pendingUpdatesByWorkspaceId: Map<string, WorkspaceUpdatePayload>;
  lastEmittedByWorkspaceId: Map<string, WorkspaceUpdatePayload>;
};
type FetchWorkspacesCursor = {
  sort: FetchWorkspacesRequestSort[];
  values: Record<string, string | number | null>;
  id: string;
};

function summarizeFetchWorkspacesEntries(entries: Iterable<FetchWorkspacesResponseEntry>): {
  count: number;
  projectIds: string[];
  statusCounts: Record<string, number>;
  workspaces: Array<{
    id: string;
    projectId: string;
    projectDisplayName: string;
    name: string;
    status: FetchWorkspacesResponseEntry["status"];
    workspaceKind: FetchWorkspacesResponseEntry["workspaceKind"];
    activityAt: string | null;
  }>;
} {
  const workspaces = Array.from(entries, (entry) => ({
    id: entry.id,
    projectId: entry.projectId,
    projectDisplayName: entry.projectDisplayName,
    name: entry.name,
    status: entry.status,
    workspaceKind: entry.workspaceKind,
    activityAt: entry.activityAt,
  }));
  const statusCounts = new Map<string, number>();
  for (const workspace of workspaces) {
    statusCounts.set(workspace.status, (statusCounts.get(workspace.status) ?? 0) + 1);
  }

  return {
    count: workspaces.length,
    projectIds: [...new Set(workspaces.map((workspace) => workspace.projectId))],
    statusCounts: Object.fromEntries(statusCounts),
    workspaces,
  };
}

/**
 * Build the user-facing text that gets recorded into the agent timeline when
 * the user sends a message. If the user typed something we use that verbatim;
 * if they only attached attachments we synthesize a short summary so the
 * timeline entry is non-empty.
 *
 * Why it has to be non-empty: timeline entries get replayed back to the
 * Anthropic API on later turns, and the Claude Agent SDK adds
 * `cache_control` markers at fixed breakpoints. The API rejects
 * `cache_control` on empty text blocks ("cache_control cannot be set for
 * empty text blocks"), so a single empty user message anywhere in the
 * history can fail every subsequent turn.
 *
 * It also matters for chip persistence after Cmd-R: the frontend reducer
 * historically dropped user_message events with empty text, which made
 * attachment-only sends disappear entirely on reload.
 */
function synthesizeRecordedUserText(
  rawText: string,
  files: readonly { fileName: string }[],
  imageCount: number = 0,
): string {
  if (rawText.trim().length > 0) {
    return rawText;
  }
  if (files.length === 0 && imageCount === 0) {
    return rawText;
  }
  const parts: string[] = [];
  if (imageCount > 0) {
    parts.push(imageCount === 1 ? "Sent 1 image" : `Sent ${imageCount} images`);
  }
  if (files.length > 0) {
    const names = files.map((f) => f.fileName).join(", ");
    parts.push(
      files.length === 1 ? `uploaded ${names}` : `uploaded ${files.length} files: ${names}`,
    );
  }
  return parts.join(" and ");
}

/**
 * Build the bytes-stripped image echo for the user_message timeline row.
 * One entry per input image, in the same order. Where persistence succeeded
 * we attach a `daemonRef` so any client can fetch bytes by id; the SessionImage
 * also gives us the canonical id and the (sanitized) fileName/size when the
 * client didn't supply them.
 */
function buildImageEcho(
  incoming: ReadonlyArray<{
    id?: string;
    mimeType: string;
    fileName?: string;
    byteSize?: number;
  }>,
  persisted: readonly SessionImage[],
): Array<{
  id: string;
  mimeType: string;
  fileName?: string;
  byteSize?: number;
  daemonRef?: { kind: "image"; attachmentId: string; relativePath: string };
}> {
  const out: Array<{
    id: string;
    mimeType: string;
    fileName?: string;
    byteSize?: number;
    daemonRef?: { kind: "image"; attachmentId: string; relativePath: string };
  }> = [];
  for (let i = 0; i < incoming.length; i++) {
    const img = incoming[i];
    const stored = persisted[i];
    // Prefer the persisted entry's id (always present, sanitized) when we
    // have one. Falls back to the client's id, then to skipping the echo
    // entirely if neither exists — without an id, downstream clients can't
    // reconcile the chip.
    const id = stored?.id ?? img?.id;
    if (!img || !id) continue;
    const fileName = img.fileName ?? stored?.fileName ?? undefined;
    const byteSize =
      typeof img.byteSize === "number"
        ? img.byteSize
        : typeof stored?.size === "number"
          ? stored.size
          : undefined;
    out.push({
      id,
      mimeType: img.mimeType,
      ...(fileName ? { fileName } : {}),
      ...(typeof byteSize === "number" ? { byteSize } : {}),
      ...(stored
        ? {
            daemonRef: {
              kind: "image" as const,
              attachmentId: stored.id,
              relativePath: stored.relativePath,
            },
          }
        : {}),
    });
  }
  return out;
}

class SessionRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SessionRequestError";
  }
}

const PCM_SAMPLE_RATE = 16000;
const PCM_CHANNELS = 1;
const PCM_BITS_PER_SAMPLE = 16;
const PCM_BYTES_PER_MS = (PCM_SAMPLE_RATE * PCM_CHANNELS * (PCM_BITS_PER_SAMPLE / 8)) / 1000;
const MIN_STREAMING_SEGMENT_DURATION_MS = 1000;
const MIN_STREAMING_SEGMENT_BYTES = Math.round(
  PCM_BYTES_PER_MS * MIN_STREAMING_SEGMENT_DURATION_MS,
);
const AgentIdSchema = z.string().uuid();
const VOICE_INTERRUPT_CONFIRMATION_MS = 500;

type VoiceModeBaseConfig = {
  systemPrompt?: string;
};

interface AudioBufferState {
  chunks: Buffer[];
  format: string;
  isPCM: boolean;
  totalPCMBytes: number;
}

// Stub types for features under development (modules not yet available)
type AgentMcpTransportFactory = () => Promise<unknown>;

type VoiceTranscriptionResultPayload = {
  text: string;
  requestId: string;
  language?: string;
  duration?: number;
  avgLogprob?: number;
  isLowConfidence?: boolean;
  byteLength?: number;
  format?: string;
  debugRecordingPath?: string;
};

export type SessionOptions = {
  clientId: string;
  appVersion?: string | null;
  /**
   * The peer's e2ee public key (base64) captured at handshake. Multi-tenant
   * session isolation uses this to resolve the connecting device → its
   * owning user (via the auth-server allow-list). `null` for local TCP
   * connections (same machine) and legacy daemons that don't surface the
   * pubkey yet.
   */
  peerPublicKeyB64?: string | null;
  /**
   * Authoritative auth-server user-id resolved from `peerPublicKeyB64`
   * via the pinned `auth-server-client.getUserIdForPubkey` lookup.
   * Required — the websocket-server rejects sessions whose peer pubkey
   * cannot be resolved to a userId (1008 `auth_required`), so by the time
   * a `Session` is constructed there is always an authoritative owner.
   * This is what `canAccessAgent` gates on — never trust the self-declared
   * `userId` below for access control.
   */
  ownerUserId: string;
  /**
   * Self-declared auth-server userId from the hello message. Display /
   * metadata use only (e.g. Claude profile dir naming). Authoritative
   * ownership lives in `ownerUserId`.
   */
  userId?: string | null;
  /** Display name of the connecting user (for Claude profile dir naming). */
  username?: string | null;
  onMessage: (msg: SessionOutboundMessage) => void;
  onBinaryMessage?: (frame: Uint8Array) => void;
  onLifecycleIntent?: (intent: SessionLifecycleIntent) => void;
  logger: pino.Logger;
  downloadTokenStore: DownloadTokenStore;
  pushTokenStore: PushTokenStore;
  claudeTokenStore: ClaudeTokenStore;
  webPushStore: WebPushStore;
  webPushService: WebPushService;
  pushService: PushService;
  appostleHome: string;
  agentManager: AgentManager;
  agentStorage: AgentStorage;
  projectRegistry: ProjectRegistry;
  workspaceRegistry: WorkspaceRegistry;
  workspaceStacksService: WorkspaceStacksService;
  todoStore: TodoStore;
  chatService: FileBackedChatService;
  previewService: PreviewService;
  scheduleService: ScheduleService;
  loopService: LoopService;
  questService: QuestService;
  checkoutDiffManager: CheckoutDiffManager;
  github?: GitHubService;
  gitlab?: GitLabService;
  oauth?: OAuthService;
  createAgentMcpTransport?: AgentMcpTransportFactory;
  workspaceGitService: WorkspaceGitService;
  daemonConfigStore: DaemonConfigStore;
  mcpBaseUrl?: string | null;
  stt: Resolvable<SpeechToTextProvider | null>;
  tts: Resolvable<TextToSpeechProvider | null>;
  terminalManager: TerminalManager | null;
  providerSnapshotManager?: ProviderSnapshotManager;
  scriptRouteStore?: ScriptRouteStore;
  scriptRuntimeStore?: WorkspaceScriptRuntimeStore;
  onBranchChanged?: (
    workspaceId: string,
    oldBranch: string | null,
    newBranch: string | null,
  ) => void;
  /**
   * Called after a workspace is archived locally. Bootstrap supplies this to
   * fan the removal out to the auth-server (delete the project row + its
   * stacks/tabs) so removed projects don't linger in recents/tab strips.
   */
  onWorkspaceArchived?: (workspace: PersistedWorkspaceRecord) => void;
  /**
   * Called after an archived workspace is flipped back to active (re-add of
   * a previously removed folder). The archive fan-out deleted the cloud
   * project (rows + stacks + tabs), so bootstrap supplies this to re-run
   * the workspace backfill and resurrect the cloud entry.
   */
  onWorkspaceUnarchived?: (workspace: PersistedWorkspaceRecord) => void;
  getDaemonTcpPort?: () => number | null;
  getDaemonTcpHost?: () => string | null;
  resolveScriptHealth?: (hostname: string) => ScriptHealthState | null;
  voice?: {
    turnDetection?: Resolvable<TurnDetectionProvider | null>;
  };
  voiceBridge?: {
    registerVoiceSpeakHandler?: (agentId: string, handler: VoiceSpeakHandler) => void;
    unregisterVoiceSpeakHandler?: (agentId: string) => void;
    registerVoiceCallerContext?: (agentId: string, context: VoiceCallerContext) => void;
    unregisterVoiceCallerContext?: (agentId: string) => void;
  };
  dictation?: {
    finalTimeoutMs?: number;
    stt?: Resolvable<SpeechToTextProvider | null>;
    getSpeechReadiness?: () => SpeechReadinessSnapshot;
  };
  agentProviderRuntimeSettings?: AgentProviderRuntimeSettingsMap;
  providerOverrides?: Record<string, ProviderOverride>;
  /**
   * Called after a successful `link_account_request` writes the persisted
   * account block. Bootstrap supplies this to bring the auth-server client
   * up immediately so the daemon transitions from single-tenant
   * (ownerUserId=null for everyone) to multi-tenant (pubkey-resolved
   * ownership) WITHOUT a manual daemon restart. Without it, the daemon
   * stays in single-tenant mode until the operator restarts the service —
   * which was the cause of the cross-user agent bleed on freshly-paired
   * shared hosts. See https://gitlab.com/ohlord-suite/appostle/-/issues/24.
   */
  onAccountLinked?: (account: {
    daemonToken: string;
    serverPubkey: string;
    userId: string;
    hostId: string;
    authServerUrl: string;
  }) => Promise<void>;
  /**
   * When true the daemon was started without an `account` block in
   * `~/.appostle/config.json` (Fix 2 — "awaiting_link" boot mode). The
   * session exists ONLY to receive a `link_account_request`; every other
   * inbound message is rejected with `daemon_awaiting_link_response` so the
   * client can route the user to the pairing flow.
   *
   * The daemon stays in this mode until a successful link triggers a
   * `process.exit(0)` (systemd restarts the unit and the second boot picks
   * the `linked` path).
   */
  awaitingLink?: boolean;
  /**
   * Called after this session pushes a fresh `todo_list_state` to itself
   * following any todo mutation. The websocket-server supplies this to fan the
   * update out to every OTHER session that is subscribed to the same
   * workspaceId, so all open clients see live todo changes without a reload.
   */
  onTodoMutation?: (workspaceId: string) => void;
};

export type SessionLifecycleIntent =
  | {
      type: "shutdown";
      clientId: string;
      requestId: string;
    }
  | {
      type: "restart";
      clientId: string;
      requestId: string;
      reason?: string;
    };

type CheckoutPrStatusPayload = Extract<
  SessionOutboundMessage,
  { type: "checkout_pr_status_response" }
>["payload"];
type CheckoutPrStatusPayloadStatus = NonNullable<CheckoutPrStatusPayload["status"]>;
type PullRequestTimelinePayload = Extract<
  SessionOutboundMessage,
  { type: "pull_request_timeline_response" }
>["payload"];
type PullRequestTimelinePayloadItem = PullRequestTimelinePayload["items"][number];

type VoiceFeatureUnavailableContext = {
  reasonCode: SpeechReadinessSnapshot["voiceFeature"]["reasonCode"];
  message: string;
  retryable: boolean;
  missingModelIds: LocalSpeechModelId[];
};

type VoiceFeatureUnavailableResponseMetadata = {
  reasonCode?: SpeechReadinessSnapshot["voiceFeature"]["reasonCode"];
  retryable?: boolean;
  missingModelIds?: LocalSpeechModelId[];
};

class VoiceFeatureUnavailableError extends Error {
  readonly reasonCode: SpeechReadinessSnapshot["voiceFeature"]["reasonCode"];
  readonly retryable: boolean;
  readonly missingModelIds: LocalSpeechModelId[];

  constructor(context: VoiceFeatureUnavailableContext) {
    super(context.message);
    this.name = "VoiceFeatureUnavailableError";
    this.reasonCode = context.reasonCode;
    this.retryable = context.retryable;
    this.missingModelIds = [...context.missingModelIds];
  }
}

function convertPCMToWavBuffer(
  pcmBuffer: Buffer,
  sampleRate: number,
  channels: number,
  bitsPerSample: number,
): Buffer {
  const headerSize = 44;
  const wavBuffer = Buffer.alloc(headerSize + pcmBuffer.length);
  const byteRate = (sampleRate * channels * bitsPerSample) / 8;
  const blockAlign = (channels * bitsPerSample) / 8;

  wavBuffer.write("RIFF", 0);
  wavBuffer.writeUInt32LE(36 + pcmBuffer.length, 4);
  wavBuffer.write("WAVE", 8);
  wavBuffer.write("fmt ", 12);
  wavBuffer.writeUInt32LE(16, 16);
  wavBuffer.writeUInt16LE(1, 20);
  wavBuffer.writeUInt16LE(channels, 22);
  wavBuffer.writeUInt32LE(sampleRate, 24);
  wavBuffer.writeUInt32LE(byteRate, 28);
  wavBuffer.writeUInt16LE(blockAlign, 32);
  wavBuffer.writeUInt16LE(bitsPerSample, 34);
  wavBuffer.write("data", 36);
  wavBuffer.writeUInt32LE(pcmBuffer.length, 40);
  pcmBuffer.copy(wavBuffer, 44);

  return wavBuffer;
}

/**
 * Probe whether an existing daemon-token is still recognized by the auth-server.
 * Used by handleLinkAccountRequest to detect a stale persisted `account` block:
 * if the auth-server's host row has been wiped (manual DB cleanup, restore from
 * backup, regression test) the daemon-token is orphaned and the link is dead.
 *
 *   "valid"   — auth-server returned 2xx for /daemon/allowlist (token recognized).
 *   "stale"   — 401 (unauthenticated) or 404 (host_gone) — relink is safe.
 *   "unknown" — anything else (network failure, 5xx, timeout). Caller should
 *               err conservative and refuse the relink.
 *
 * Short timeout (5s) — this runs inline on a relink attempt; we don't want to
 * block the user's pair flow on a slow auth-server.
 */
async function probeDaemonTokenStatus(args: {
  authServerUrl: string;
  daemonToken: string;
  log: pino.Logger;
}): Promise<"valid" | "stale" | "unknown"> {
  const url = new URL("/daemon/allowlist", args.authServerUrl).toString();
  const ac = new AbortController();
  const timeoutHandle = setTimeout(() => ac.abort(), 5_000);
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { authorization: `Bearer ${args.daemonToken}` },
      signal: ac.signal,
    });
    if (response.ok) return "valid";
    if (response.status === 401 || response.status === 404) return "stale";
    return "unknown";
  } catch (err) {
    args.log.warn({ err }, "probeDaemonTokenStatus: probe failed");
    return "unknown";
  } finally {
    clearTimeout(timeoutHandle);
  }
}

/**
 * Session represents a single connected client session.
 * It owns all state management, orchestration logic, and message processing.
 * Session has no knowledge of WebSockets - it only emits and receives messages.
 */
export class Session {
  private readonly clientId: string;
  private appVersion: string | null;
  private readonly sessionId: string;
  /** Auth-server userId of the connecting user (null = unknown / pre-account era). */
  private readonly userId: string | null;
  /** Display name of the connecting user. */
  private readonly username: string | null;
  private readonly onMessage: (msg: SessionOutboundMessage) => void;
  private readonly onBinaryMessage: ((frame: Uint8Array) => void) | null;
  private readonly onLifecycleIntent: ((intent: SessionLifecycleIntent) => void) | null;
  private readonly sessionLogger: pino.Logger;
  private readonly appostleHome: string;

  // State machine
  private abortController: AbortController;
  private processingPhase: ProcessingPhase = "idle";

  // Voice mode state
  private isVoiceMode = false;
  private speechInProgress = false;
  private pendingVoiceSpeechStartAt: number | null = null;
  private pendingVoiceSpeechTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly dictationStreamManager: DictationStreamManager;
  private readonly resolveVoiceTurnDetection: () => TurnDetectionProvider | null;
  private voiceTurnController: VoiceTurnController | null = null;
  private voiceInputChunkCount = 0;
  private voiceInputBytes = 0;
  private voiceInputWindowStartedAt = Date.now();

  // Audio buffering for interruption handling
  private pendingAudioSegments: Array<{ audio: Buffer; format: string }> = [];
  private bufferTimeout: ReturnType<typeof setTimeout> | null = null;
  private audioBuffer: AudioBufferState | null = null;

  // Optional TTS debug capture (persisted per utterance)
  private readonly ttsDebugStreams = new Map<string, { format: string; chunks: Buffer[] }>();

  // Per-session managers
  private readonly ttsManager: TTSManager;
  private readonly sttManager: STTManager;

  // Per-session MCP client and tools
  private agentMcpClient: Awaited<ReturnType<typeof experimental_createMCPClient>> | null = null;
  private agentTools: ToolSet | null = null;
  private agentManager: AgentManager;
  private readonly agentStorage: AgentStorage;
  /**
   * Peer device's e2ee pubkey (base64) for this session — captured once at
   * connect time, immutable for the connection's lifetime. Null for local
   * TCP. The next phase resolves this to a `userId` via the auth-server
   * allow-list for per-user agent filtering.
   */
  private readonly peerPublicKeyB64: string | null;
  /**
   * The user-id that owns the device on the other side of this connection.
   * Resolved at session-create time from `peerPublicKeyB64` via the
   * auth-server allow-list. Always present — the websocket-server rejects
   * sessions whose peer cannot be resolved to a userId. `canAccessAgent`
   * checks and all downstream RPC gates read this.
   */
  private readonly ownerUserId: string;
  /** Persists user-attached files to `<workspace>/.appostle/tmp/uploaded_files/` so agents can read them. */
  private readonly uploadStore: SessionUploadStore;
  /**
   * Persists user-attached images to `<workspace>/.appostle/tmp/uploaded_images/`.
   * Unlike files, images aren't read by the agent's `Read` tool — they're
   * embedded as base64 multimodal blocks in the LLM prompt. This store exists
   * so chat clients can fetch image bytes back by id after a hard-refresh or
   * from another paired device, without round-tripping bytes through chat
   * history. Wired in Phase 2 of the attachment-fetch-model migration.
   */
  private readonly imageStore: SessionImageStore;
  private readonly projectRegistry: ProjectRegistry;
  private readonly workspaceRegistry: WorkspaceRegistry;
  private readonly workspaceStacksService: WorkspaceStacksService;
  private readonly todoStore: TodoStore;
  /**
   * Set of workspaceIds this session has subscribed to for todo-list updates.
   * Populated by `todo_list_subscribe` and emptied on `todo_list_unsubscribe`
   * or `cleanup()`. On any mutation the session re-fetches from the auth-server
   * and pushes a fresh `todo_list_state` to itself.
   */
  private readonly subscribedTodoWorkspaceIds = new Set<string>();
  private readonly chatService: FileBackedChatService;
  private readonly previewService: PreviewService;
  private readonly scheduleService: ScheduleService;
  private readonly loopService: LoopService;
  private readonly questService: QuestService;
  private readonly checkoutDiffManager: CheckoutDiffManager;
  private readonly github: GitHubService;
  private readonly gitlab: GitLabService;
  private readonly oauth: OAuthService;
  private readonly workspaceGitService: WorkspaceGitService;
  private readonly daemonConfigStore: DaemonConfigStore;
  private readonly mcpBaseUrl: string | null;
  private readonly downloadTokenStore: DownloadTokenStore;
  private readonly pushTokenStore: PushTokenStore;
  private readonly claudeTokenStore: ClaudeTokenStore;
  private readonly webPushStore: WebPushStore;
  private readonly webPushService: WebPushService;
  private readonly pushService: PushService;
  private readonly providerRegistry: ReturnType<typeof buildProviderRegistry>;
  private unsubscribeAgentEvents: (() => void) | null = null;
  private agentUpdatesSubscription: AgentUpdatesSubscriptionState | null = null;
  private workspaceUpdatesSubscription: WorkspaceUpdatesSubscriptionState | null = null;
  private clientActivity: {
    deviceType: "web" | "mobile";
    focusedAgentId: string | null;
    lastActivityAt: Date;
    appVisible: boolean;
    appVisibilityChangedAt: Date;
  } | null = null;
  private readonly MOBILE_BACKGROUND_STREAM_GRACE_MS = 60_000;
  private readonly terminalManager: TerminalManager | null;
  private readonly providerSnapshotManager: ProviderSnapshotManager | null;
  private unsubscribeProviderSnapshotEvents: (() => void) | null = null;
  private readonly scriptRouteStore: ScriptRouteStore | null;
  private readonly scriptRuntimeStore: WorkspaceScriptRuntimeStore | null;
  private readonly onBranchChanged?: (
    workspaceId: string,
    oldBranch: string | null,
    newBranch: string | null,
  ) => void;
  private readonly onWorkspaceArchived?: (workspace: PersistedWorkspaceRecord) => void;
  private readonly onWorkspaceUnarchived?: (workspace: PersistedWorkspaceRecord) => void;
  private readonly getDaemonTcpPort: (() => number | null) | null;
  private readonly getDaemonTcpHost: (() => string | null) | null;
  private readonly resolveScriptHealth: ((hostname: string) => ScriptHealthState | null) | null;
  private readonly subscribedTerminalDirectories = new Set<string>();
  private unsubscribeTerminalsChanged: (() => void) | null = null;
  private terminalExitSubscriptions: Map<string, () => void> = new Map();
  private readonly activeTerminalStreams = new Map<number, ActiveTerminalStream>();
  private readonly terminalIdToSlot = new Map<string, number>();
  private nextTerminalSlot = 0;
  private inflightRequests = 0;
  private peakInflightRequests = 0;
  private readonly checkoutDiffSubscriptions = new Map<string, () => void>();
  private readonly workspaceGitWatchTargets = new Map<string, WorkspaceGitWatchTarget>();
  private readonly workspaceSetupSnapshots = new Map<string, WorkspaceSetupSnapshot>();
  private readonly workspaceGitFetchSubscriptions = new Map<string, () => void>();
  private readonly workspaceGitSubscriptions = new Map<string, () => void>();
  private readonly registerVoiceSpeakHandler?: (
    agentId: string,
    handler: VoiceSpeakHandler,
  ) => void;
  private readonly unregisterVoiceSpeakHandler?: (agentId: string) => void;
  private readonly registerVoiceCallerContext?: (
    agentId: string,
    context: VoiceCallerContext,
  ) => void;
  private readonly unregisterVoiceCallerContext?: (agentId: string) => void;
  private readonly getSpeechReadiness?: () => SpeechReadinessSnapshot;
  private readonly agentProviderRuntimeSettings: AgentProviderRuntimeSettingsMap | undefined;
  private readonly providerOverrides: Record<string, ProviderOverride> | undefined;
  private readonly onAccountLinked?: SessionOptions["onAccountLinked"];
  /** See SessionOptions.awaitingLink. */
  private readonly awaitingLink: boolean;
  private readonly onTodoMutation?: (workspaceId: string) => void;
  private voiceModeAgentId: string | null = null;
  private voiceModeBaseConfig: VoiceModeBaseConfig | null = null;

  constructor(options: SessionOptions) {
    const {
      clientId,
      appVersion,
      peerPublicKeyB64,
      ownerUserId,
      onMessage,
      onBinaryMessage,
      onLifecycleIntent,
      logger,
      downloadTokenStore,
      pushTokenStore,
      claudeTokenStore,
      webPushStore,
      webPushService,
      pushService,
      appostleHome,
      agentManager,
      agentStorage,
      projectRegistry,
      workspaceRegistry,
      workspaceStacksService,
      todoStore,
      chatService,
      previewService,
      scheduleService,
      loopService,
      questService,
      checkoutDiffManager,
      github,
      gitlab,
      oauth,
      workspaceGitService,
      daemonConfigStore,
      mcpBaseUrl,
      stt,
      tts,
      terminalManager,
      providerSnapshotManager,
      scriptRouteStore,
      scriptRuntimeStore,
      onBranchChanged,
      onWorkspaceArchived,
      onWorkspaceUnarchived,
      getDaemonTcpPort,
      getDaemonTcpHost,
      resolveScriptHealth,
      voice,
      voiceBridge,
      dictation,
      agentProviderRuntimeSettings,
      providerOverrides,
    } = options;
    this.clientId = clientId;
    this.appVersion = appVersion ?? null;
    this.sessionId = uuidv4();
    this.userId = options.userId ?? null;
    this.username = options.username ?? null;
    this.onMessage = onMessage;
    this.onBinaryMessage = onBinaryMessage ?? null;
    this.onLifecycleIntent = onLifecycleIntent ?? null;
    this.downloadTokenStore = downloadTokenStore;
    this.pushTokenStore = pushTokenStore;
    this.claudeTokenStore = claudeTokenStore;
    this.webPushStore = webPushStore;
    this.webPushService = webPushService;
    this.pushService = pushService;
    this.appostleHome = appostleHome;
    this.sessionLogger = logger.child({
      module: "session",
      clientId: this.clientId,
      sessionId: this.sessionId,
    });
    this.agentManager = agentManager;
    this.agentStorage = agentStorage;
    this.peerPublicKeyB64 = peerPublicKeyB64 ?? null;
    this.ownerUserId = ownerUserId;
    this.uploadStore = new SessionUploadStore({ logger: this.sessionLogger });
    this.imageStore = new SessionImageStore({ logger: this.sessionLogger });
    this.projectRegistry = projectRegistry;
    this.workspaceRegistry = workspaceRegistry;
    this.workspaceStacksService = workspaceStacksService;
    this.todoStore = todoStore;
    this.chatService = chatService;
    this.previewService = previewService;
    this.scheduleService = scheduleService;
    this.loopService = loopService;
    this.questService = questService;
    this.checkoutDiffManager = checkoutDiffManager;
    this.github = github ?? createGitHubService();
    this.gitlab = gitlab ?? createGitLabService();
    this.oauth =
      oauth ??
      createOAuthService({
        appostleHome: this.appostleHome,
        logger: this.sessionLogger,
      });
    this.workspaceGitService = workspaceGitService;
    this.daemonConfigStore = daemonConfigStore;
    this.mcpBaseUrl = mcpBaseUrl ?? null;
    this.terminalManager = terminalManager;
    this.providerSnapshotManager = providerSnapshotManager ?? null;
    this.scriptRouteStore = scriptRouteStore ?? null;
    this.scriptRuntimeStore = scriptRuntimeStore ?? null;
    this.onBranchChanged = onBranchChanged;
    this.onWorkspaceArchived = onWorkspaceArchived;
    this.onWorkspaceUnarchived = onWorkspaceUnarchived;
    this.getDaemonTcpPort = getDaemonTcpPort ?? null;
    this.getDaemonTcpHost = getDaemonTcpHost ?? null;
    this.resolveScriptHealth = resolveScriptHealth ?? null;
    if (this.terminalManager) {
      this.unsubscribeTerminalsChanged = this.terminalManager.subscribeTerminalsChanged((event) =>
        this.handleTerminalsChanged(event),
      );
    }
    if (this.providerSnapshotManager) {
      const handleProviderSnapshotChange = (entries: ProviderSnapshotEntry[], cwd?: string) => {
        // COMPAT(providersSnapshot): keep provider visibility gating for older clients.
        const visibleEntries = entries.filter((entry) =>
          this.isProviderVisibleToClient(entry.provider),
        );
        this.emit({
          type: "providers_snapshot_update",
          payload: {
            cwd,
            entries: visibleEntries,
            generatedAt: new Date().toISOString(),
          },
        });
      };
      this.providerSnapshotManager.on("change", handleProviderSnapshotChange);
      this.unsubscribeProviderSnapshotEvents = () => {
        this.providerSnapshotManager?.off("change", handleProviderSnapshotChange);
      };
    }
    this.resolveVoiceTurnDetection = toResolver(voice?.turnDetection ?? null);
    this.registerVoiceSpeakHandler = voiceBridge?.registerVoiceSpeakHandler;
    this.unregisterVoiceSpeakHandler = voiceBridge?.unregisterVoiceSpeakHandler;
    this.registerVoiceCallerContext = voiceBridge?.registerVoiceCallerContext;
    this.unregisterVoiceCallerContext = voiceBridge?.unregisterVoiceCallerContext;
    this.getSpeechReadiness = dictation?.getSpeechReadiness;
    this.agentProviderRuntimeSettings = agentProviderRuntimeSettings;
    this.providerOverrides = providerOverrides;
    this.onAccountLinked = options.onAccountLinked;
    this.awaitingLink = options.awaitingLink === true;
    this.onTodoMutation = options.onTodoMutation;
    this.abortController = new AbortController();
    this.providerRegistry = buildProviderRegistry(this.sessionLogger, {
      runtimeSettings: this.agentProviderRuntimeSettings,
      providerOverrides: this.providerOverrides,
    });

    // Initialize per-session managers
    this.ttsManager = new TTSManager(this.sessionId, this.sessionLogger, tts);
    this.sttManager = new STTManager(this.sessionId, this.sessionLogger, stt);
    this.dictationStreamManager = new DictationStreamManager({
      logger: this.sessionLogger,
      sessionId: this.sessionId,
      emit: (msg) => this.handleDictationManagerMessage(msg),
      stt: dictation?.stt ?? null,
      finalTimeoutMs: dictation?.finalTimeoutMs,
    });

    // Initialize agent MCP client asynchronously
    void this.initializeAgentMcp();
    this.subscribeToAgentEvents();

    this.sessionLogger.trace(
      {
        hasPeerPubkey: this.peerPublicKeyB64 !== null,
        // Log a fingerprint, not the full key, so the audit log stays useful
        // without bloating every entry with 44-char base64. First 8 chars is
        // unique enough for human cross-reference.
        peerPubkeyPrefix: this.peerPublicKeyB64 ? this.peerPublicKeyB64.slice(0, 8) : null,
        ownerUserId: this.ownerUserId,
      },
      "Session created",
    );
  }

  /**
   * Peer device's e2ee pubkey (base64). Surfaced so the upcoming user-scope
   * lookup can resolve it via the auth-server allow-list. Null when unknown
   * (local TCP, legacy paths).
   */
  getPeerPublicKeyB64(): string | null {
    return this.peerPublicKeyB64;
  }

  /**
   * Auth-server user-id that owns the connecting device. Always present —
   * the websocket-server rejects sessions whose peer pubkey can't be
   * resolved via the auth-server allow-list. Callers use this for per-user
   * agent filtering.
   */
  getOwnerUserId(): string {
    return this.ownerUserId;
  }

  /**
   * Retained as a no-op stub for backward-compat with code paths that asked
   * "is this session account-bound?". Sessions are always account-bound now;
   * the single-tenant fallback has been removed. Returns true unconditionally.
   */
  hasOwnerUserId(): boolean {
    return true;
  }

  updateAppVersion(appVersion: string | null): void {
    if (appVersion && appVersion !== this.appVersion) {
      this.appVersion = appVersion;
    }
  }

  async primeWorkspaceGitWatchFingerprintForWorkspace(
    workspace: PersistedWorkspaceRecord,
  ): Promise<void> {
    const descriptor = await this.describeWorkspaceRecordWithGitData(workspace);
    await this.primeWorkspaceGitWatchFingerprints([descriptor]);
  }

  async emitWorkspaceUpdateForWorkspaceId(workspaceId: string): Promise<void> {
    await this.emitWorkspaceUpdatesForWorkspaceIds([workspaceId], { skipReconcile: true });
  }

  /**
   * Get the client's current activity state
   */
  public getClientActivity(): {
    deviceType: "web" | "mobile";
    focusedAgentId: string | null;
    lastActivityAt: Date;
    appVisible: boolean;
    appVisibilityChangedAt: Date;
  } | null {
    return this.clientActivity;
  }

  public getRuntimeMetrics(): SessionRuntimeMetrics {
    return {
      terminalDirectorySubscriptionCount: this.subscribedTerminalDirectories.size,
      terminalSubscriptionCount: this.activeTerminalStreams.size,
      inflightRequests: this.inflightRequests,
      peakInflightRequests: this.peakInflightRequests,
    };
  }

  public emitServerMessage(message: SessionOutboundMessage): void {
    this.emit(message);
  }

  /**
   * Push the first page of the agent directory unsolicited right after
   * `server_info` so the client can paint attention state before its own
   * paginated `fetch_agents_request` round-trip lands. The regular fetch
   * still runs in parallel — it establishes the live-update subscription
   * and paginates beyond the first page. This push exists purely to shave
   * one relay round-trip from time-to-first-paint.
   *
   * Errors are swallowed: a failed push degrades to "wait for the client
   * to ask," which is still correct.
   */
  public async sendInitialState(): Promise<void> {
    try {
      // Sort `created_at` DESC so the initial 20-row payload is the MOST
      // RECENT agents — critical when a user has hundreds of historical
      // agents (the default `created_at` ASC fallback would return ancient
      // ones first). Mirrors the client's `DEFAULT_AGENT_DIRECTORY_SORT`.
      const synthetic = {
        type: "fetch_agents_request",
        requestId: "internal:initial-snapshot",
        filter: { includeArchived: false },
        sort: [{ key: "created_at", direction: "desc" }],
        page: { limit: InitialAgentSnapshotPageLimit },
      } satisfies Extract<SessionInboundMessage, { type: "fetch_agents_request" }>;

      const { entries } = await this.listFetchAgentsEntries(synthetic);

      this.emit({
        type: "initial_agent_snapshot",
        payload: { entries },
      });
    } catch (error) {
      this.sessionLogger.warn(
        { err: error },
        "Failed to push initial agent snapshot — client will fall back to fetch_agents_request",
      );
    }
  }

  /**
   * Normalize a user prompt (with optional image metadata) for AgentManager
   */
  private buildAgentPrompt(
    text: string,
    images?: Array<{ data: string; mimeType: string }>,
    attachments?: AgentAttachment[],
    files?: SessionUpload[],
  ): AgentPromptInput {
    const normalized = text?.trim() ?? "";
    const hasImages = Boolean(images && images.length > 0);
    const hasAttachments = Boolean(attachments && attachments.length > 0);
    const hasFiles = Boolean(files && files.length > 0);
    if (!hasImages && !hasAttachments && !hasFiles) {
      return normalized;
    }
    const blocks: AgentPromptContentBlock[] = [];
    if (normalized.length > 0) {
      blocks.push({ type: "text", text: normalized });
    }
    for (const image of images ?? []) {
      blocks.push({ type: "image", data: image.data, mimeType: image.mimeType });
    }
    for (const attachment of attachments ?? []) {
      blocks.push(attachment);
    }
    if (hasFiles && files) {
      blocks.push({ type: "text", text: renderFileAttachmentFooter(files) });
    }
    return blocks;
  }

  /**
   * Read persisted image bytes off disk for the LLM prompt. The daemon is
   * the canonical source of truth for image bytes (Clay pattern); the wire
   * payload is just an upload conduit. Reads through the agent's cwd via
   * `imageStore.readImageBytes`, which traversal-guards the relative path.
   *
   * Falls back to skipping any image whose disk read fails (logged) — better
   * to send a partial prompt than to crash the whole turn.
   */
  private async loadPromptImagesFromDisk(
    persistedImages: readonly SessionImage[],
    agentId: string,
  ): Promise<Array<{ data: string; mimeType: string }>> {
    const agent = this.agentManager.getAgentSnapshot(agentId);
    if (!agent) {
      // Should be impossible: persistImages already validated the agent.
      // Defensive — return empty rather than throw, so the prompt still ships.
      this.sessionLogger.warn(
        { agentId },
        "loadPromptImagesFromDisk: agent vanished between persist and read",
      );
      return [];
    }
    const out: Array<{ data: string; mimeType: string }> = [];
    for (const persisted of persistedImages) {
      try {
        const { data } = await this.imageStore.readImageBytes({
          cwd: agent.cwd,
          relativePath: persisted.relativePath,
        });
        out.push({ data: data.toString("base64"), mimeType: persisted.mimeType });
      } catch (error) {
        this.sessionLogger.warn(
          { agentId, attachmentId: persisted.id, err: error },
          "loadPromptImagesFromDisk: failed to read image; skipping for this turn",
        );
      }
    }
    return out;
  }

  /**
   * Interrupt the agent's active run so the next prompt starts a fresh turn.
   * Returns once the manager confirms the stream has been cancelled.
   */
  private async interruptAgentIfRunning(agentId: string): Promise<void> {
    const snapshot = this.agentManager.getAgentSnapshot(agentId);
    if (!snapshot) {
      this.sessionLogger.trace({ agentId }, "interruptAgentIfRunning: agent not found");
      throw new Error(`Agent ${agentId} not found`);
    }

    const hasInFlightRun = this.agentManager.hasInFlightRun(agentId);
    if (!hasInFlightRun) {
      this.sessionLogger.trace(
        { agentId, lifecycle: snapshot.lifecycle, hasInFlightRun },
        "interruptAgentIfRunning: skipping because agent is not running",
      );
      return;
    }

    this.sessionLogger.debug(
      { agentId, lifecycle: snapshot.lifecycle, hasInFlightRun },
      "interruptAgentIfRunning: interrupting",
    );

    try {
      const t0 = Date.now();
      const cancelled = await this.agentManager.cancelAgentRun(agentId);
      this.sessionLogger.debug(
        { agentId, cancelled, durationMs: Date.now() - t0 },
        "interruptAgentIfRunning: cancelAgentRun completed",
      );
      if (!cancelled) {
        this.sessionLogger.warn(
          { agentId },
          "interruptAgentIfRunning: reported running but no active run was cancelled",
        );
      }
    } catch (error) {
      throw error;
    }
  }

  private hasActiveAgentRun(agentId: string | null): boolean {
    if (!agentId) {
      return false;
    }
    return this.agentManager.hasInFlightRun(agentId);
  }

  /**
   * Start streaming an agent run and forward results via the websocket broadcast
   */
  private startAgentStream(
    agentId: string,
    prompt: AgentPromptInput,
    runOptions?: AgentRunOptions,
  ): { ok: true } | { ok: false; error: string } {
    this.sessionLogger.trace(
      {
        agentId,
        promptType: typeof prompt === "string" ? "string" : "structured",
        hasRunOptions: Boolean(runOptions),
      },
      "startAgentStream: requested",
    );
    let iterator: AsyncGenerator<AgentStreamEvent>;
    try {
      const shouldReplace = this.agentManager.hasInFlightRun(agentId);
      iterator = shouldReplace
        ? this.agentManager.replaceAgentRun(agentId, prompt, runOptions)
        : this.agentManager.streamAgent(agentId, prompt, runOptions);
      this.sessionLogger.trace(
        { agentId, shouldReplace },
        "startAgentStream: agent iterator returned",
      );
    } catch (error) {
      this.handleAgentRunError(agentId, error, "Failed to start agent run");
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unknown error";
      return { ok: false, error: message };
    }

    void (async () => {
      try {
        for await (const _ of iterator) {
          // Events are forwarded via the session's AgentManager subscription.
        }
        this.sessionLogger.trace({ agentId }, "startAgentStream: iterator drained");
      } catch (error) {
        this.sessionLogger.trace({ agentId, err: error }, "startAgentStream: iterator threw");
        this.handleAgentRunError(agentId, error, "Agent stream failed");
      }
    })();

    return { ok: true };
  }

  private handleAgentRunError(agentId: string, error: unknown, context: string): void {
    const message =
      error instanceof Error ? error.message : typeof error === "string" ? error : "Unknown error";
    this.sessionLogger.error({ err: error, agentId, context }, `${context} for agent ${agentId}`);
    this.emit({
      type: "activity_log",
      payload: {
        id: uuidv4(),
        timestamp: new Date(),
        type: "error",
        content: `${context}: ${message}`,
      },
    });
  }

  /**
   * Initialize Agent MCP client for this session using the daemon's HTTP MCP endpoint.
   */
  private async initializeAgentMcp(): Promise<void> {
    try {
      if (!this.mcpBaseUrl) {
        this.sessionLogger.info(
          "Skipping Agent MCP initialization because no MCP base URL is configured",
        );
        return;
      }
      const transport = new StreamableHTTPClientTransport(new URL(this.mcpBaseUrl));

      this.agentMcpClient = await experimental_createMCPClient({
        transport,
      });

      this.agentTools = (await this.agentMcpClient.tools()) as ToolSet;
      const agentToolCount = Object.keys(this.agentTools ?? {}).length;
      this.sessionLogger.trace(
        { agentToolCount },
        `Agent MCP initialized with ${agentToolCount} tools`,
      );
    } catch (error) {
      this.sessionLogger.error({ err: error }, "Failed to initialize Agent MCP");
    }
  }

  /**
   * Subscribe to AgentManager events and forward them to the client
   */
  private subscribeToAgentEvents(): void {
    if (this.unsubscribeAgentEvents) {
      this.unsubscribeAgentEvents();
    }

    this.unsubscribeAgentEvents = this.agentManager.subscribe(
      (event) => {
        if (event.type === "agent_state") {
          void this.forwardAgentUpdate(event.agent);
          return;
        }

        if (
          this.isVoiceMode &&
          this.voiceModeAgentId === event.agentId &&
          event.event.type === "permission_requested" &&
          isVoicePermissionAllowed(event.event.request)
        ) {
          const requestId = event.event.request.id;
          void this.agentManager
            .respondToPermission(event.agentId, requestId, {
              behavior: "allow",
            })
            .catch((error) => {
              this.sessionLogger.warn(
                {
                  err: error,
                  agentId: event.agentId,
                  requestId,
                },
                "Failed to auto-allow speak tool permission in voice mode",
              );
            });
        }

        // Don't forward stream events for agents this user can't access.
        if (!this.agentManager.canUserAccessAgentById(event.agentId, this.ownerUserId)) {
          return;
        }

        // Reduce bandwidth/CPU on mobile: only forward high-frequency agent stream events
        // for the focused agent, with a short grace window while backgrounded.
        // History catch-up is handled via pull-based `fetch_agent_timeline_request`.
        const activity = this.clientActivity;
        if (activity?.deviceType === "mobile") {
          if (!activity.focusedAgentId) {
            return;
          }
          if (activity.focusedAgentId !== event.agentId) {
            return;
          }
          if (!activity.appVisible) {
            const hiddenForMs = Date.now() - activity.appVisibilityChangedAt.getTime();
            if (hiddenForMs >= this.MOBILE_BACKGROUND_STREAM_GRACE_MS) {
              return;
            }
          }
        }

        const serializedEvent = serializeAgentStreamEvent(event.event);
        if (!serializedEvent) {
          return;
        }

        const payload = {
          agentId: event.agentId,
          event: serializedEvent,
          timestamp: new Date().toISOString(),
          ...(typeof event.seq === "number" ? { seq: event.seq } : {}),
          ...(typeof event.epoch === "string" ? { epoch: event.epoch } : {}),
        } as const;

        this.emit({
          type: "agent_stream",
          payload,
        });

        if (event.event.type === "permission_requested") {
          this.emit({
            type: "agent_permission_request",
            payload: {
              agentId: event.agentId,
              request: event.event.request,
            },
          });
        } else if (event.event.type === "permission_resolved") {
          this.emit({
            type: "agent_permission_resolved",
            payload: {
              agentId: event.agentId,
              requestId: event.event.requestId,
              resolution: event.event.resolution,
            },
          });
        }

        // Title updates may be applied asynchronously after agent creation.
      },
      { replayState: false },
    );
  }

  private async buildAgentPayload(agent: ManagedAgent): Promise<AgentSnapshotPayload> {
    const storedRecord = await this.agentStorage.get(agent.id);
    const title = storedRecord?.title ?? storedRecord?.config?.title ?? null;
    const payload = toAgentPayload(agent, { title });
    const storedUpdatedAt = storedRecord ? resolveStoredAgentPayloadUpdatedAt(storedRecord) : null;
    if (storedUpdatedAt) {
      const liveUpdatedAt = Date.parse(payload.updatedAt);
      const persistedUpdatedAt = Date.parse(storedUpdatedAt);
      if (Number.isNaN(liveUpdatedAt) || persistedUpdatedAt > liveUpdatedAt) {
        payload.updatedAt = storedUpdatedAt;
      }
    }
    payload.archivedAt = storedRecord?.archivedAt ?? null;
    return payload;
  }

  private buildStoredAgentPayload(record: StoredAgentRecord): AgentSnapshotPayload {
    return buildStoredAgentPayload(record, this.providerRegistry, this.sessionLogger);
  }

  private isProviderVisibleToClient(provider: string): boolean {
    if (clientSupportsAllProviders(this.appVersion)) {
      return true;
    }
    return LEGACY_PROVIDER_IDS.has(provider);
  }

  private filterEditorsForClient(
    editors: EditorTargetDescriptorPayload[],
  ): EditorTargetDescriptorPayload[] {
    if (clientSupportsFlexibleEditorIds(this.appVersion)) {
      return editors;
    }
    return editors.filter((editor) => isLegacyEditorTargetId(editor.id));
  }

  private matchesAgentFilter(options: {
    agent: AgentSnapshotPayload;
    project: ProjectPlacementPayload;
    filter?: AgentUpdatesFilter;
  }): boolean {
    const { agent, project, filter } = options;

    if (filter?.labels) {
      const matchesLabels = Object.entries(filter.labels).every(
        ([key, value]) => agent.labels[key] === value,
      );
      if (!matchesLabels) {
        return false;
      }
    }

    const includeArchived = filter?.includeArchived ?? false;
    if (!includeArchived && agent.archivedAt) {
      return false;
    }

    if (filter?.thinkingOptionId !== undefined) {
      const expectedThinkingOptionId = resolveEffectiveThinkingOptionId({
        configuredThinkingOptionId: filter.thinkingOptionId ?? null,
      });
      const resolvedThinkingOptionId =
        agent.effectiveThinkingOptionId ??
        resolveEffectiveThinkingOptionId({
          runtimeInfo: agent.runtimeInfo,
          configuredThinkingOptionId: agent.thinkingOptionId ?? null,
        });
      if (resolvedThinkingOptionId !== expectedThinkingOptionId) {
        return false;
      }
    }

    if (filter?.statuses && filter.statuses.length > 0) {
      const statuses = new Set(filter.statuses);
      if (!statuses.has(agent.status)) {
        return false;
      }
    }

    if (typeof filter?.requiresAttention === "boolean") {
      const requiresAttention = agent.requiresAttention ?? false;
      if (requiresAttention !== filter.requiresAttention) {
        return false;
      }
    }

    if (filter?.projectKeys && filter.projectKeys.length > 0) {
      const projectKeys = new Set(filter.projectKeys.filter((item) => item.trim().length > 0));
      if (projectKeys.size > 0 && !projectKeys.has(project.projectKey)) {
        return false;
      }
    }

    return true;
  }

  private getAgentUpdateTargetId(update: AgentUpdatePayload): string {
    return update.kind === "remove" ? update.agentId : update.agent.id;
  }

  private bufferOrEmitAgentUpdate(
    subscription: AgentUpdatesSubscriptionState,
    payload: AgentUpdatePayload,
  ): void {
    if (payload.kind === "upsert" && !this.isProviderVisibleToClient(payload.agent.provider)) {
      return;
    }
    if (subscription.isBootstrapping) {
      subscription.pendingUpdatesByAgentId.set(this.getAgentUpdateTargetId(payload), payload);
      return;
    }

    this.emit({
      type: "agent_update",
      payload,
    });
  }

  private flushBootstrappedAgentUpdates(options?: {
    snapshotUpdatedAtByAgentId?: Map<string, number>;
  }): void {
    const subscription = this.agentUpdatesSubscription;
    if (!subscription || !subscription.isBootstrapping) {
      return;
    }

    subscription.isBootstrapping = false;
    const pending = Array.from(subscription.pendingUpdatesByAgentId.values());
    subscription.pendingUpdatesByAgentId.clear();

    for (const payload of pending) {
      if (payload.kind === "upsert") {
        const snapshotUpdatedAt = options?.snapshotUpdatedAtByAgentId?.get(payload.agent.id);
        if (typeof snapshotUpdatedAt === "number") {
          const updateUpdatedAt = Date.parse(payload.agent.updatedAt);
          if (!Number.isNaN(updateUpdatedAt) && updateUpdatedAt <= snapshotUpdatedAt) {
            continue;
          }
        }
      }

      this.emit({
        type: "agent_update",
        payload,
      });
    }
  }

  private async findWorkspaceByDirectory(cwd: string): Promise<PersistedWorkspaceRecord | null> {
    const normalizedCwd = await this.resolveWorkspaceDirectory(cwd);
    const workspaces = await this.workspaceRegistry.list();
    return workspaces.find((workspace) => workspace.cwd === normalizedCwd) ?? null;
  }

  private async resolveWorkspaceDirectory(cwd: string): Promise<string> {
    const normalizedCwd = normalizePersistedWorkspaceId(cwd);
    try {
      const snapshot = await this.workspaceGitService.getSnapshot(normalizedCwd);
      return normalizePersistedWorkspaceId(snapshot.git.repoRoot ?? normalizedCwd);
    } catch {
      return normalizedCwd;
    }
  }

  private async buildProjectPlacementForWorkspace(
    workspace: PersistedWorkspaceRecord,
    projectRecord?: PersistedProjectRecord | null,
  ): Promise<ProjectPlacementPayload> {
    const project = projectRecord ?? (await this.projectRegistry.get(workspace.projectId));
    if (!project) {
      throw new Error(`Project not found for workspace ${workspace.workspaceId}`);
    }
    const checkout =
      project.kind !== "git"
        ? {
            cwd: workspace.cwd,
            isGit: false as const,
            currentBranch: null,
            remoteUrl: null,
            worktreeRoot: null,
            isAppostleOwnedWorktree: false as const,
            mainRepoRoot: null,
          }
        : workspace.kind === "worktree"
          ? {
              cwd: workspace.cwd,
              isGit: true as const,
              currentBranch: workspace.displayName,
              remoteUrl: null,
              worktreeRoot: workspace.cwd,
              isAppostleOwnedWorktree: true as const,
              mainRepoRoot: project.rootPath,
            }
          : {
              cwd: workspace.cwd,
              isGit: true as const,
              currentBranch: workspace.displayName,
              remoteUrl: null,
              worktreeRoot: workspace.cwd,
              isAppostleOwnedWorktree: false as const,
              mainRepoRoot: null,
            };
    return {
      projectKey: project.projectId,
      projectName: project.displayName,
      checkout,
    };
  }

  private async buildProjectPlacementForCwd(cwd: string): Promise<ProjectPlacementPayload | null> {
    const workspace = await this.findWorkspaceByDirectory(cwd);
    if (!workspace) {
      return null;
    }
    return this.buildProjectPlacementForWorkspace(workspace);
  }

  private async forwardAgentUpdate(agent: ManagedAgent): Promise<void> {
    try {
      if (!this.agentManager.canUserAccessAgentById(agent.id, this.ownerUserId)) {
        return;
      }
      const subscription = this.agentUpdatesSubscription;
      const payload = await this.buildAgentPayload(agent);
      if (subscription) {
        const project = await this.buildProjectPlacementForCwd(payload.cwd);
        if (!project) {
          throw new Error(`Workspace not found for agent ${payload.id}`);
        }
        const matches = this.matchesAgentFilter({
          agent: payload,
          project,
          filter: subscription.filter,
        });

        if (matches) {
          this.bufferOrEmitAgentUpdate(subscription, {
            kind: "upsert",
            agent: payload,
            project,
          });
        } else {
          this.bufferOrEmitAgentUpdate(subscription, {
            kind: "remove",
            agentId: payload.id,
          });
        }
      }

      await this.emitWorkspaceUpdateForCwd(payload.cwd);
    } catch (error) {
      this.sessionLogger.error({ err: error }, "Failed to emit agent update");
    }
  }

  /**
   * Main entry point for processing session messages
   */
  public async handleMessage(msg: SessionInboundMessage): Promise<void> {
    this.inflightRequests++;
    if (this.inflightRequests > this.peakInflightRequests) {
      this.peakInflightRequests = this.inflightRequests;
    }
    try {
      // Fix 2 — awaiting_link mode: the daemon booted without an `account`
      // block, so it has no agent state, no auth-server client, and no
      // resolver. The ONLY thing it can do is receive a `link_account_request`
      // and persist the resulting account block. Everything else gets a
      // structured "daemon_awaiting_link" reject so the web client can route
      // the user to the pairing flow instead of showing generic errors.
      if (this.awaitingLink && msg.type !== "link_account_request") {
        const requestId =
          typeof (msg as { requestId?: unknown }).requestId === "string"
            ? ((msg as { requestId?: string }).requestId as string)
            : undefined;
        this.sessionLogger.warn(
          { messageType: msg.type, requestId },
          "rejected non-link message in awaiting_link mode",
        );
        this.emit({
          type: "daemon_awaiting_link_response",
          payload: {
            ok: false,
            error: "daemon_awaiting_link",
            rejectedType: msg.type,
            ...(requestId ? { requestId } : {}),
          },
        });
        return;
      }
      this.sessionLogger.trace(
        { messageType: msg.type, payloadBytes: JSON.stringify(msg).length },
        "inbound message",
      );
      try {
        switch (msg.type) {
          case "voice_audio_chunk":
            await this.handleAudioChunk(msg);
            break;

          case "abort_request":
            await this.handleAbort();
            break;

          case "audio_played":
            this.handleAudioPlayed(msg.id);
            break;

          case "link_account_request":
            await this.handleLinkAccountRequest(msg);
            break;

          case "claude_token_set_request":
            this.handleClaudeTokenSetRequest(msg);
            break;

          case "claude_token_status_request":
            this.handleClaudeTokenStatusRequest(msg);
            break;

          case "claude_token_remove_request":
            this.handleClaudeTokenRemoveRequest(msg);
            break;

          case "fetch_agents_request":
            await this.handleFetchAgents(msg);
            break;

          case "lenses_list_request":
            await this.handleLensesListRequest(msg);
            break;

          case "fetch_workspaces_request":
            await this.handleFetchWorkspacesRequest(msg);
            break;

          case "fetch_agent_request":
            await this.handleFetchAgent(msg.agentId, msg.requestId);
            break;

          case "delete_agent_request":
            await this.handleDeleteAgentRequest(msg.agentId, msg.requestId);
            break;

          case "archive_agent_request":
            await this.handleArchiveAgentRequest(msg.agentId, msg.requestId);
            break;

          case "fork_agent_request":
            await this.handleForkAgentRequest(msg);
            break;

          case "rewind_agent_request":
            await this.handleRewindAgentRequest(msg);
            break;

          case "close_items_request":
            await this.handleCloseItemsRequest(msg);
            break;

          case "update_agent_request":
            await this.handleUpdateAgentRequest(
              msg.agentId,
              msg.name,
              msg.labels,
              msg.requestId,
              msg.internal,
              msg.stackId,
            );
            break;

          case "share_session_request":
            await this.handleShareSessionRequest(msg);
            break;

          case "unshare_session_request":
            await this.handleUnshareSessionRequest(msg);
            break;

          case "stack_create_request":
            await this.handleStackCreateRequest(msg);
            break;

          case "stack_rename_request":
            await this.handleStackRenameRequest(msg);
            break;

          case "stack_delete_request":
            await this.handleStackDeleteRequest(msg);
            break;

          case "stack_reorder_request":
            await this.handleStackReorderRequest(msg);
            break;

          case "stack_set_active_request":
            await this.handleStackSetActiveRequest(msg);
            break;

          case "set_voice_mode":
            await this.handleSetVoiceMode(msg.enabled, msg.agentId, msg.requestId);
            break;

          case "send_agent_message_request":
            await this.handleSendAgentMessageRequest(msg);
            break;

          case "list_session_uploads_request":
            await this.handleListSessionUploadsRequest(msg);
            break;

          case "delete_session_upload_request":
            await this.handleDeleteSessionUploadRequest(msg);
            break;

          case "list_session_images_request":
            await this.handleListSessionImagesRequest(msg);
            break;

          case "delete_session_image_request":
            await this.handleDeleteSessionImageRequest(msg);
            break;

          case "fetch_attachment_bytes_request":
            await this.handleFetchAttachmentBytesRequest(msg);
            break;

          case "plan_usage_request":
            await this.handlePlanUsageRequest(msg);
            break;

          case "todo_list_subscribe":
            await this.handleTodoListSubscribe(msg);
            break;

          case "todo_list_unsubscribe":
            this.handleTodoListUnsubscribe(msg);
            break;

          case "todo_remove_request":
            await this.handleTodoRemoveRequest(msg);
            break;

          case "todo_set_completed_request":
            await this.handleTodoSetCompletedRequest(msg);
            break;

          case "todo_reorder_request":
            await this.handleTodoReorderRequest(msg);
            break;

          case "todo_add_request":
            await this.handleTodoAddRequest(msg);
            break;

          case "todo_notes_update_request":
            await this.handleTodoNotesUpdateRequest(msg);
            break;

          case "todo_linked_agent_update_request":
            await this.handleTodoLinkedAgentUpdateRequest(msg);
            break;

          case "todo_assignee_update_request":
            await this.handleTodoAssigneeUpdateRequest(msg);
            break;

          case "todo_update_text_request":
            await this.handleTodoUpdateTextRequest(msg);
            break;

          case "todo_subtask_add_request":
            await this.handleTodoSubtaskAddRequest(msg);
            break;

          case "todo_subtask_update_text_request":
            await this.handleTodoSubtaskUpdateTextRequest(msg);
            break;

          case "todo_subtask_remove_request":
            await this.handleTodoSubtaskRemoveRequest(msg);
            break;

          case "todo_subtask_set_completed_request":
            await this.handleTodoSubtaskSetCompletedRequest(msg);
            break;

          case "todo_subtask_reorder_request":
            await this.handleTodoSubtaskReorderRequest(msg);
            break;
          case "todo_status_update_request":
            await this.handleTodoStatusUpdateRequest(msg);
            break;
          case "todo_priority_update_request":
            await this.handleTodoPriorityUpdateRequest(msg);
            break;
          case "todo_schedule_update_request":
            await this.handleTodoScheduleUpdateRequest(msg);
            break;
          case "todo_blocked_by_update_request":
            await this.handleTodoBlockedByUpdateRequest(msg);
            break;
          case "todo_result_report_update_request":
            await this.handleTodoResultReportUpdateRequest(msg);
            break;
          case "todo_activity_add_request":
            await this.handleTodoActivityAddRequest(msg);
            break;
          case "todo_activity_list_request":
            await this.handleTodoActivityListRequest(msg);
            break;
          case "todo_sessions_list_request":
            await this.handleTodoSessionsListRequest(msg);
            break;
          case "todo_subtask_notes_update_request":
            await this.handleTodoSubtaskNotesUpdateRequest(msg);
            break;
          case "todo_subtask_assignee_update_request":
            await this.handleTodoSubtaskAssigneeUpdateRequest(msg);
            break;
          case "todo_subtask_status_update_request":
            await this.handleTodoSubtaskStatusUpdateRequest(msg);
            break;
          case "todo_subtask_schedule_update_request":
            await this.handleTodoSubtaskScheduleUpdateRequest(msg);
            break;
          case "todo_subtask_priority_update_request":
            await this.handleTodoSubtaskPriorityUpdateRequest(msg);
            break;
          case "todo_subtask_blocked_by_update_request":
            await this.handleTodoSubtaskBlockedByUpdateRequest(msg);
            break;

          case "wait_for_finish_request":
            await this.handleWaitForFinish(msg.agentId, msg.requestId, msg.timeoutMs);
            break;

          case "get_daemon_config_request":
            this.emit({
              type: "get_daemon_config_response",
              payload: {
                requestId: msg.requestId,
                config: this.daemonConfigStore.get(),
              },
            });
            break;

          case "set_daemon_config_request":
            this.emit({
              type: "set_daemon_config_response",
              payload: {
                requestId: msg.requestId,
                config: this.daemonConfigStore.patch(msg.config),
              },
            });
            break;

          case "dictation_stream_start":
            {
              const unavailable = this.resolveVoiceFeatureUnavailableContext("dictation");
              if (unavailable) {
                this.emit({
                  type: "dictation_stream_error",
                  payload: {
                    dictationId: msg.dictationId,
                    error: unavailable.message,
                    retryable: unavailable.retryable,
                    reasonCode: unavailable.reasonCode,
                    missingModelIds: unavailable.missingModelIds,
                  },
                });
                break;
              }
            }
            await this.dictationStreamManager.handleStart(msg.dictationId, msg.format);
            break;

          case "dictation_stream_chunk":
            await this.dictationStreamManager.handleChunk({
              dictationId: msg.dictationId,
              seq: msg.seq,
              audioBase64: msg.audio,
              format: msg.format,
            });
            break;

          case "dictation_stream_finish":
            await this.dictationStreamManager.handleFinish(msg.dictationId, msg.finalSeq);
            break;

          case "dictation_stream_cancel":
            this.dictationStreamManager.handleCancel(msg.dictationId);
            break;

          case "create_agent_request":
            await this.handleCreateAgentRequest(msg);
            break;

          case "resume_agent_request":
            await this.handleResumeAgentRequest(msg);
            break;

          case "refresh_agent_request":
            await this.handleRefreshAgentRequest(msg);
            break;

          case "cancel_agent_request":
            await this.handleCancelAgentRequest(msg.agentId, msg.requestId);
            break;

          case "restart_server_request":
            await this.handleRestartServerRequest(msg.requestId, msg.reason);
            break;

          case "shutdown_server_request":
            await this.handleShutdownServerRequest(msg.requestId);
            break;

          case "fetch_agent_timeline_request":
            await this.handleFetchAgentTimelineRequest(msg);
            break;

          case "set_agent_mode_request":
            await this.handleSetAgentModeRequest(msg.agentId, msg.modeId, msg.requestId);
            break;

          case "set_agent_model_request":
            await this.handleSetAgentModelRequest(msg.agentId, msg.modelId, msg.requestId);
            break;

          case "set_agent_feature_request":
            await this.handleSetAgentFeatureRequest(
              msg.agentId,
              msg.featureId,
              msg.value,
              msg.requestId,
            );
            break;

          case "list_agent_mcp_servers_request":
            await this.handleListAgentMcpServersRequest(msg.agentId, msg.requestId);
            break;

          case "set_agent_mcp_server_request":
            await this.handleSetAgentMcpServerRequest(
              msg.agentId,
              msg.serverName,
              msg.enabled,
              msg.requestId,
            );
            break;

          case "set_agent_thinking_request":
            await this.handleSetAgentThinkingRequest(
              msg.agentId,
              msg.thinkingOptionId,
              msg.requestId,
            );
            break;

          case "agent_permission_response":
            await this.handleAgentPermissionResponse(msg.agentId, msg.requestId, msg.response);
            break;

          case "checkout_status_request":
            await this.handleCheckoutStatusRequest(msg);
            break;

          case "validate_branch_request":
            await this.handleValidateBranchRequest(msg);
            break;

          case "branch_suggestions_request":
            await this.handleBranchSuggestionsRequest(msg);
            break;

          case "directory_suggestions_request":
            await this.handleDirectorySuggestionsRequest(msg);
            break;

          case "list_directory_request":
            await this.handleListDirectoryRequest(msg);
            break;

          case "list_filesystem_roots_request":
            await this.handleListFilesystemRootsRequest(msg);
            break;

          case "subscribe_checkout_diff_request":
            await this.handleSubscribeCheckoutDiffRequest(msg);
            break;

          case "unsubscribe_checkout_diff_request":
            this.handleUnsubscribeCheckoutDiffRequest(msg);
            break;

          case "checkout_switch_branch_request":
            await this.handleCheckoutSwitchBranchRequest(msg);
            break;

          case "checkout_create_branch_request":
            await this.handleCheckoutCreateBranchRequest(msg);
            break;

          case "stash_save_request":
            await this.handleStashSaveRequest(msg);
            break;

          case "stash_pop_request":
            await this.handleStashPopRequest(msg);
            break;

          case "stash_list_request":
            await this.handleStashListRequest(msg);
            break;

          case "checkout_commit_request":
            await this.handleCheckoutCommitRequest(msg);
            break;

          case "checkout_generate_commit_message_request":
            await this.handleCheckoutGenerateCommitMessageRequest(msg);
            break;

          case "checkout_merge_request":
            await this.handleCheckoutMergeRequest(msg);
            break;

          case "checkout_merge_from_base_request":
            await this.handleCheckoutMergeFromBaseRequest(msg);
            break;

          case "checkout_pull_request":
            await this.handleCheckoutPullRequest(msg);
            break;

          case "checkout_push_request":
            await this.handleCheckoutPushRequest(msg);
            break;

          case "checkout_delete_branch_request":
            await this.handleCheckoutDeleteBranchRequest(msg);
            break;

          case "checkout_pr_create_request":
            await this.handleCheckoutPrCreateRequest(msg);
            break;

          case "checkout_pr_status_request":
            await this.handleCheckoutPrStatusRequest(msg);
            break;

          case "pull_request_timeline_request":
            await this.handlePullRequestTimelineRequest(msg);
            break;

          case "github_search_request":
            await this.handleGitHubSearchRequest(msg);
            break;

          case "gitlab_search_request":
            await this.handleGitLabSearchRequest(msg);
            break;

          case "create_github_issue_request":
            await this.handleCreateGitHubIssueRequest(msg);
            break;

          case "create_gitlab_issue_request":
            await this.handleCreateGitLabIssueRequest(msg);
            break;

          case "oauth_initiate_request":
            await this.handleOAuthInitiateRequest(msg);
            break;

          case "oauth_complete_request":
            await this.handleOAuthCompleteRequest(msg);
            break;

          case "oauth_status_request":
            await this.handleOAuthStatusRequest(msg);
            break;

          case "oauth_sign_out_request":
            await this.handleOAuthSignOutRequest(msg);
            break;

          case "appostle_worktree_list_request":
            await this.handleAppostleWorktreeListRequest(msg);
            break;

          case "appostle_worktree_archive_request":
            await this.handleAppostleWorktreeArchiveRequest(msg);
            break;

          case "create_appostle_worktree_request":
            await this.handleCreateAppostleWorktreeRequest(msg);
            break;

          case "workspace_setup_status_request":
            await this.handleWorkspaceSetupStatusRequest(msg);
            break;

          case "list_available_editors_request":
            await this.handleListAvailableEditorsRequest(msg);
            break;

          case "open_in_editor_request":
            await this.handleOpenInEditorRequest(msg);
            break;

          case "open_project_request":
            await this.handleOpenProjectRequest(msg);
            break;

          case "init_repo_request":
            await this.handleInitRepoRequest(msg);
            break;

          case "clone_repo_request":
            await this.handleCloneRepoRequest(msg);
            break;

          case "archive_workspace_request":
            await this.handleArchiveWorkspaceRequest(msg);
            break;

          case "file_explorer_request":
            await this.handleFileExplorerRequest(msg);
            break;

          case "workspace_search_files_request":
            await this.handleWorkspaceSearchFilesRequest(msg);
            break;

          case "file_write_request":
            await this.handleFileWriteRequest(msg);
            break;

          case "file_delete_request":
            await this.handleFileDeleteRequest(msg);
            break;

          case "file_explorer_delete_request":
            await this.handleFileExplorerDeleteRequest(msg);
            break;

          case "file_mkdir_request":
            await this.handleFileMkdirRequest(msg);
            break;

          case "file_move_request":
            await this.handleFileMoveRequest(msg);
            break;

          case "project_icon_request":
            await this.handleProjectIconRequest(msg);
            break;

          case "file_download_token_request":
            await this.handleFileDownloadTokenRequest(msg);
            break;

          case "list_provider_models_request":
            await this.handleListProviderModelsRequest(msg);
            break;

          case "list_provider_modes_request":
            await this.handleListProviderModesRequest(msg);
            break;

          case "list_provider_features_request":
            await this.handleListProviderFeaturesRequest(msg);
            break;

          case "list_available_providers_request":
            await this.handleListAvailableProvidersRequest(msg);
            break;

          case "get_providers_snapshot_request":
            await this.handleGetProvidersSnapshotRequest(msg);
            break;

          case "refresh_providers_snapshot_request":
            await this.handleRefreshProvidersSnapshotRequest(msg);
            break;

          case "provider_diagnostic_request":
            await this.handleProviderDiagnosticRequest(msg);
            break;

          case "clear_agent_attention":
            await this.handleClearAgentAttention(msg.agentId, msg.requestId);
            break;

          case "client_heartbeat":
            this.handleClientHeartbeat(msg);
            break;

          case "ping": {
            const now = Date.now();
            this.emit({
              type: "pong",
              payload: {
                requestId: msg.requestId,
                clientSentAt: msg.clientSentAt,
                serverReceivedAt: now,
                serverSentAt: now,
              },
            });
            break;
          }

          case "list_commands_request":
            await this.handleListCommandsRequest(msg);
            break;

          case "list_skills_request":
            await this.handleListSkillsRequest(msg);
            break;

          case "create_skill_request":
            await this.handleCreateSkillRequest(msg);
            break;

          case "write_skill_frontmatter_request":
            await this.handleWriteSkillFrontmatterRequest(msg);
            break;

          case "register_push_token":
            this.handleRegisterPushToken({
              token: msg.token,
              deviceId: msg.deviceId,
              preferences: msg.preferences,
            });
            break;

          case "unregister_push_token":
            this.handleUnregisterPushToken(msg.token);
            break;

          case "register_web_push_subscription":
            this.handleRegisterWebPushSubscription({
              subscription: msg.subscription,
              deviceId: msg.deviceId,
              preferences: msg.preferences,
            });
            break;

          case "unregister_web_push_subscription":
            this.handleUnregisterWebPushSubscription(msg.endpoint);
            break;

          case "update_notification_preferences":
            this.handleUpdateNotificationPreferences(msg);
            break;

          case "get_vapid_public_key_request":
            this.handleGetVapidPublicKeyRequest(msg.requestId);
            break;

          case "send_test_web_push_request":
            void this.handleSendTestWebPush(msg.deviceId);
            break;

          case "send_test_push_request":
            void this.handleSendTestPush(msg.deviceId);
            break;

          case "subscribe_terminals_request":
            this.handleSubscribeTerminalsRequest(msg);
            break;

          case "unsubscribe_terminals_request":
            this.handleUnsubscribeTerminalsRequest(msg);
            break;

          case "list_terminals_request":
            await this.handleListTerminalsRequest(msg);
            break;

          case "create_terminal_request":
            await this.handleCreateTerminalRequest(msg);
            break;

          case "start_workspace_script_request":
            await this.handleStartWorkspaceScriptRequest(msg);
            break;

          case "subscribe_terminal_request":
            await this.handleSubscribeTerminalRequest(msg);
            break;

          case "unsubscribe_terminal_request":
            this.handleUnsubscribeTerminalRequest(msg);
            break;

          case "terminal_input":
            this.handleTerminalInput(msg);
            break;

          case "kill_terminal_request":
            await this.handleKillTerminalRequest(msg);
            break;

          case "capture_terminal_request":
            await this.handleCaptureTerminalRequest(msg);
            break;

          case "chat/create":
            await this.handleChatCreateRequest(msg);
            break;

          case "chat/list":
            await this.handleChatListRequest(msg);
            break;

          case "chat/inspect":
            await this.handleChatInspectRequest(msg);
            break;

          case "chat/delete":
            await this.handleChatDeleteRequest(msg);
            break;

          case "chat/post":
            await this.handleChatPostRequest(msg);
            break;

          case "chat/read":
            await this.handleChatReadRequest(msg);
            break;

          case "chat/wait":
            await this.handleChatWaitRequest(msg);
            break;

          case "preview/open":
            await this.handlePreviewOpenRequest(msg);
            break;

          case "preview/frame":
            await this.handlePreviewFrameRequest(msg);
            break;

          case "preview/scroll":
            await this.handlePreviewScrollRequest(msg);
            break;

          case "preview/pick":
            await this.handlePreviewPickRequest(msg);
            break;

          case "preview/close":
            await this.handlePreviewCloseRequest(msg);
            break;

          case "preview/input":
            await this.handlePreviewInputRequest(msg);
            break;

          case "preview/resize":
            await this.handlePreviewResizeRequest(msg);
            break;

          case "schedule/create":
            await this.handleScheduleCreateRequest(msg);
            break;

          case "schedule/list":
            await this.handleScheduleListRequest(msg);
            break;

          case "schedule/inspect":
            await this.handleScheduleInspectRequest(msg);
            break;

          case "schedule/logs":
            await this.handleScheduleLogsRequest(msg);
            break;

          case "schedule/pause":
            await this.handleSchedulePauseRequest(msg);
            break;

          case "schedule/resume":
            await this.handleScheduleResumeRequest(msg);
            break;

          case "schedule/delete":
            await this.handleScheduleDeleteRequest(msg);
            break;

          case "loop/run":
            await this.handleLoopRunRequest(msg);
            break;

          case "loop/list":
            await this.handleLoopListRequest(msg);
            break;

          case "loop/inspect":
            await this.handleLoopInspectRequest(msg);
            break;

          case "loop/logs":
            await this.handleLoopLogsRequest(msg);
            break;

          case "loop/stop":
            await this.handleLoopStopRequest(msg);
            break;

          case "quest/run":
            await this.handleQuestRunRequest(msg);
            break;

          case "quest/list":
            await this.handleQuestListRequest(msg);
            break;

          case "quest/inspect":
            await this.handleQuestInspectRequest(msg);
            break;

          case "quest/stop":
            await this.handleQuestStopRequest(msg);
            break;

          case "roles/list":
            await this.handleRolesListRequest(msg);
            break;

          case "roles/list-scope":
            await this.handleRolesListScopeRequest(msg);
            break;

          case "roles/create":
            await this.handleRoleCreateRequest(msg);
            break;

          case "roles/write-frontmatter":
            await this.handleRoleWriteFrontmatterRequest(msg);
            break;

          case "roles/move":
            await this.handleRoleMoveRequest(msg);
            break;

          case "brands/list-scope":
            await this.handleBrandsListScopeRequest(msg);
            break;

          case "brands/create":
            await this.handleBrandCreateRequest(msg);
            break;

          case "brands/write-frontmatter":
            await this.handleBrandWriteFrontmatterRequest(msg);
            break;

          case "brands/asset-copy":
            await this.handleBrandAssetCopyRequest(msg);
            break;

          case "brands/asset-upload":
            await this.handleBrandAssetUploadRequest(msg);
            break;

          case "brands/asset-delete":
            await this.handleBrandAssetDeleteRequest(msg);
            break;

          case "brands/asset-rename":
            await this.handleBrandAssetRenameRequest(msg);
            break;

          case "brands/generate-tokens":
            await this.handleBrandGenerateTokensRequest(msg);
            break;

          case "brands/token-generation-status":
            await this.handleBrandTokenGenerationStatusRequest(msg);
            break;

          case "brands/generate-layout":
            await this.handleBrandGenerateLayoutRequest(msg);
            break;

          case "brands/layout-qa/next":
            await this.handleBrandLayoutQaNextRequest(msg);
            break;

          case "brands/generate-wireframe":
            await this.handleBrandGenerateWireframeRequest(msg);
            break;

          case "brands/analyze-photography":
            await this.handleBrandAnalyzePhotographyRequest(msg);
            break;

          case "rightfont/library":
            await this.handleRightFontLibraryRequest(msg);
            break;

          case "google-fonts/catalog":
            await this.handleGoogleFontsCatalogRequest(msg);
            break;

          case "google-fonts/download":
            await this.handleGoogleFontsDownloadRequest(msg);
            break;
        }
      } catch (error: any) {
        const err = error instanceof Error ? error : new Error(String(error));
        this.sessionLogger.error({ err }, "Error handling message");

        const requestId = (msg as { requestId?: unknown }).requestId;
        if (typeof requestId === "string") {
          try {
            this.emit({
              type: "rpc_error",
              payload: {
                requestId,
                requestType: msg.type,
                error: `Request failed: ${err.message}`,
                code: "handler_error",
              },
            });
          } catch (emitError) {
            this.sessionLogger.error({ err: emitError }, "Failed to emit rpc_error");
          }
        }

        this.emit({
          type: "activity_log",
          payload: {
            id: uuidv4(),
            timestamp: new Date(),
            type: "error",
            content: `Error: ${err.message}`,
          },
        });
      }
    } finally {
      this.inflightRequests--;
    }
  }

  public resetPeakInflight(): void {
    this.peakInflightRequests = this.inflightRequests;
  }

  public handleBinaryFrame(frame: TerminalStreamFrame): void {
    const activeStream = this.activeTerminalStreams.get(frame.slot);
    if (!activeStream || !this.terminalManager) {
      return;
    }
    const terminal = this.terminalManager.getTerminal(activeStream.terminalId);
    if (!terminal) {
      this.detachTerminalStream(activeStream.terminalId, { emitExit: true });
      return;
    }

    switch (frame.opcode) {
      case TerminalStreamOpcode.Input: {
        if (frame.payload.byteLength === 0) {
          return;
        }
        const text = Buffer.from(frame.payload).toString("utf8");
        if (!text) {
          return;
        }
        terminal.send({ type: "input", data: text });
        return;
      }

      case TerminalStreamOpcode.Resize: {
        const resize = decodeTerminalResizePayload(frame.payload);
        if (!resize) {
          return;
        }
        terminal.send({ type: "resize", rows: resize.rows, cols: resize.cols });
        return;
      }

      default:
        return;
    }
  }

  private async handleLinkAccountRequest(
    msg: z.infer<typeof LinkAccountRequestMessageSchema>,
  ): Promise<void> {
    const { requestId, bootstrapToken, authServerUrl } = msg;
    const log = this.sessionLogger.child({ component: "link-account", requestId });

    const respond = (
      payload: { ok: true; hostId: string; userId: string } | { ok: false; error: string },
    ): void => {
      this.emit({
        type: "link_account_response",
        payload: { requestId, ...payload },
      });
    };

    try {
      // Idempotency safety: refuse to overwrite an *active* link. Re-issuing
      // a daemon token would silently cut off the previous one — that's a real
      // operator condition we want to surface, not silently overwrite.
      //
      // BUT: the auth-server's host row may have been wiped while this
      // daemon's persisted account block was preserved (manual cleanup, DB
      // restore from backup, a regression test that truncated `hosts`). In
      // that case the existing daemon-token is orphaned: the auth-server
      // returns 401 for /daemon/allowlist, and any future allow-list sync
      // is dead in the water. Refusing the relink would leave the daemon
      // permanently unable to recover without manual config surgery.
      //
      // Probe the existing token before deciding:
      //   - "valid"   → genuine "already linked"; refuse.
      //   - "stale"   → auth-server says no such token; accept relink.
      //   - "unknown" → network blip / 5xx; refuse, conservative — better to
      //                 require a retry on a working network than overwrite a
      //                 healthy link based on bad info.
      const existing = loadPersistedConfig(this.appostleHome, log);
      if (existing.account) {
        const probe = await probeDaemonTokenStatus({
          authServerUrl: existing.account.authServerUrl,
          daemonToken: existing.account.daemonToken,
          log,
        });
        if (probe !== "stale") {
          log.warn(
            { existingHostId: existing.account.hostId, probe },
            "link_account_request rejected: already linked",
          );
          respond({ ok: false, error: "already_linked" });
          return;
        }
        log.info(
          { previousHostId: existing.account.hostId },
          "link_account_request: existing account block is stale — accepting relink",
        );
        // Fall through; savePersistedConfig at the end overwrites the stale
        // block atomically once the new link succeeds.
      }

      const serverId = getOrCreateServerId(this.appostleHome, { logger: log });
      const daemonKeyPair = await loadOrCreateDaemonKeyPair(this.appostleHome, log);
      const fingerprint = daemonKeyPair.publicKeyB64;

      // Best-available human label: explicit daemon identity → first hostname
      // alias from config → OS hostname. Truncated to /daemon/link's 120-char
      // cap; an undefined value is fine, the auth-server falls back gracefully.
      const daemonConfig = this.daemonConfigStore.get();
      const aliasLabel =
        Array.isArray(daemonConfig.hostnames) && daemonConfig.hostnames.length > 0
          ? daemonConfig.hostnames[0]
          : null;
      const rawLabel = aliasLabel ?? osHostname() ?? null;
      const label =
        typeof rawLabel === "string" && rawLabel.trim().length > 0
          ? rawLabel.trim().slice(0, 120)
          : undefined;

      // Phase 5 (account-bound auto-pair). Hand the auth-server the same data
      // that a QR pair offer carries — daemon pubkey + the relay endpoint
      // we'd put on a fresh offer URL — so a *new* signed-in device can
      // auto-pair with this daemon without re-scanning a QR.
      //
      // We pull the relay endpoint directly from the persisted config we
      // already loaded above. The bootstrap fallback ("pair.appostle.app:443")
      // mirrors what bootstrap.ts uses when starting the relay transport;
      // keep the two in sync. If relay is explicitly disabled we send
      // `undefined` and the auth-server records null — that host opts out
      // of auto-pair, but QR pair still works.
      const relayCfg = existing.daemon?.relay;
      const relayEnabled = relayCfg?.enabled ?? true;
      const rawRelayEndpoint = relayEnabled
        ? (relayCfg?.publicEndpoint ?? relayCfg?.endpoint ?? "pair.appostle.app:443")
        : "";
      const relayEndpoint =
        typeof rawRelayEndpoint === "string" && rawRelayEndpoint.trim().length > 0
          ? rawRelayEndpoint.trim().slice(0, 256)
          : undefined;

      const linkUrl = new URL("/daemon/link", authServerUrl).toString();
      const ac = new AbortController();
      const timeoutHandle = setTimeout(() => ac.abort(), 10_000);
      let response: Response;
      try {
        response = await fetch(linkUrl, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            bootstrapToken,
            serverId,
            ...(label ? { label } : {}),
            fingerprint,
            // Phase 5: same value as `fingerprint`, sent under the more
            // specific name so the auth-server can record it as the daemon
            // pubkey for cross-device auto-pair. Both sent so an older
            // auth-server that doesn't know about `daemonPubkey` ignores it.
            daemonPubkey: fingerprint,
            ...(relayEndpoint ? { relayEndpoint } : {}),
          }),
          signal: ac.signal,
        });
      } catch (err) {
        clearTimeout(timeoutHandle);
        const message = err instanceof Error ? err.message : "network_error";
        log.warn({ err, linkUrl }, "link_account_request: fetch failed");
        respond({ ok: false, error: `network_error: ${message}` });
        return;
      }
      clearTimeout(timeoutHandle);

      if (!response.ok) {
        let bodyError = `http_${response.status}`;
        try {
          const errBody = (await response.json()) as { error?: unknown };
          if (typeof errBody?.error === "string") {
            bodyError = errBody.error;
          }
        } catch {
          // ignore — keep status-derived error
        }
        log.warn(
          { status: response.status, error: bodyError },
          "link_account_request: auth-server rejected",
        );
        respond({ ok: false, error: bodyError });
        return;
      }

      const body = (await response.json()) as Partial<{
        daemonToken: string;
        hostId: string;
        userId: string;
        serverPubkey: string;
      }>;
      if (
        typeof body.daemonToken !== "string" ||
        typeof body.hostId !== "string" ||
        typeof body.userId !== "string" ||
        typeof body.serverPubkey !== "string"
      ) {
        log.error({ body }, "link_account_request: malformed auth-server response");
        respond({ ok: false, error: "malformed_response" });
        return;
      }

      const nextConfig = {
        ...existing,
        account: {
          daemonToken: body.daemonToken,
          serverPubkey: body.serverPubkey,
          userId: body.userId,
          hostId: body.hostId,
          authServerUrl,
          linkedAt: new Date().toISOString(),
        },
      };
      savePersistedConfig(this.appostleHome, nextConfig, log);
      log.info(
        { hostId: body.hostId, userId: body.userId, authServerUrl },
        "daemon linked to account — starting allow-list sync",
      );

      respond({ ok: true, hostId: body.hostId, userId: body.userId });

      // Bring the auth-server client up in-process so the daemon transitions
      // from single-tenant (ownerUserId=null for everyone — bleed-prone) to
      // multi-tenant (pubkey-resolved ownership) WITHOUT a manual restart.
      // Bootstrap supplies the callback; it starts the client, awaits the
      // first poll, sets the wsServer resolver, and closes all existing
      // sessions so clients reconnect under strict auth. Fire-and-forget so
      // the WS response above flushes first; errors are logged but don't
      // affect the link RPC's success status (the account block is already
      // persisted — operator can manually restart as a fallback if the
      // callback fails for any reason).
      if (this.onAccountLinked) {
        void this.onAccountLinked({
          daemonToken: body.daemonToken,
          serverPubkey: body.serverPubkey,
          userId: body.userId,
          hostId: body.hostId,
          authServerUrl,
        }).catch((err) => {
          log.error({ err }, "onAccountLinked callback failed");
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown_error";
      log.error({ err }, "link_account_request: unexpected error");
      respond({ ok: false, error: message });
    }
  }

  // ---------------------------------------------------------------------------
  // Claude account token — per-user subscription on shared hosts
  // ---------------------------------------------------------------------------
  //
  // Each user pastes a long-lived OAuth token (`claude setup-token`, generated on
  // their own machine). We store it keyed by the connection's *verified* userId
  // (`this.ownerUserId`) and inject it as `CLAUDE_CODE_OAUTH_TOKEN` when their
  // agents spawn (see agent-manager `buildLaunchContext`). The wire never carries
  // an identity — the daemon always uses the verified session owner.

  private handleClaudeTokenSetRequest(
    msg: Extract<SessionInboundMessage, { type: "claude_token_set_request" }>,
  ): void {
    const { requestId, token } = msg;
    const trimmed = token.trim();
    // v1 validation is shape-only on purpose: `claude auth status` reports
    // "logged in" for ANY non-empty token (it just sees the env var present), so
    // it cannot prove a token actually works. Real proof happens when an agent
    // runs — a dead token surfaces a clear spawn error. The `valid` field is
    // kept on the wire so live validation can be added later without a change.
    const valid = trimmed.length >= 20 && !/\s/.test(trimmed);
    if (!valid) {
      this.emit({
        type: "claude_token_set_response",
        requestId,
        ok: false,
        valid: false,
        error:
          "That doesn't look like a Claude token. Run `claude setup-token` on your own computer and paste the full value.",
      });
      return;
    }
    this.claudeTokenStore.set(this.ownerUserId, trimmed);
    this.emit({ type: "claude_token_set_response", requestId, ok: true, valid: true });
  }

  private handleClaudeTokenStatusRequest(
    msg: Extract<SessionInboundMessage, { type: "claude_token_status_request" }>,
  ): void {
    const { requestId } = msg;
    const stored = this.claudeTokenStore.get(this.ownerUserId);
    this.emit({
      type: "claude_token_status_response",
      requestId,
      hasToken: stored !== null,
      valid: stored !== null,
      addedAt: stored?.addedAt,
    });
  }

  private handleClaudeTokenRemoveRequest(
    msg: Extract<SessionInboundMessage, { type: "claude_token_remove_request" }>,
  ): void {
    const { requestId } = msg;
    this.claudeTokenStore.remove(this.ownerUserId);
    this.emit({ type: "claude_token_remove_response", requestId, ok: true });
  }

  private async handleRestartServerRequest(requestId: string, reason?: string): Promise<void> {
    const payload: { status: string } & Record<string, unknown> = {
      status: "restart_requested",
      clientId: this.clientId,
    };
    if (reason && reason.trim().length > 0) {
      payload.reason = reason;
    }
    payload.requestId = requestId;

    this.sessionLogger.warn({ reason }, "Restart requested via websocket");
    this.emit({
      type: "status",
      payload,
    });

    this.emitLifecycleIntent({
      type: "restart",
      clientId: this.clientId,
      requestId,
      ...(reason ? { reason } : {}),
    });
  }

  private async handleShutdownServerRequest(requestId: string): Promise<void> {
    this.sessionLogger.warn("Shutdown requested via websocket");
    this.emit({
      type: "status",
      payload: {
        status: "shutdown_requested",
        clientId: this.clientId,
        requestId,
      },
    });

    this.emitLifecycleIntent({
      type: "shutdown",
      clientId: this.clientId,
      requestId,
    });
  }

  private emitLifecycleIntent(intent: SessionLifecycleIntent): void {
    if (!this.onLifecycleIntent) {
      return;
    }
    try {
      this.onLifecycleIntent(intent);
    } catch (error) {
      this.sessionLogger.error({ err: error, intent }, "Lifecycle intent handler failed");
    }
  }

  private async handleDeleteAgentRequest(agentId: string, requestId: string): Promise<void> {
    this.sessionLogger.info({ agentId }, `Deleting agent ${agentId} from registry`);

    const knownCwd =
      this.agentManager.getAgentSnapshot(agentId)?.cwd ??
      (await this.agentStorage.get(agentId))?.cwd ??
      null;

    // File-backed storage still needs an early delete fence before closeAgent().
    beginAgentDeleteIfSupported(this.agentStorage, agentId);

    try {
      await this.agentManager.closeAgent(agentId);
    } catch (error: any) {
      this.sessionLogger.warn(
        { err: error, agentId },
        `Failed to close agent ${agentId} during delete`,
      );
    }

    // Drain queued persistence from the just-closed agent before removing its
    // durable snapshot, otherwise an in-flight background write can recreate it.
    await this.agentManager.flush();

    try {
      await this.agentStorage.remove(agentId);
      await this.agentManager.deleteCommittedTimeline(agentId);
    } catch (error: any) {
      this.sessionLogger.error({ err: error, agentId }, `Failed to fully delete agent ${agentId}`);
    }

    this.emit({
      type: "agent_deleted",
      payload: {
        agentId,
        requestId,
      },
    });

    if (this.agentUpdatesSubscription) {
      this.bufferOrEmitAgentUpdate(this.agentUpdatesSubscription, {
        kind: "remove",
        agentId,
      });
    }

    if (knownCwd) {
      await this.emitWorkspaceUpdateForCwd(knownCwd);
    }
  }

  private async handleArchiveAgentRequest(agentId: string, requestId: string): Promise<void> {
    this.sessionLogger.info({ agentId }, `Archiving agent ${agentId}`);

    const { archivedAt } = await this.archiveAgentForClose(agentId);

    this.emit({
      type: "agent_archived",
      payload: {
        agentId,
        archivedAt,
        requestId,
      },
    });
  }

  private async handleShareSessionRequest(
    msg: Extract<SessionInboundMessage, { type: "share_session_request" }>,
  ): Promise<void> {
    const agent =
      this.agentManager.getAgentSnapshot(msg.agentId) ?? (await this.agentStorage.get(msg.agentId));
    if (!agent || agent.ownerUserId !== this.ownerUserId) {
      this.emit({
        type: "share_session_response",
        payload: {
          requestId: msg.requestId,
          agentId: msg.agentId,
          sessionShares: [],
          error: "unauthorized",
        },
      });
      return;
    }
    try {
      const sessionShares = await this.agentManager.shareSession(
        msg.agentId,
        msg.userId,
        msg.orgId,
      );
      this.emit({
        type: "share_session_response",
        payload: { requestId: msg.requestId, agentId: msg.agentId, sessionShares, error: null },
      });
    } catch (err) {
      this.emit({
        type: "share_session_response",
        payload: {
          requestId: msg.requestId,
          agentId: msg.agentId,
          sessionShares: [],
          error: err instanceof Error ? err.message : String(err),
        },
      });
    }
  }

  private async handleUnshareSessionRequest(
    msg: Extract<SessionInboundMessage, { type: "unshare_session_request" }>,
  ): Promise<void> {
    const agent =
      this.agentManager.getAgentSnapshot(msg.agentId) ?? (await this.agentStorage.get(msg.agentId));
    // Two authorised callers: (a) the agent owner revoking anyone, or
    // (b) a sharee revoking their own grant. The owner-only check was
    // a holdover from the pre-FSM design where archive was the sharee
    // path; under the new state machine, sharee-close → revoke-self.
    const isOwner = agent?.ownerUserId === this.ownerUserId;
    const isSelfRevoke = msg.userId === this.ownerUserId;
    if (!agent || (!isOwner && !isSelfRevoke)) {
      this.emit({
        type: "unshare_session_response",
        payload: {
          requestId: msg.requestId,
          agentId: msg.agentId,
          sessionShares: [],
          error: "unauthorized",
        },
      });
      return;
    }
    // Emit success BEFORE mutating sessionShares. The mutation triggers
    // an `emitState` whose filter drops events for users that no longer
    // have access (see `session.ts:1602` + `canUserAccessAgent`). The
    // response itself bypasses that filter (it goes through `this.emit`
    // → `onMessage` → raw `ws.send`), but we've observed intermittent
    // 10s client timeouts on this RPC in practice. Reordering removes
    // the entire class of "response races the access-revocation"
    // failures — the client gets its ack immediately, and the actual
    // revoke + fan-out happens after.
    this.emit({
      type: "unshare_session_response",
      payload: {
        requestId: msg.requestId,
        agentId: msg.agentId,
        sessionShares: agent.sessionShares ?? [],
        error: null,
      },
    });
    try {
      await this.agentManager.unshareSession(msg.agentId, msg.userId, msg.orgId);
    } catch (err) {
      this.sessionLogger.warn(
        { err, agentId: msg.agentId, userId: msg.userId },
        "unshareSession failed after early ack",
      );
    }
  }

  private async handleForkAgentRequest(
    msg: Extract<SessionInboundMessage, { type: "fork_agent_request" }>,
  ): Promise<void> {
    this.sessionLogger.info(
      { parentAgentId: msg.parentAgentId, forkPointUuid: msg.forkPointUuid },
      "Forking agent",
    );
    try {
      const { newAgentId, droppedUserText, record } = await this.agentManager.forkAgent({
        parentAgentId: msg.parentAgentId,
        forkPointUuid: msg.forkPointUuid,
        forkPointUserMessageIndex: msg.forkPointUserMessageIndex,
      });
      // Project the persisted record into a snapshot so the client can seed
      // its session store before navigating — otherwise the new tab lands in
      // the Default stack instead of inheriting the parent's stackId.
      const agentSnapshot = this.buildStoredAgentPayload(record);
      this.emit({
        type: "fork_agent_response",
        payload: {
          requestId: msg.requestId,
          success: true,
          newAgentId,
          parentAgentId: msg.parentAgentId,
          forkedFromMessageUuid: msg.forkPointUuid,
          droppedUserText,
          agent: agentSnapshot,
          error: null,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.sessionLogger.warn(
        { err, parentAgentId: msg.parentAgentId, forkPointUuid: msg.forkPointUuid },
        "Fork failed",
      );
      this.emit({
        type: "fork_agent_response",
        payload: {
          requestId: msg.requestId,
          success: false,
          newAgentId: null,
          parentAgentId: msg.parentAgentId,
          forkedFromMessageUuid: msg.forkPointUuid,
          error: message,
        },
      });
    }
  }

  private async handleRewindAgentRequest(
    msg: Extract<SessionInboundMessage, { type: "rewind_agent_request" }>,
  ): Promise<void> {
    this.sessionLogger.info(
      { agentId: msg.agentId, rewindPointUuid: msg.rewindPointUuid },
      "Rewinding agent",
    );
    try {
      const { droppedUserText, droppedRowCount } = await this.agentManager.rewindAgent({
        agentId: msg.agentId,
        rewindPointUuid: msg.rewindPointUuid,
        rewindPointUserMessageIndex: msg.rewindPointUserMessageIndex,
      });
      this.emit({
        type: "rewind_agent_response",
        payload: {
          requestId: msg.requestId,
          success: true,
          agentId: msg.agentId,
          rewoundToMessageUuid: msg.rewindPointUuid,
          droppedUserText,
          droppedRowCount,
          error: null,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.sessionLogger.warn(
        { err, agentId: msg.agentId, rewindPointUuid: msg.rewindPointUuid },
        "Rewind failed",
      );
      this.emit({
        type: "rewind_agent_response",
        payload: {
          requestId: msg.requestId,
          success: false,
          agentId: msg.agentId,
          rewoundToMessageUuid: msg.rewindPointUuid,
          error: message,
        },
      });
    }
  }

  private async archiveStoredAgentForClose(
    agentId: string,
  ): Promise<{ agentId: string; archivedAt: string }> {
    const existing = await this.agentStorage.get(agentId);
    if (!existing) {
      throw new Error(`Agent not found: ${agentId}`);
    }

    if (existing.archivedAt) {
      return {
        agentId,
        archivedAt: existing.archivedAt,
      };
    }

    const archivedAt = new Date().toISOString();
    await this.agentManager.archiveSnapshot(agentId, archivedAt);

    return { agentId, archivedAt };
  }

  private async archiveAgentForClose(
    agentId: string,
  ): Promise<{ agentId: string; archivedAt: string }> {
    const liveAgent = this.agentManager.getAgentSnapshot(agentId);
    if (liveAgent) {
      await this.interruptAgentIfRunning(agentId);
      await this.agentManager.clearAgentAttention(agentId).catch(() => undefined);
      await this.agentManager.archiveAgent(agentId);
    } else {
      await this.archiveStoredAgentForClose(agentId);
    }

    const archivedRecord = await this.agentStorage.get(agentId);
    if (!archivedRecord) {
      throw new Error(`Agent not found in storage after archive: ${agentId}`);
    }

    if (this.agentUpdatesSubscription) {
      const payload = this.buildStoredAgentPayload(archivedRecord);
      const project = await this.buildProjectPlacementForCwd(payload.cwd);
      if (project) {
        const matches = this.matchesAgentFilter({
          agent: payload,
          project,
          filter: this.agentUpdatesSubscription.filter,
        });
        this.bufferOrEmitAgentUpdate(
          this.agentUpdatesSubscription,
          matches
            ? {
                kind: "upsert",
                agent: payload,
                project,
              }
            : {
                kind: "remove",
                agentId,
              },
        );
      } else {
        this.bufferOrEmitAgentUpdate(this.agentUpdatesSubscription, {
          kind: "remove",
          agentId,
        });
      }
      await this.emitWorkspaceUpdateForCwd(payload.cwd);
    }

    if (!archivedRecord.archivedAt) {
      throw new Error(`Agent missing archivedAt after archive: ${agentId}`);
    }

    return { agentId, archivedAt: archivedRecord.archivedAt };
  }

  private async handleCloseItemsRequest(msg: CloseItemsRequest): Promise<void> {
    const agents = [];
    for (const agentId of msg.agentIds) {
      try {
        agents.push(await this.archiveAgentForClose(agentId));
      } catch (error: any) {
        this.sessionLogger.warn(
          { err: error, agentId, requestId: msg.requestId },
          "Failed to archive agent during close_items batch",
        );
      }
    }

    const terminals = [];
    for (const terminalId of msg.terminalIds) {
      try {
        terminals.push(this.killTerminalForClose(terminalId));
      } catch (error: any) {
        this.sessionLogger.warn(
          { err: error, terminalId, requestId: msg.requestId },
          "Failed to kill terminal during close_items batch",
        );
        terminals.push({
          terminalId,
          success: false,
        });
      }
    }

    this.emit({
      type: "close_items_response",
      payload: {
        agents,
        terminals,
        requestId: msg.requestId,
      },
    });
  }

  private async unarchiveAgentByHandle(handle: AgentPersistenceHandle): Promise<void> {
    const records = await this.agentStorage.list();
    const matched = records.find(
      (record) =>
        record.persistence?.provider === handle.provider &&
        record.persistence?.sessionId === handle.sessionId,
    );
    if (!matched) {
      return;
    }
    await unarchiveAgentState(this.agentStorage, this.agentManager, matched.id);
  }

  private async handleUpdateAgentRequest(
    agentId: string,
    name: string | undefined,
    labels: Record<string, string> | undefined,
    requestId: string,
    internal?: boolean,
    stackId?: string | null,
  ): Promise<void> {
    this.sessionLogger.info(
      {
        agentId,
        requestId,
        hasName: typeof name === "string",
        labelCount: labels ? Object.keys(labels).length : 0,
        internal,
        stackIdProvided: stackId !== undefined,
      },
      "session: update_agent_request",
    );

    const normalizedName = name?.trim();
    const normalizedLabels = labels && Object.keys(labels).length > 0 ? labels : undefined;

    if (!normalizedName && !normalizedLabels && internal === undefined && stackId === undefined) {
      this.emit({
        type: "update_agent_response",
        payload: {
          requestId,
          agentId,
          accepted: false,
          error: "Nothing to update (provide name, labels, internal, and/or stackId)",
        },
      });
      return;
    }

    // If the caller is moving the agent into a stack, validate that the
    // stackId belongs to this user's (workspace) scope before applying.
    // `null` (move to Default) is always allowed.
    if (stackId !== undefined && stackId !== null) {
      const workspaceId = await this.resolveWorkspaceIdForAgent(agentId);
      const belongs = workspaceId
        ? await this.workspaceStacksService.validateStackBelongs(
            this.ownerUserId,
            workspaceId,
            stackId,
          )
        : false;
      if (!belongs) {
        this.emit({
          type: "update_agent_response",
          payload: {
            requestId,
            agentId,
            accepted: false,
            error: "Stack does not belong to this user and workspace",
          },
        });
        return;
      }
    }

    try {
      await this.agentManager.updateAgentMetadata(agentId, {
        ...(normalizedName ? { title: normalizedName } : {}),
        ...(normalizedLabels ? { labels: normalizedLabels } : {}),
        ...(internal !== undefined ? { internal } : {}),
        ...(stackId !== undefined ? { stackId } : {}),
      });

      this.emit({
        type: "update_agent_response",
        payload: { requestId, agentId, accepted: true, error: null },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, agentId, requestId },
        "session: update_agent_request error",
      );
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to update agent: ${error.message}`,
        },
      });
      this.emit({
        type: "update_agent_response",
        payload: {
          requestId,
          agentId,
          accepted: false,
          error: error?.message ? String(error.message) : "Failed to update agent",
        },
      });
    }
  }

  /**
   * Map an agent to its workspaceId by matching the agent's `cwd` against
   * the workspace registry. Returns null if the agent doesn't exist or its
   * cwd doesn't match any registered workspace (e.g. ad-hoc agents created
   * outside a workspace context — those can't participate in stacks).
   */
  private async resolveWorkspaceIdForAgent(agentId: string): Promise<string | null> {
    const liveAgent = this.agentManager.getAgentSnapshot(agentId);
    const cwd = liveAgent ? liveAgent.cwd : ((await this.agentStorage.get(agentId))?.cwd ?? null);
    if (!cwd) return null;
    const workspaces = await this.workspaceRegistry.list();
    const match = workspaces.find((ws) => ws.cwd === cwd);
    return match?.workspaceId ?? null;
  }

  // ==========================================================================
  // Todo list — per-project quick-todo list. Mirrors the stacks broadcast
  // pattern: client subscribes to a projectId, daemon pushes full snapshots
  // on subscribe and on any change. See `todo-store.ts`.
  // ==========================================================================

  /** Resolve the cwd for a workspaceId from the workspace registry. */
  private async cwdForWorkspaceId(workspaceId: string): Promise<string | null> {
    const workspace = await this.workspaceRegistry.get(workspaceId);
    return workspace?.cwd ?? null;
  }

  /**
   * Re-fetch the todo list for a workspaceId and push a fresh todo_list_state
   * to this session. Used after every mutation so the client sees the updated
   * state immediately. Also fires `onTodoMutation` so the websocket-server can
   * fan the update out to every OTHER subscribed session.
   */
  private async pushTodoListState(workspaceId: string): Promise<void> {
    const cwd = await this.cwdForWorkspaceId(workspaceId);
    if (cwd === null) {
      this.emit({
        type: "todo_list_state",
        payload: { workspaceId, repoKey: null, noRemoteReason: "no_remote", todos: [] },
      });
      this.onTodoMutation?.(workspaceId);
      return;
    }
    const result = await this.todoStore.list(workspaceId, cwd);
    this.emit({
      type: "todo_list_state",
      payload: {
        workspaceId,
        repoKey: result.repoKey,
        noRemoteReason: result.repoKey === null ? "no_remote" : null,
        todos: result.todos.map(todoRecordToItem),
      },
    });
    this.onTodoMutation?.(workspaceId);
  }

  /**
   * Push a fresh `todo_list_state` to this session only if it is currently
   * subscribed to the given workspaceId. Called by the websocket-server's
   * broadcast path to fan out todo mutations from other sessions.
   */
  public async pushTodoListStateIfSubscribed(workspaceId: string): Promise<void> {
    if (!this.subscribedTodoWorkspaceIds.has(workspaceId)) return;
    const cwd = await this.cwdForWorkspaceId(workspaceId);
    if (cwd === null) {
      this.emit({
        type: "todo_list_state",
        payload: { workspaceId, repoKey: null, noRemoteReason: "no_remote", todos: [] },
      });
      return;
    }
    const result = await this.todoStore.list(workspaceId, cwd);
    this.emit({
      type: "todo_list_state",
      payload: {
        workspaceId,
        repoKey: result.repoKey,
        noRemoteReason: result.repoKey === null ? "no_remote" : null,
        todos: result.todos.map(todoRecordToItem),
      },
    });
  }

  private async handleTodoListSubscribe(
    msg: Extract<SessionInboundMessage, { type: "todo_list_subscribe" }>,
  ): Promise<void> {
    this.subscribedTodoWorkspaceIds.add(msg.workspaceId);
    try {
      await this.pushTodoListState(msg.workspaceId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, workspaceId: msg.workspaceId, requestId: msg.requestId },
        "todo_list_subscribe failed",
      );
      this.emit({
        type: "rpc_error",
        payload: {
          requestId: msg.requestId,
          error: message,
          code: "todo_list_subscribe_failed",
        },
      });
    }
  }

  private handleTodoListUnsubscribe(
    msg: Extract<SessionInboundMessage, { type: "todo_list_unsubscribe" }>,
  ): void {
    this.subscribedTodoWorkspaceIds.delete(msg.workspaceId);
  }

  private async handleTodoSetCompletedRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_set_completed_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.setCompleted(msg.todoId, msg.completed);
      this.emit({
        type: "todo_set_completed_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_set_completed failed",
      );
      this.emit({
        type: "todo_set_completed_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoAddRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_add_request" }>,
  ): Promise<void> {
    const cwd = await this.cwdForWorkspaceId(msg.workspaceId);
    if (cwd === null) {
      this.emit({
        type: "todo_add_response",
        payload: {
          requestId: msg.requestId,
          accepted: false,
          todoId: null,
          error: "workspace not found",
        },
      });
      return;
    }
    try {
      const record = await this.todoStore.add({
        workspaceId: msg.workspaceId,
        cwd,
        text: msg.text,
        createdByAgentId: null,
        createdByCwd: cwd,
      });
      this.emit({
        type: "todo_add_response",
        payload: { requestId: msg.requestId, accepted: true, todoId: record.id, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, workspaceId: msg.workspaceId, requestId: msg.requestId },
        "todo_add failed",
      );
      this.emit({
        type: "todo_add_response",
        payload: { requestId: msg.requestId, accepted: false, todoId: null, error: message },
      });
    }
  }

  private async handleTodoReorderRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_reorder_request" }>,
  ): Promise<void> {
    const cwd = await this.cwdForWorkspaceId(msg.workspaceId);
    if (cwd === null) {
      this.emit({
        type: "todo_reorder_response",
        payload: { requestId: msg.requestId, accepted: false, error: "workspace not found" },
      });
      return;
    }
    try {
      await this.todoStore.reorder(msg.workspaceId, cwd, msg.orderedTodoIds);
      this.emit({
        type: "todo_reorder_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, workspaceId: msg.workspaceId, requestId: msg.requestId },
        "todo_reorder failed",
      );
      this.emit({
        type: "todo_reorder_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoNotesUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_notes_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateNotes(msg.todoId, msg.notes);
      this.emit({
        type: "todo_notes_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_notes_update failed",
      );
      this.emit({
        type: "todo_notes_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoLinkedAgentUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_linked_agent_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateLinkedAgent(msg.todoId, {
        linkedAgentHostId: msg.linkedAgentHostId,
        linkedAgentId: msg.linkedAgentId,
      });
      this.emit({
        type: "todo_linked_agent_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_linked_agent_update failed",
      );
      this.emit({
        type: "todo_linked_agent_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoAssigneeUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_assignee_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateAssignee(msg.todoId, msg.assignedToUserId);
      this.emit({
        type: "todo_assignee_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_assignee_update failed",
      );
      this.emit({
        type: "todo_assignee_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoStatusUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_status_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateStatus(msg.todoId, msg.status);
      this.emit({
        type: "todo_status_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_status_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoPriorityUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_priority_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updatePriority(msg.todoId, msg.priority);
      this.emit({
        type: "todo_priority_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_priority_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoScheduleUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_schedule_update_request" }>,
  ): Promise<void> {
    try {
      const scheduleOpts: {
        scheduledAt?: string | null;
        dueAt?: string | null;
        scheduleTargetHostId?: string | null;
      } = {};
      if ("scheduledAt" in msg) scheduleOpts.scheduledAt = msg.scheduledAt;
      if ("dueAt" in msg) scheduleOpts.dueAt = msg.dueAt;
      if ("scheduleTargetHostId" in msg)
        scheduleOpts.scheduleTargetHostId = msg.scheduleTargetHostId;
      await this.todoStore.updateSchedule(msg.todoId, scheduleOpts);
      this.emit({
        type: "todo_schedule_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_schedule_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoBlockedByUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_blocked_by_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateBlockedBy(msg.todoId, msg.blockedByTodoId);
      this.emit({
        type: "todo_blocked_by_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_blocked_by_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoResultReportUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_result_report_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateResultReport(msg.todoId, msg.resultReport);
      this.emit({
        type: "todo_result_report_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_result_report_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoActivityAddRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_activity_add_request" }>,
  ): Promise<void> {
    try {
      const activity = await this.todoStore.addComment(msg.todoId, msg.body);
      this.emit({
        type: "todo_activity_add_response",
        payload: {
          requestId: msg.requestId,
          accepted: true,
          activity: {
            id: activity.id,
            todoId: activity.todoId,
            subtodoId: activity.subtodoId,
            kind: activity.kind,
            actorUserId: activity.actorUserId,
            actorUserName: activity.actorUserName,
            actorUserImage: activity.actorUserImage,
            actorAgentId: activity.actorAgentId,
            actorHostId: activity.actorHostId,
            body: activity.body,
            createdAt: activity.createdAt,
          },
        },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_activity_add_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoActivityListRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_activity_list_request" }>,
  ): Promise<void> {
    try {
      const entries = await this.todoStore.listActivity(msg.todoId);
      this.emit({
        type: "todo_activity_list_response",
        payload: {
          requestId: msg.requestId,
          activity: entries.map((e) => ({
            id: e.id,
            todoId: e.todoId,
            subtodoId: e.subtodoId,
            kind: e.kind,
            actorUserId: e.actorUserId,
            actorUserName: e.actorUserName,
            actorUserImage: e.actorUserImage,
            actorAgentId: e.actorAgentId,
            actorHostId: e.actorHostId,
            body: e.body,
            createdAt: e.createdAt,
          })),
        },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_activity_list_response",
        payload: { requestId: msg.requestId, activity: [], error: message },
      });
    }
  }

  private async handleTodoSessionsListRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_sessions_list_request" }>,
  ): Promise<void> {
    try {
      const sessions = await this.todoStore.listSessions(msg.todoId);
      this.emit({
        type: "todo_sessions_list_response",
        payload: {
          requestId: msg.requestId,
          sessions: sessions.map((s) => ({
            id: s.id,
            todoId: s.todoId,
            hostId: s.hostId,
            agentId: s.agentId,
            linkedAt: s.linkedAt,
            unlinkedAt: s.unlinkedAt,
          })),
        },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_sessions_list_response",
        payload: { requestId: msg.requestId, sessions: [], error: message },
      });
    }
  }

  private async handleTodoSubtaskNotesUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_notes_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateSubtodoNotes(msg.subtodoId, msg.notes);
      this.emit({
        type: "todo_subtask_notes_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_subtask_notes_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskAssigneeUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_assignee_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateSubtodoAssignee(msg.subtodoId, msg.assignedToUserId);
      this.emit({
        type: "todo_subtask_assignee_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_subtask_assignee_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskStatusUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_status_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateSubtodoStatus(msg.subtodoId, msg.status);
      this.emit({
        type: "todo_subtask_status_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_subtask_status_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskScheduleUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_schedule_update_request" }>,
  ): Promise<void> {
    try {
      const scheduleOpts: {
        scheduledAt?: string | null;
        dueAt?: string | null;
        scheduleTargetHostId?: string | null;
      } = {};
      if ("scheduledAt" in msg) scheduleOpts.scheduledAt = msg.scheduledAt;
      if ("dueAt" in msg) scheduleOpts.dueAt = msg.dueAt;
      if ("scheduleTargetHostId" in msg)
        scheduleOpts.scheduleTargetHostId = msg.scheduleTargetHostId;
      await this.todoStore.updateSubtodoSchedule(msg.subtodoId, scheduleOpts);
      this.emit({
        type: "todo_subtask_schedule_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_subtask_schedule_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskPriorityUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_priority_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateSubtodoPriority(msg.subtodoId, msg.priority);
      this.emit({
        type: "todo_subtask_priority_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_subtask_priority_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskBlockedByUpdateRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_blocked_by_update_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateSubtodoBlockedBy(msg.subtodoId, msg.blockedBySubtodoId);
      this.emit({
        type: "todo_subtask_blocked_by_update_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "todo_subtask_blocked_by_update_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoUpdateTextRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_update_text_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateText(msg.todoId, msg.text);
      this.emit({
        type: "todo_update_text_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_update_text failed",
      );
      this.emit({
        type: "todo_update_text_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskAddRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_add_request" }>,
  ): Promise<void> {
    try {
      const subtodoRecord = await this.todoStore.addSubtodo(msg.todoId, msg.text);
      this.emit({
        type: "todo_subtask_add_response",
        payload: {
          requestId: msg.requestId,
          accepted: true,
          subtodo: {
            id: subtodoRecord.id,
            todoId: subtodoRecord.todoId,
            text: subtodoRecord.text,
            position: subtodoRecord.position,
            completedAt: subtodoRecord.completedAt,
          },
          error: null,
        },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_subtask_add failed",
      );
      this.emit({
        type: "todo_subtask_add_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskUpdateTextRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_update_text_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.updateSubtodoText(msg.subtodoId, msg.text);
      this.emit({
        type: "todo_subtask_update_text_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, subtodoId: msg.subtodoId, requestId: msg.requestId },
        "todo_subtask_update_text failed",
      );
      this.emit({
        type: "todo_subtask_update_text_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskRemoveRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_remove_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.removeSubtodo(msg.subtodoId);
      this.emit({
        type: "todo_subtask_remove_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, subtodoId: msg.subtodoId, requestId: msg.requestId },
        "todo_subtask_remove failed",
      );
      this.emit({
        type: "todo_subtask_remove_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskSetCompletedRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_set_completed_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.setSubtodoCompleted(msg.subtodoId, msg.completed);
      this.emit({
        type: "todo_subtask_set_completed_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, subtodoId: msg.subtodoId, requestId: msg.requestId },
        "todo_subtask_set_completed failed",
      );
      this.emit({
        type: "todo_subtask_set_completed_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoSubtaskReorderRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_subtask_reorder_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.reorderSubtodos(msg.todoId, msg.orderedSubtodoIds);
      this.emit({
        type: "todo_subtask_reorder_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      if (this.subscribedTodoWorkspaceIds.has(msg.workspaceId)) {
        await this.pushTodoListState(msg.workspaceId);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_subtask_reorder failed",
      );
      this.emit({
        type: "todo_subtask_reorder_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleTodoRemoveRequest(
    msg: Extract<SessionInboundMessage, { type: "todo_remove_request" }>,
  ): Promise<void> {
    try {
      await this.todoStore.remove(msg.todoId);
      this.emit({
        type: "todo_remove_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, todoId: msg.todoId, requestId: msg.requestId },
        "todo_remove failed",
      );
      this.emit({
        type: "todo_remove_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
    // Push a fresh list to all subscribed workspaces after the remove attempt
    // (both success and failure). On success the deleted todo is gone. On
    // failure (e.g. 404 — todo already removed externally) the fresh list
    // also omits it, clearing the stale entry from the client's store.
    // todo_remove_request carries no workspaceId, so we broadcast to all
    // currently subscribed workspaces.
    for (const workspaceId of this.subscribedTodoWorkspaceIds) {
      void this.pushTodoListState(workspaceId).catch((err) => {
        this.sessionLogger.warn({ err, workspaceId }, "pushTodoListState after todo_remove failed");
      });
    }
  }

  // ==========================================================================
  // Tab stacks — see `.plans/tab-stacks.md` and `workspace-stacks-service.ts`.
  // Stacks are per-(userId, workspaceId) on the daemon; the agent's stackId
  // field rides agent snapshots so the client can group tabs by active
  // stack. After every mutation we broadcast a `workspace_stacks_state`
  // event so other sessions of the same user reflect the change.
  // ==========================================================================

  private emitWorkspaceStacksState(
    workspaceId: string,
    snapshot: {
      stacks: import("../shared/messages.js").WorkspaceStack[];
      activeStackId: string | null;
    },
  ): void {
    this.emit({
      type: "workspace_stacks_state",
      payload: {
        workspaceId,
        stacks: snapshot.stacks,
        activeStackId: snapshot.activeStackId,
      },
    });
  }

  private async handleStackCreateRequest(
    msg: Extract<SessionInboundMessage, { type: "stack_create_request" }>,
  ): Promise<void> {
    try {
      const result = await this.workspaceStacksService.createStack(
        this.ownerUserId,
        msg.workspaceId,
        msg.name,
      );
      this.emit({
        type: "stack_create_response",
        payload: {
          requestId: msg.requestId,
          accepted: true,
          stack: result.stack,
          error: null,
        },
      });
      this.emitWorkspaceStacksState(msg.workspaceId, result.snapshot);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, requestId: msg.requestId }, "stack_create failed");
      this.emit({
        type: "stack_create_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleStackRenameRequest(
    msg: Extract<SessionInboundMessage, { type: "stack_rename_request" }>,
  ): Promise<void> {
    // Stack mutations now go via auth-server (tab-store). The daemon no longer
    // owns stack rename/delete — reject with an explicit error so callers know
    // they're on the wrong path.
    this.sessionLogger.warn(
      { requestId: msg.requestId },
      "stack_rename_request rejected: stack mutations now go via auth-server",
    );
    this.emit({
      type: "stack_rename_response",
      payload: {
        requestId: msg.requestId,
        accepted: false,
        error: "stack mutations now go via auth-server",
      },
    });
  }

  private async handleStackDeleteRequest(
    msg: Extract<SessionInboundMessage, { type: "stack_delete_request" }>,
  ): Promise<void> {
    // Stack mutations now go via auth-server (tab-store). The daemon no longer
    // owns stack rename/delete — reject with an explicit error so callers know
    // they're on the wrong path.
    this.sessionLogger.warn(
      { requestId: msg.requestId },
      "stack_delete_request rejected: stack mutations now go via auth-server",
    );
    this.emit({
      type: "stack_delete_response",
      payload: {
        requestId: msg.requestId,
        accepted: false,
        movedAgentCount: 0,
        error: "stack mutations now go via auth-server",
      },
    });
  }

  private async handleStackReorderRequest(
    msg: Extract<SessionInboundMessage, { type: "stack_reorder_request" }>,
  ): Promise<void> {
    try {
      const snapshot = await this.workspaceStacksService.reorderStacks(
        this.ownerUserId,
        msg.workspaceId,
        msg.orderedStackIds,
      );
      this.emit({
        type: "stack_reorder_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      this.emitWorkspaceStacksState(msg.workspaceId, snapshot);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, requestId: msg.requestId }, "stack_reorder failed");
      this.emit({
        type: "stack_reorder_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  private async handleStackSetActiveRequest(
    msg: Extract<SessionInboundMessage, { type: "stack_set_active_request" }>,
  ): Promise<void> {
    try {
      const snapshot = await this.workspaceStacksService.setActiveStack(
        this.ownerUserId,
        msg.workspaceId,
        msg.stackId,
      );
      this.emit({
        type: "stack_set_active_response",
        payload: { requestId: msg.requestId, accepted: true, error: null },
      });
      this.emitWorkspaceStacksState(msg.workspaceId, snapshot);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, requestId: msg.requestId }, "stack_set_active failed");
      this.emit({
        type: "stack_set_active_response",
        payload: { requestId: msg.requestId, accepted: false, error: message },
      });
    }
  }

  /**
   * Push a `workspace_stacks_state` snapshot for every workspace currently
   * known on this daemon. Called when the client first subscribes to
   * workspace state so the picker + tab filter have data immediately —
   * subsequent updates ride on the per-mutation broadcasts above.
   */
  private async emitInitialStacksStateForAllWorkspaces(): Promise<void> {
    try {
      const workspaces = await this.workspaceRegistry.list();
      for (const workspace of workspaces) {
        const snapshot = await this.workspaceStacksService.getSnapshot(
          this.ownerUserId,
          workspace.workspaceId,
        );
        this.emitWorkspaceStacksState(workspace.workspaceId, snapshot);
      }
    } catch (error) {
      this.sessionLogger.error({ err: error }, "Failed to emit initial workspace stacks state");
    }
  }

  private toVoiceFeatureUnavailableContext(
    state: SpeechReadinessState,
  ): VoiceFeatureUnavailableContext {
    return {
      reasonCode: state.reasonCode,
      message: state.message,
      retryable: state.retryable,
      missingModelIds: [...state.missingModelIds],
    };
  }

  private resolveModeReadinessState(
    readiness: SpeechReadinessSnapshot,
    mode: "voice_mode" | "dictation",
  ): SpeechReadinessState {
    if (mode === "voice_mode") {
      return readiness.realtimeVoice;
    }
    return readiness.dictation;
  }

  private getVoiceFeatureUnavailableResponseMetadata(
    error: unknown,
  ): VoiceFeatureUnavailableResponseMetadata {
    if (!(error instanceof VoiceFeatureUnavailableError)) {
      return {};
    }
    return {
      reasonCode: error.reasonCode,
      retryable: error.retryable,
      missingModelIds: error.missingModelIds,
    };
  }

  private resolveVoiceFeatureUnavailableContext(
    mode: "voice_mode" | "dictation",
  ): VoiceFeatureUnavailableContext | null {
    const readiness = this.getSpeechReadiness?.();
    if (!readiness) {
      return null;
    }

    const modeReadiness = this.resolveModeReadinessState(readiness, mode);
    if (!modeReadiness.enabled) {
      return this.toVoiceFeatureUnavailableContext(modeReadiness);
    }
    if (!readiness.voiceFeature.available) {
      return this.toVoiceFeatureUnavailableContext(readiness.voiceFeature);
    }
    if (!modeReadiness.available) {
      return this.toVoiceFeatureUnavailableContext(modeReadiness);
    }
    return null;
  }

  /**
   * Handle voice mode toggle
   */
  private async handleSetVoiceMode(
    enabled: boolean,
    agentId?: string,
    requestId?: string,
  ): Promise<void> {
    const startedAt = Date.now();
    try {
      this.sessionLogger.info(
        { enabled, requestedAgentId: agentId ?? null, requestId: requestId ?? null },
        "set_voice_mode started",
      );
      if (enabled) {
        const unavailable = this.resolveVoiceFeatureUnavailableContext("voice_mode");
        if (unavailable) {
          throw new VoiceFeatureUnavailableError(unavailable);
        }

        const normalizedAgentId = this.parseVoiceTargetAgentId(agentId ?? "", "set_voice_mode");

        if (
          this.isVoiceMode &&
          this.voiceModeAgentId &&
          this.voiceModeAgentId !== normalizedAgentId
        ) {
          this.sessionLogger.info(
            {
              previousAgentId: this.voiceModeAgentId,
              nextAgentId: normalizedAgentId,
              elapsedMs: Date.now() - startedAt,
            },
            "set_voice_mode disabling previous active voice agent",
          );
          await this.disableVoiceModeForActiveAgent(true);
        }

        if (!this.isVoiceMode || this.voiceModeAgentId !== normalizedAgentId) {
          this.sessionLogger.info(
            { agentId: normalizedAgentId, elapsedMs: Date.now() - startedAt },
            "set_voice_mode enabling voice for agent",
          );
          const refreshedAgentId = await this.enableVoiceModeForAgent(normalizedAgentId);
          this.voiceModeAgentId = refreshedAgentId;
          this.sessionLogger.info(
            { agentId: refreshedAgentId, elapsedMs: Date.now() - startedAt },
            "set_voice_mode agent enable complete",
          );
        }

        this.sessionLogger.info(
          { agentId: this.voiceModeAgentId, elapsedMs: Date.now() - startedAt },
          "set_voice_mode starting voice turn controller",
        );
        await this.startVoiceTurnController();
        this.sessionLogger.info(
          { agentId: this.voiceModeAgentId, elapsedMs: Date.now() - startedAt },
          "set_voice_mode voice turn controller started",
        );
        this.isVoiceMode = true;
        this.sessionLogger.info(
          {
            agentId: this.voiceModeAgentId,
            elapsedMs: Date.now() - startedAt,
          },
          "Voice mode enabled for existing agent",
        );
        if (requestId) {
          this.emit({
            type: "set_voice_mode_response",
            payload: {
              requestId,
              enabled: true,
              agentId: this.voiceModeAgentId,
              accepted: true,
              error: null,
            },
          });
        }
        return;
      }

      this.sessionLogger.info(
        { agentId: this.voiceModeAgentId, elapsedMs: Date.now() - startedAt },
        "set_voice_mode disabling active voice mode",
      );
      await this.disableVoiceModeForActiveAgent(true);
      this.isVoiceMode = false;
      this.sessionLogger.info({ elapsedMs: Date.now() - startedAt }, "Voice mode disabled");
      if (requestId) {
        this.emit({
          type: "set_voice_mode_response",
          payload: {
            requestId,
            enabled: false,
            agentId: null,
            accepted: true,
            error: null,
          },
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to set voice mode";
      const unavailable = this.getVoiceFeatureUnavailableResponseMetadata(error);
      this.sessionLogger.error(
        {
          err: error,
          enabled,
          requestedAgentId: agentId ?? null,
          elapsedMs: Date.now() - startedAt,
        },
        "set_voice_mode failed",
      );
      if (requestId) {
        this.emit({
          type: "set_voice_mode_response",
          payload: {
            requestId,
            enabled: this.isVoiceMode,
            agentId: this.voiceModeAgentId,
            accepted: false,
            error: errorMessage,
            ...unavailable,
          },
        });
        return;
      }
      throw error;
    }
  }

  private parseVoiceTargetAgentId(rawId: string, source: string): string {
    const parsed = AgentIdSchema.safeParse(rawId.trim());
    if (!parsed.success) {
      throw new Error(`${source}: agentId must be a UUID`);
    }
    return parsed.data;
  }

  private async enableVoiceModeForAgent(agentId: string): Promise<string> {
    const startedAt = Date.now();
    this.sessionLogger.info({ agentId }, "enableVoiceModeForAgent.ensureAgentLoaded.start");
    const existing = await ensureAgentLoaded(agentId, {
      agentManager: this.agentManager,
      agentStorage: this.agentStorage,
      logger: this.sessionLogger,
    });
    this.sessionLogger.info(
      { agentId, elapsedMs: Date.now() - startedAt },
      "enableVoiceModeForAgent.ensureAgentLoaded.done",
    );

    this.registerVoiceBridgeForAgent(agentId);

    const baseConfig: VoiceModeBaseConfig = {
      systemPrompt: stripVoiceModeSystemPrompt(existing.config.systemPrompt),
    };
    this.voiceModeBaseConfig = baseConfig;
    const refreshOverrides: Partial<AgentSessionConfig> = {
      systemPrompt: buildVoiceModeSystemPrompt(baseConfig.systemPrompt, true),
    };

    try {
      this.sessionLogger.info(
        { agentId, elapsedMs: Date.now() - startedAt },
        "enableVoiceModeForAgent.reloadAgentSession.start",
      );
      const refreshed = await this.agentManager.reloadAgentSession(agentId, refreshOverrides);
      this.sessionLogger.info(
        { agentId, refreshedAgentId: refreshed.id, elapsedMs: Date.now() - startedAt },
        "enableVoiceModeForAgent.reloadAgentSession.done",
      );
      return refreshed.id;
    } catch (error) {
      this.unregisterVoiceSpeakHandler?.(agentId);
      this.unregisterVoiceCallerContext?.(agentId);
      this.voiceModeBaseConfig = null;
      throw error;
    }
  }

  private async disableVoiceModeForActiveAgent(restoreAgentConfig: boolean): Promise<void> {
    await this.stopVoiceTurnController();

    const agentId = this.voiceModeAgentId;
    if (!agentId) {
      this.voiceModeBaseConfig = null;
      return;
    }

    this.unregisterVoiceSpeakHandler?.(agentId);
    this.unregisterVoiceCallerContext?.(agentId);

    if (restoreAgentConfig && this.voiceModeBaseConfig) {
      const baseConfig = this.voiceModeBaseConfig;
      try {
        await this.agentManager.reloadAgentSession(agentId, {
          systemPrompt: buildVoiceModeSystemPrompt(baseConfig.systemPrompt, false),
        });
      } catch (error) {
        this.sessionLogger.warn(
          { err: error, agentId },
          "Failed to restore agent config while disabling voice mode",
        );
      }
    }

    this.voiceModeBaseConfig = null;
    this.voiceModeAgentId = null;
  }

  private handleDictationManagerMessage(msg: DictationStreamOutboundMessage): void {
    this.emit(msg as unknown as SessionOutboundMessage);
  }

  private async startVoiceTurnController(): Promise<void> {
    if (this.voiceTurnController) {
      this.sessionLogger.info("startVoiceTurnController skipped: already running");
      return;
    }

    const turnDetection = this.resolveVoiceTurnDetection();
    if (!turnDetection) {
      throw new Error("Voice turn detection is not configured");
    }

    this.sessionLogger.info(
      { providerId: turnDetection.id },
      "startVoiceTurnController creating controller",
    );

    const controller = createVoiceTurnController({
      logger: this.sessionLogger.child({ component: "voice-turn-controller" }),
      turnDetection,
      utteranceSink: {
        submitUtterance: async ({ pcm16, format, sampleRate, startedAt, endedAt }) => {
          this.sessionLogger.debug(
            {
              audioBytes: pcm16.length,
              sampleRate,
              startedAt,
              endedAt,
              durationMs: Math.max(0, endedAt - startedAt),
            },
            "Submitting detected voice utterance",
          );
          await this.processCompletedAudio(pcm16, format);
        },
      },
      callbacks: {
        onSpeechStarted: async () => {
          this.handleProvisionalVoiceSpeechStarted();
        },
        onSpeechStopped: async () => {
          this.handleVoiceSpeechStopped();
        },
        onError: (error) => {
          this.sessionLogger.error({ err: error }, "Voice turn controller failed");
        },
      },
    });

    this.sessionLogger.info("startVoiceTurnController connecting controller");
    await controller.start();
    this.voiceTurnController = controller;
    this.sessionLogger.info("startVoiceTurnController connected");
  }

  private async stopVoiceTurnController(): Promise<void> {
    if (!this.voiceTurnController) {
      return;
    }

    this.clearPendingVoiceSpeechStart("turn-controller-stop");
    const controller = this.voiceTurnController;
    this.voiceTurnController = null;
    await controller.stop();
  }

  private clearPendingVoiceSpeechStart(reason: string): void {
    if (this.pendingVoiceSpeechTimer) {
      clearTimeout(this.pendingVoiceSpeechTimer);
      this.pendingVoiceSpeechTimer = null;
    }
    if (this.pendingVoiceSpeechStartAt !== null) {
      this.sessionLogger.debug({ reason }, "Clearing provisional voice speech start");
      this.pendingVoiceSpeechStartAt = null;
    }
  }

  private handleProvisionalVoiceSpeechStarted(): void {
    if (this.speechInProgress || this.pendingVoiceSpeechTimer) {
      return;
    }

    const startedAt = Date.now();
    this.pendingVoiceSpeechStartAt = startedAt;
    this.sessionLogger.info(
      { confirmationMs: VOICE_INTERRUPT_CONFIRMATION_MS },
      "Silero VAD provisional speech_started",
    );
    this.pendingVoiceSpeechTimer = setTimeout(() => {
      this.pendingVoiceSpeechTimer = null;
      if (this.pendingVoiceSpeechStartAt !== startedAt || this.speechInProgress) {
        return;
      }

      this.pendingVoiceSpeechStartAt = null;
      this.sessionLogger.info("voice_input_state emitting isSpeaking=true");
      this.emit({
        type: "voice_input_state",
        payload: {
          isSpeaking: true,
        },
      });
      void this.handleVoiceSpeechStart();
    }, VOICE_INTERRUPT_CONFIRMATION_MS);
  }

  private handleVoiceSpeechStopped(): void {
    if (this.pendingVoiceSpeechStartAt !== null) {
      const durationMs = Date.now() - this.pendingVoiceSpeechStartAt;
      this.clearPendingVoiceSpeechStart("speech-stopped-before-confirmation");
      this.sessionLogger.info(
        { durationMs, confirmationMs: VOICE_INTERRUPT_CONFIRMATION_MS },
        "Ignoring provisional voice speech start that ended before confirmation",
      );
      return;
    }

    this.sessionLogger.info("voice_input_state emitting isSpeaking=false");
    this.emit({
      type: "voice_input_state",
      payload: {
        isSpeaking: false,
      },
    });
  }

  /**
   * Handle text message to agent (with optional image attachments)
   */
  private async handleSendAgentMessage(
    agentId: string,
    text: string,
    messageId?: string,
    images?: Array<{
      data: string;
      mimeType: string;
      // Optional client-side identity for chip rehydration. Present on new
      // clients; old clients omit them and we just skip the image echo.
      id?: string;
      fileName?: string;
      byteSize?: number;
    }>,
    attachments?: AgentAttachment[],
    runOptions?: AgentRunOptions,
    options?: { spokenInput?: boolean; files?: readonly FileAttachment[] },
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    const files = options?.files ?? [];
    this.sessionLogger.info(
      {
        agentId,
        textPreview: text.substring(0, 50),
        imageCount: images?.length ?? 0,
        attachmentCount: attachments?.length ?? 0,
        fileCount: files.length,
      },
      `Sending text to agent ${agentId}${
        images && images.length > 0 ? ` with ${images.length} image attachment(s)` : ""
      }${
        attachments && attachments.length > 0
          ? ` and ${attachments.length} structured attachment(s)`
          : ""
      }${files.length > 0 ? ` and ${files.length} file attachment(s)` : ""}`,
    );

    // Persist file uploads BEFORE building the prompt so the footer can list
    // their on-disk paths. We resolve the workspace cwd from the agent record;
    // if the agent isn't loaded yet the persistence is skipped and we surface
    // an error to the caller — silent drop would mislead the user.
    let persistedFiles: SessionUpload[] = [];
    let persistedImages: SessionImage[] = [];
    const hasFiles = files.length > 0;
    const hasImages = (images?.length ?? 0) > 0;
    if (hasFiles || hasImages) {
      const agent = this.agentManager.getAgentSnapshot(agentId);
      if (!agent) {
        return {
          ok: false,
          error: `Agent ${agentId} not found; cannot persist attachments`,
        };
      }
      try {
        if (hasFiles) {
          persistedFiles = await this.uploadStore.persistFiles({
            cwd: agent.cwd,
            agentId,
            messageId: messageId ?? null,
            files,
          });
        }
        if (hasImages && images) {
          persistedImages = await this.imageStore.persistImages({
            cwd: agent.cwd,
            agentId,
            messageId: messageId ?? null,
            images,
          });
        }
      } catch (error) {
        if (error instanceof UploadSizeError) {
          return {
            ok: false,
            error: `Attachments exceed the ${MAX_UPLOAD_BYTES_PER_MESSAGE} byte limit per message`,
          };
        }
        const message = error instanceof Error ? error.message : String(error);
        return { ok: false, error: `Failed to persist attachments: ${message}` };
      }
    }

    const promptText = options?.spokenInput ? wrapSpokenInput(text) : text;
    // Image bytes for the LLM prompt come from disk after persistence — the
    // daemon is the single source of truth (Clay pattern). Keeps the prompt
    // de-coupled from the wire payload, which is now just an upload conduit.
    // Falls back to wire bytes when persistence didn't run (legacy clients
    // without `id`, or images-only sends from old daemons).
    const promptImages =
      persistedImages.length > 0
        ? await this.loadPromptImagesFromDisk(persistedImages, agentId)
        : images;
    const prompt = this.buildAgentPrompt(promptText, promptImages, attachments, persistedFiles);
    // Recorded timeline entries that get replayed to Claude on later turns must
    // have non-empty text — empty content blocks combined with the SDK's
    // automatic cache_control breakpoints get rejected by the Anthropic API
    // ("cache_control cannot be set for empty text blocks"). When the user
    // attaches files or images without typing anything, synthesize a short
    // summary so the recorded message has content (also keeps the bubble
    // visible after Cmd-R — the frontend reducer drops empty-text entries).
    const recordedText = synthesizeRecordedUserText(text, persistedFiles, images?.length ?? 0);

    // Bytes-stripped chip metadata. Persisted on the user_message timeline
    // row so a reload re-renders the attachment chip without round-tripping
    // bytes. Each entry can carry a `daemonRef` pointing at the daemon's
    // canonical copy — clients use it to fetch image/file bytes via the
    // `fetch_attachment_bytes_request` RPC.
    //
    // Images: when persistence succeeded, the matching SessionImage gives us
    // the daemonRef (kind, attachmentId, relativePath). When the client
    // didn't supply an `id` we still echo the persisted entry's id so reload
    // can find the chip. Order is preserved one-to-one with `images`.
    const userMessageImages = buildImageEcho(images ?? [], persistedImages);
    const userMessageFiles = persistedFiles.map((f) => ({
      id: f.id,
      fileName: f.fileName,
      mimeType: f.mimeType,
      size: f.size,
      daemonRef: {
        kind: "file" as const,
        attachmentId: f.id,
        relativePath: f.relativePath,
      },
    }));

    // True when the recorded text was synthesized by the daemon (user typed
    // nothing, but attachments were present). The chat bubble hides text
    // when this is set; the LLM still sees it for cache_control compliance.
    const recordedTextIsSynthesized =
      text.trim().length === 0 && (userMessageImages.length > 0 || userMessageFiles.length > 0);

    try {
      await sendPromptToAgent({
        agentManager: this.agentManager,
        agentStorage: this.agentStorage,
        agentId,
        userMessageText: recordedText,
        prompt,
        messageId,
        runOptions,
        ...(userMessageImages.length > 0 ? { userMessageImages } : {}),
        ...(userMessageFiles.length > 0 ? { userMessageFiles } : {}),
        ...(recordedTextIsSynthesized ? { userMessageTextIsSynthesized: true } : {}),
        logger: this.sessionLogger,
      });
      return { ok: true };
    } catch (error) {
      this.handleAgentRunError(agentId, error, "Failed to send agent message");
      return {
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Handle create agent request
   */
  private async handleCreateAgentRequest(
    msg: Extract<SessionInboundMessage, { type: "create_agent_request" }>,
  ): Promise<void> {
    const {
      config,
      worktreeName,
      requestId,
      initialPrompt,
      clientMessageId,
      outputSchema,
      git,
      images,
      files,
      attachments,
      labels,
    } = msg;
    this.sessionLogger.info(
      { cwd: config.cwd, provider: config.provider, worktreeName },
      `Creating agent in ${config.cwd} (${config.provider})${
        worktreeName ? ` with worktree ${worktreeName}` : ""
      }`,
    );

    try {
      const trimmedPrompt = initialPrompt?.trim();
      const { explicitTitle, provisionalTitle } = resolveCreateAgentTitles({
        configTitle: config.title,
        initialPrompt: trimmedPrompt,
      });
      const resolvedConfig: AgentSessionConfig = {
        ...config,
        ...(provisionalTitle ? { title: provisionalTitle } : {}),
      };

      const { sessionConfig, worktreeBootstrap } = await this.buildAgentSessionConfig(
        resolvedConfig,
        git,
        worktreeName,
        attachments,
      );
      const resolvedWorkspace = msg.workspaceId
        ? await this.workspaceRegistry.get(msg.workspaceId)
        : ((await this.findWorkspaceByDirectory(sessionConfig.cwd)) ??
          (await this.findOrCreateWorkspaceForDirectory(sessionConfig.cwd)));
      if (!resolvedWorkspace) {
        throw new Error(`Workspace not found: ${msg.workspaceId}`);
      }
      // Resolve the stack the new agent should land in. Priority order:
      //   1. Client-supplied stackId on the request (caller knows better).
      //   2. The user's currently-active stack for this workspace.
      //   3. Default (null) — agent stays unstacked.
      // Validation: a non-null stackId must belong to the user's stack list
      // for the resolved workspace; otherwise fall back to active/Default.
      let resolvedStackId: string | null = null;
      if (msg.stackId !== undefined && msg.stackId !== null) {
        const belongs = await this.workspaceStacksService.validateStackBelongs(
          this.ownerUserId,
          resolvedWorkspace.workspaceId,
          msg.stackId,
        );
        resolvedStackId = belongs ? msg.stackId : null;
      } else if (msg.stackId === undefined) {
        const snap = await this.workspaceStacksService.getSnapshot(
          this.ownerUserId,
          resolvedWorkspace.workspaceId,
        );
        resolvedStackId = snap.activeStackId;
      }

      const snapshot = await this.agentManager.createAgent(
        {
          ...sessionConfig,
          cwd: resolvedWorkspace.cwd,
        },
        undefined,
        {
          labels,
          workspaceId: resolvedWorkspace.workspaceId,
          initialPrompt: trimmedPrompt,
          // Stamp the agent with the user that created it. Required —
          // the websocket-server has already resolved the peer's pubkey to
          // an auth-server userId, so this is always present.
          ownerUserId: this.ownerUserId,
          // Self-declared identity, used for Claude profile dir naming etc.
          userId: this.userId ?? undefined,
          username: this.username ?? undefined,
          stackId: resolvedStackId,
        },
      );
      await this.forwardAgentUpdate(snapshot);

      if (
        trimmedPrompt ||
        (images?.length ?? 0) > 0 ||
        (attachments?.length ?? 0) > 0 ||
        (files?.length ?? 0) > 0
      ) {
        scheduleAgentMetadataGeneration({
          agentManager: this.agentManager,
          agentId: snapshot.id,
          cwd: snapshot.cwd,
          initialPrompt: trimmedPrompt,
          explicitTitle,
          appostleHome: this.appostleHome,
          logger: this.sessionLogger,
          ownerUserId: this.ownerUserId,
        });

        const started = await this.handleSendAgentMessage(
          snapshot.id,
          trimmedPrompt || "",
          resolveClientMessageId(clientMessageId),
          images,
          attachments,
          outputSchema ? { outputSchema } : undefined,
          files && files.length > 0 ? { files } : undefined,
        );
        if (!started.ok) {
          throw new Error(started.error);
        }
      }

      if (requestId) {
        const agentPayload = await this.buildAgentPayload(snapshot);
        this.emit({
          type: "status",
          payload: {
            status: "agent_created",
            agentId: snapshot.id,
            requestId,
            agent: agentPayload,
          },
        });
      }

      if (worktreeBootstrap) {
        void runAsyncWorktreeBootstrap({
          agentId: snapshot.id,
          worktree: worktreeBootstrap.worktree,
          shouldBootstrap: worktreeBootstrap.shouldBootstrap,
          terminalManager: this.terminalManager,
          appendTimelineItem: (item) =>
            appendTimelineItemIfAgentKnown({
              agentManager: this.agentManager,
              agentId: snapshot.id,
              item,
            }),
          emitLiveTimelineItem: (item) =>
            emitLiveTimelineItemIfAgentKnown({
              agentManager: this.agentManager,
              agentId: snapshot.id,
              item,
            }),
          logger: this.sessionLogger,
        });
      }

      this.sessionLogger.info(
        { agentId: snapshot.id, provider: snapshot.provider },
        `Created agent ${snapshot.id} (${snapshot.provider})`,
      );
    } catch (error: any) {
      const wireError = toWorktreeWireError(error);
      this.sessionLogger.error({ err: error }, "Failed to create agent");
      if (requestId) {
        this.emit({
          type: "status",
          payload: {
            status: "agent_create_failed",
            requestId,
            error: wireError.message,
            errorCode: wireError.code,
          },
        });
      }
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to create agent: ${wireError.message}`,
        },
      });
    }
  }

  private async handleResumeAgentRequest(
    msg: Extract<SessionInboundMessage, { type: "resume_agent_request" }>,
  ): Promise<void> {
    const { handle, overrides, requestId } = msg;
    if (!handle) {
      this.sessionLogger.warn("Resume request missing persistence handle");
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: "Unable to resume agent: missing persistence handle",
        },
      });
      return;
    }
    this.sessionLogger.info(
      { sessionId: handle.sessionId, provider: handle.provider },
      `Resuming agent ${handle.sessionId} (${handle.provider})`,
    );
    try {
      await this.unarchiveAgentByHandle(handle);
      const snapshot = await this.agentManager.resumeAgentFromPersistence(handle, overrides);
      await unarchiveAgentState(this.agentStorage, this.agentManager, snapshot.id);
      await this.agentManager.hydrateTimelineFromProvider(snapshot.id);
      await this.forwardAgentUpdate(snapshot);
      const timelineSize = this.agentManager.getTimeline(snapshot.id).length;
      if (requestId) {
        const agentPayload = await this.buildAgentPayload(snapshot);
        this.emit({
          type: "status",
          payload: {
            status: "agent_resumed",
            agentId: snapshot.id,
            requestId,
            timelineSize,
            agent: agentPayload,
          },
        });
      }
    } catch (error: any) {
      this.sessionLogger.error({ err: error }, "Failed to resume agent");
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to resume agent: ${error.message}`,
        },
      });
    }
  }

  private async handleRefreshAgentRequest(
    msg: Extract<SessionInboundMessage, { type: "refresh_agent_request" }>,
  ): Promise<void> {
    const { agentId, requestId } = msg;
    this.sessionLogger.info({ agentId }, `Refreshing agent ${agentId} from persistence`);

    try {
      await unarchiveAgentState(this.agentStorage, this.agentManager, agentId);
      let snapshot: ManagedAgent;
      const existing = this.agentManager.getAgentSnapshot(agentId);
      if (existing) {
        await this.interruptAgentIfRunning(agentId);
        snapshot = await this.agentManager.reloadAgentSession(agentId);
      } else {
        const record = await this.agentStorage.get(agentId);
        if (!record) {
          throw new Error(`Agent not found: ${agentId}`);
        }
        const handle = toAgentPersistenceHandle(
          this.sessionLogger,
          this.providerRegistry,
          record.persistence,
        );
        if (!handle) {
          throw new Error(`Agent ${agentId} cannot be refreshed because it lacks persistence`);
        }
        snapshot = await this.agentManager.resumeAgentFromPersistence(
          handle,
          buildConfigOverrides(record),
          agentId,
          extractTimestamps(record),
        );
      }
      await this.agentManager.hydrateTimelineFromProvider(agentId);
      await this.forwardAgentUpdate(snapshot);
      const timelineSize = this.agentManager.getTimeline(agentId).length;
      if (requestId) {
        this.emit({
          type: "status",
          payload: {
            status: "agent_refreshed",
            agentId,
            requestId,
            timelineSize,
          },
        });
      }
    } catch (error: any) {
      this.sessionLogger.error({ err: error, agentId }, `Failed to refresh agent ${agentId}`);
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to refresh agent: ${error.message}`,
        },
      });
    }
  }

  private async handleCancelAgentRequest(agentId: string, requestId?: string): Promise<void> {
    this.sessionLogger.info({ agentId }, `Cancel request received for agent ${agentId}`);

    try {
      await this.interruptAgentIfRunning(agentId);
      if (requestId) {
        const agent = this.agentManager.getAgentSnapshot(agentId);
        const payload = agent ? await this.buildAgentPayload(agent) : null;
        this.emit({
          type: "cancel_agent_response",
          payload: {
            requestId,
            agentId,
            agent: payload,
          },
        });
      }
    } catch (error) {
      this.handleAgentRunError(agentId, error, "Failed to cancel running agent on request");
    }
  }

  private async buildAgentSessionConfig(
    config: AgentSessionConfig,
    gitOptions?: GitSetupOptions,
    legacyWorktreeName?: string,
    attachments?: AgentAttachment[],
  ): Promise<{
    sessionConfig: AgentSessionConfig;
    worktreeBootstrap?: { worktree: WorktreeConfig; shouldBootstrap: boolean };
  }> {
    return buildWorktreeAgentSessionConfig(
      {
        appostleHome: this.appostleHome,
        sessionLogger: this.sessionLogger,
        workspaceGitService: this.workspaceGitService,
        createAppostleWorktree: (input, serviceOptions) =>
          this.createAppostleWorktree(input, serviceOptions),
        checkoutExistingBranch: (cwd, branch) => this.checkoutExistingBranch(cwd, branch),
        createBranchFromBase: (params) => this.createBranchFromBase(params),
        github: this.github,
      },
      config,
      gitOptions,
      legacyWorktreeName,
      attachments,
    );
  }

  private async handleLensesListRequest(
    msg: Extract<SessionInboundMessage, { type: "lenses_list_request" }>,
  ): Promise<void> {
    const cwd = expandTilde(msg.cwd);
    try {
      const lenses = await scanLenses(cwd);
      this.emit({
        type: "lenses_list_response",
        payload: { lenses, error: null, requestId: msg.requestId },
      });
    } catch (error) {
      this.sessionLogger.error({ err: error, cwd }, "Failed to scan lenses");
      this.emit({
        type: "lenses_list_response",
        payload: {
          lenses: [],
          error: (error as Error)?.message ?? String(error),
          requestId: msg.requestId,
        },
      });
    }
  }

  private async handleListProviderModelsRequest(
    msg: Extract<SessionInboundMessage, { type: "list_provider_models_request" }>,
  ): Promise<void> {
    const cwd = msg.cwd ? expandTilde(msg.cwd) : undefined;
    const fetchedAt = new Date().toISOString();
    const manager = this.providerSnapshotManager;

    if (!manager) {
      try {
        const models = await this.providerRegistry[msg.provider].fetchModels({ cwd });
        this.emit({
          type: "list_provider_models_response",
          payload: {
            provider: msg.provider,
            models,
            error: null,
            fetchedAt,
            requestId: msg.requestId,
          },
        });
      } catch (error) {
        this.sessionLogger.error(
          { err: error, provider: msg.provider },
          `Failed to list models for ${msg.provider}`,
        );
        this.emit({
          type: "list_provider_models_response",
          payload: {
            provider: msg.provider,
            error: (error as Error)?.message ?? String(error),
            fetchedAt,
            requestId: msg.requestId,
          },
        });
      }
      return;
    }

    const findEntry = () =>
      manager.getSnapshot(cwd).find((candidate) => candidate.provider === msg.provider);

    let entry = findEntry();
    if (!entry || entry.status === "loading") {
      // Awaits the in-flight warmup (deduped per-cwd) so old clients still get
      // a resolved answer rather than a loading placeholder.
      await manager.refresh({ cwd, providers: [msg.provider] });
      entry = findEntry();
    }

    if (!entry) {
      this.emit({
        type: "list_provider_models_response",
        payload: {
          provider: msg.provider,
          error: `Unknown provider: ${msg.provider}`,
          fetchedAt,
          requestId: msg.requestId,
        },
      });
      return;
    }

    if (entry.status === "ready") {
      this.emit({
        type: "list_provider_models_response",
        payload: {
          provider: msg.provider,
          models: entry.models ?? [],
          error: null,
          fetchedAt: entry.fetchedAt ?? fetchedAt,
          requestId: msg.requestId,
        },
      });
      return;
    }

    const errorMessage =
      entry.status === "error"
        ? (entry.error ?? `Failed to list models for ${msg.provider}`)
        : `Provider ${msg.provider} is not available`;

    this.emit({
      type: "list_provider_models_response",
      payload: {
        provider: msg.provider,
        error: errorMessage,
        fetchedAt,
        requestId: msg.requestId,
      },
    });
  }

  private async handleListProviderModesRequest(
    msg: Extract<SessionInboundMessage, { type: "list_provider_modes_request" }>,
  ): Promise<void> {
    const fetchedAt = new Date().toISOString();
    try {
      const modes = await this.providerRegistry[msg.provider].fetchModes({
        cwd: msg.cwd ? expandTilde(msg.cwd) : undefined,
      });
      this.emit({
        type: "list_provider_modes_response",
        payload: {
          provider: msg.provider,
          modes,
          error: null,
          fetchedAt,
          requestId: msg.requestId,
        },
      });
    } catch (error) {
      this.sessionLogger.error(
        { err: error, provider: msg.provider },
        `Failed to list modes for ${msg.provider}`,
      );
      this.emit({
        type: "list_provider_modes_response",
        payload: {
          provider: msg.provider,
          error: (error as Error)?.message ?? String(error),
          fetchedAt,
          requestId: msg.requestId,
        },
      });
    }
  }

  private buildDraftAgentSessionConfig(draftConfig: {
    provider: AgentProvider;
    cwd: string;
    modeId?: string;
    model?: string;
    thinkingOptionId?: string;
    featureValues?: Record<string, unknown>;
  }): AgentSessionConfig {
    return {
      provider: draftConfig.provider,
      cwd: expandTilde(draftConfig.cwd),
      ...(draftConfig.modeId ? { modeId: draftConfig.modeId } : {}),
      ...(draftConfig.model ? { model: draftConfig.model } : {}),
      ...(draftConfig.thinkingOptionId ? { thinkingOptionId: draftConfig.thinkingOptionId } : {}),
      ...(draftConfig.featureValues ? { featureValues: draftConfig.featureValues } : {}),
    };
  }

  private async handleListProviderFeaturesRequest(
    msg: Extract<SessionInboundMessage, { type: "list_provider_features_request" }>,
  ): Promise<void> {
    const fetchedAt = new Date().toISOString();
    try {
      const sessionConfig = this.buildDraftAgentSessionConfig(msg.draftConfig);
      const features = await this.agentManager.listDraftFeatures(sessionConfig);
      this.emit({
        type: "list_provider_features_response",
        payload: {
          provider: msg.draftConfig.provider,
          features,
          error: null,
          fetchedAt,
          requestId: msg.requestId,
        },
      });
    } catch (error) {
      this.sessionLogger.error(
        { err: error, provider: msg.draftConfig.provider, draftConfig: msg.draftConfig },
        `Failed to list features for ${msg.draftConfig.provider}`,
      );
      this.emit({
        type: "list_provider_features_response",
        payload: {
          provider: msg.draftConfig.provider,
          error: (error as Error)?.message ?? String(error),
          fetchedAt,
          requestId: msg.requestId,
        },
      });
    }
  }

  private async handleListAvailableProvidersRequest(
    msg: Extract<SessionInboundMessage, { type: "list_available_providers_request" }>,
  ): Promise<void> {
    const fetchedAt = new Date().toISOString();
    try {
      const providers = (await this.agentManager.listProviderAvailability()).filter((provider) =>
        this.isProviderVisibleToClient(provider.provider),
      );
      this.emit({
        type: "list_available_providers_response",
        payload: {
          providers,
          error: null,
          fetchedAt,
          requestId: msg.requestId,
        },
      });
    } catch (error) {
      this.sessionLogger.error({ err: error }, "Failed to list provider availability");
      this.emit({
        type: "list_available_providers_response",
        payload: {
          providers: [],
          error: (error as Error)?.message ?? String(error),
          fetchedAt,
          requestId: msg.requestId,
        },
      });
    }
  }

  private async handleGetProvidersSnapshotRequest(
    msg: Extract<SessionInboundMessage, { type: "get_providers_snapshot_request" }>,
  ): Promise<void> {
    // COMPAT(providersSnapshot): keep legacy provider-list RPCs alongside snapshot flow.
    const entries = this.providerSnapshotManager
      ? this.providerSnapshotManager
          .getSnapshot(msg.cwd ? expandTilde(msg.cwd) : undefined)
          .filter((entry) => this.isProviderVisibleToClient(entry.provider))
      : [];

    this.emit({
      type: "get_providers_snapshot_response",
      payload: {
        entries,
        generatedAt: new Date().toISOString(),
        requestId: msg.requestId,
      },
    });
  }

  private async handleRefreshProvidersSnapshotRequest(
    msg: Extract<SessionInboundMessage, { type: "refresh_providers_snapshot_request" }>,
  ): Promise<void> {
    await this.providerSnapshotManager?.refresh({
      cwd: msg.cwd ? expandTilde(msg.cwd) : undefined,
      providers: msg.providers,
    });
    this.emit({
      type: "refresh_providers_snapshot_response",
      payload: {
        acknowledged: true,
        requestId: msg.requestId,
      },
    });
  }

  private async handleProviderDiagnosticRequest(
    msg: Extract<SessionInboundMessage, { type: "provider_diagnostic_request" }>,
  ): Promise<void> {
    try {
      const client = this.providerRegistry[msg.provider].createClient(this.sessionLogger);
      const diagnostic = client.getDiagnostic
        ? (await client.getDiagnostic()).diagnostic
        : "No diagnostic available for this provider.";
      this.emit({
        type: "provider_diagnostic_response",
        payload: {
          provider: msg.provider,
          diagnostic,
          requestId: msg.requestId,
        },
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.sessionLogger.error(
        { err, provider: msg.provider },
        `Failed to get provider diagnostic for ${msg.provider}`,
      );
      this.emit({
        type: "rpc_error",
        payload: {
          requestId: msg.requestId,
          requestType: msg.type,
          error: `Failed to get provider diagnostic: ${err.message}`,
          code: "provider_diagnostic_failed",
        },
      });
    }
  }

  private assertSafeGitRef(ref: string, label: string): void {
    if (!/^[A-Za-z0-9._/-]+$/.test(ref)) {
      throw new Error(`Invalid ${label}: ${ref}`);
    }
    assertWorktreeSafeGitRef(ref, label);
  }

  private isPathWithinRoot(rootPath: string, candidatePath: string): boolean {
    const resolvedRoot = resolve(rootPath);
    const resolvedCandidate = resolve(candidatePath);
    if (resolvedCandidate === resolvedRoot) {
      return true;
    }
    return resolvedCandidate.startsWith(resolvedRoot + sep);
  }

  private async generateCommitMessage(cwd: string): Promise<string> {
    // Lightweight prompt — file names + change type only. We intentionally
    // skip the diff content (used to be up to 120 KB) because for a short
    // feature-level summary the file list already conveys intent, and
    // dropping the patch saves ~1-2 s of model time plus all the untracked-
    // file diff work on the server.
    const files = await listUncommittedFiles(cwd);
    const schema = z.object({
      message: z
        .string()
        .min(1)
        .max(100)
        .describe(
          "Short feature-level summary, lowercase, imperative mood, no trailing period, no filename references.",
        ),
    });
    const fileList =
      files.length > 0
        ? ["Files changed:", ...files.map((f) => `${f.changeType}\t${f.path}`)].join("\n")
        : "Files changed: (none detected)";
    const prompt = [
      "Summarize in 100 characters or less what features were worked on.",
      "Return JSON only with a single field 'message'. Lowercase, imperative",
      "mood, no trailing period, no filename references — describe intent.",
      "",
      fileList,
    ].join("\n");
    const suffix = " - Assisted by Appostle.";
    try {
      const result = await generateStructuredAgentResponseWithFallback({
        manager: this.agentManager,
        cwd,
        prompt,
        schema,
        schemaName: "CommitMessage",
        maxRetries: 2,
        providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
        ownerUserId: this.ownerUserId,
        agentConfigOverrides: {
          title: "Commit generator",
          internal: true,
        },
      });
      return `${result.message}${suffix}`;
    } catch (error) {
      if (
        error instanceof StructuredAgentResponseError ||
        error instanceof StructuredAgentFallbackError
      ) {
        return `update files${suffix}`;
      }
      throw error;
    }
  }

  private async generatePullRequestText(
    cwd: string,
    baseRef?: string,
  ): Promise<{
    title: string;
    body: string;
  }> {
    const diff = await getCheckoutDiff(
      cwd,
      {
        mode: "base",
        baseRef,
        includeStructured: true,
      },
      { appostleHome: this.appostleHome },
    );
    const schema = z.object({
      title: z.string().min(1).max(72),
      body: z.string().min(1),
    });
    const fileList =
      diff.structured && diff.structured.length > 0
        ? [
            "Files changed:",
            ...diff.structured.map((file) => {
              const changeType = file.isNew ? "A" : file.isDeleted ? "D" : "M";
              const status = file.status && file.status !== "ok" ? ` [${file.status}]` : "";
              return `${changeType}\t${file.path}\t(+${file.additions} -${file.deletions})${status}`;
            }),
          ].join("\n")
        : "Files changed: (unknown)";
    const maxPatchChars = 200_000;
    const patch =
      diff.diff.length > maxPatchChars
        ? `${diff.diff.slice(0, maxPatchChars)}\n\n... (diff truncated to ${maxPatchChars} chars)\n`
        : diff.diff;
    const prompt = [
      "Write a pull request title and body for the changes below.",
      "Return JSON only with fields 'title' and 'body'.",
      "",
      fileList,
      "",
      patch.length > 0 ? patch : "(No diff available)",
    ].join("\n");
    try {
      return await generateStructuredAgentResponseWithFallback({
        manager: this.agentManager,
        cwd,
        prompt,
        schema,
        schemaName: "PullRequest",
        maxRetries: 2,
        providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
        ownerUserId: this.ownerUserId,
        agentConfigOverrides: {
          title: "PR generator",
          internal: true,
        },
      });
    } catch (error) {
      if (
        error instanceof StructuredAgentResponseError ||
        error instanceof StructuredAgentFallbackError
      ) {
        return {
          title: "Update changes",
          body: "Automated PR generated by Appostle.",
        };
      }
      throw error;
    }
  }

  private async ensureCleanWorkingTree(cwd: string): Promise<void> {
    const dirty = await this.isWorkingTreeDirty(cwd);
    if (dirty) {
      throw new Error(
        "Working directory has uncommitted changes. Commit or stash before switching branches.",
      );
    }
  }

  private async isWorkingTreeDirty(cwd: string): Promise<boolean> {
    try {
      const { stdout } = await execAsync("git status --porcelain", {
        cwd,
        env: READ_ONLY_GIT_ENV,
      });
      return stdout.trim().length > 0;
    } catch (error) {
      throw new Error(`Unable to inspect git status for ${cwd}: ${(error as Error).message}`);
    }
  }

  private async checkoutExistingBranch(
    cwd: string,
    branch: string,
  ): Promise<CheckoutExistingBranchResult> {
    this.assertSafeGitRef(branch, "branch");
    const resolution = await resolveBranchCheckout(cwd, branch);
    if (resolution.kind === "not-found") {
      throw new Error(`Branch not found: ${branch}`);
    }
    await this.ensureCleanWorkingTree(cwd);
    return checkoutResolvedBranch({
      cwd,
      resolution,
    });
  }

  private async createBranchFromBase(params: {
    cwd: string;
    baseBranch: string;
    newBranchName: string;
  }): Promise<void> {
    const { cwd, baseBranch, newBranchName } = params;
    this.assertSafeGitRef(baseBranch, "base branch");
    this.assertSafeGitRef(newBranchName, "new branch");

    try {
      await execCommand("git", ["rev-parse", "--verify", baseBranch], { cwd });
    } catch (error) {
      throw new Error(`Base branch not found: ${baseBranch}`);
    }

    const exists = await this.doesLocalBranchExist(cwd, newBranchName);
    if (exists) {
      throw new Error(`Branch already exists: ${newBranchName}`);
    }

    await this.ensureCleanWorkingTree(cwd);
    await execCommand("git", ["checkout", "-b", newBranchName, baseBranch], {
      cwd,
    });
  }

  private async doesLocalBranchExist(cwd: string, branch: string): Promise<boolean> {
    this.assertSafeGitRef(branch, "branch");
    try {
      await execCommand("git", ["show-ref", "--verify", "--quiet", `refs/heads/${branch}`], {
        cwd,
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Handle set agent mode request
   */
  private async handleSetAgentModeRequest(
    agentId: string,
    modeId: string,
    requestId: string,
  ): Promise<void> {
    this.sessionLogger.info({ agentId, modeId, requestId }, "session: set_agent_mode_request");

    try {
      await this.agentManager.setAgentMode(agentId, modeId);
      this.sessionLogger.info(
        { agentId, modeId, requestId },
        "session: set_agent_mode_request success",
      );
      this.emit({
        type: "set_agent_mode_response",
        payload: { requestId, agentId, accepted: true, error: null },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, agentId, modeId, requestId },
        "session: set_agent_mode_request error",
      );
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to set agent mode: ${error.message}`,
        },
      });
      this.emit({
        type: "set_agent_mode_response",
        payload: {
          requestId,
          agentId,
          accepted: false,
          error: error?.message ? String(error.message) : "Failed to set agent mode",
        },
      });
    }
  }

  private async handleSetAgentModelRequest(
    agentId: string,
    modelId: string | null,
    requestId: string,
  ): Promise<void> {
    this.sessionLogger.info({ agentId, modelId, requestId }, "session: set_agent_model_request");

    try {
      await this.agentManager.setAgentModel(agentId, modelId);
      this.sessionLogger.info(
        { agentId, modelId, requestId },
        "session: set_agent_model_request success",
      );
      const displayName = modelId ?? "default";
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "system",
          content: `Model switched to ${displayName}`,
        },
      });
      this.emit({
        type: "set_agent_model_response",
        payload: { requestId, agentId, accepted: true, error: null },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, agentId, modelId, requestId },
        "session: set_agent_model_request error",
      );
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to set agent model: ${error.message}`,
        },
      });
      this.emit({
        type: "set_agent_model_response",
        payload: {
          requestId,
          agentId,
          accepted: false,
          error: error?.message ? String(error.message) : "Failed to set agent model",
        },
      });
    }
  }

  private async handleSetAgentFeatureRequest(
    agentId: string,
    featureId: string,
    value: unknown,
    requestId: string,
  ): Promise<void> {
    this.sessionLogger.info(
      { agentId, featureId, value, requestId },
      "session: set_agent_feature_request",
    );

    try {
      await this.agentManager.setAgentFeature(agentId, featureId, value);
      this.sessionLogger.info(
        { agentId, featureId, value, requestId },
        "session: set_agent_feature_request success",
      );
      this.emit({
        type: "set_agent_feature_response",
        payload: { requestId, agentId, accepted: true, error: null },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, agentId, featureId, value, requestId },
        "session: set_agent_feature_request error",
      );
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to set agent feature: ${error.message}`,
        },
      });
      this.emit({
        type: "set_agent_feature_response",
        payload: {
          requestId,
          agentId,
          accepted: false,
          error: error?.message ? String(error.message) : "Failed to set agent feature",
        },
      });
    }
  }

  private async handleListAgentMcpServersRequest(
    agentId: string,
    requestId: string,
  ): Promise<void> {
    try {
      const servers = await this.agentManager.listAgentMcpServers(agentId);
      this.emit({
        type: "list_agent_mcp_servers_response",
        payload: { requestId, agentId, servers, error: null },
      });
    } catch (error: any) {
      this.emit({
        type: "list_agent_mcp_servers_response",
        payload: {
          requestId,
          agentId,
          servers: null,
          error: error?.message ?? "Failed to list MCP servers",
        },
      });
    }
  }

  private async handleSetAgentMcpServerRequest(
    agentId: string,
    serverName: string,
    enabled: boolean,
    requestId: string,
  ): Promise<void> {
    try {
      await this.agentManager.setAgentMcpServer(agentId, serverName, enabled);
      this.emit({
        type: "set_agent_mcp_server_response",
        payload: { requestId, agentId, accepted: true, error: null },
      });
    } catch (error: any) {
      this.emit({
        type: "set_agent_mcp_server_response",
        payload: {
          requestId,
          agentId,
          accepted: false,
          error: error?.message ?? "Failed to set MCP server",
        },
      });
    }
  }

  private async handleSetAgentThinkingRequest(
    agentId: string,
    thinkingOptionId: string | null,
    requestId: string,
  ): Promise<void> {
    this.sessionLogger.info(
      { agentId, thinkingOptionId, requestId },
      "session: set_agent_thinking_request",
    );

    try {
      await this.agentManager.setAgentThinkingOption(agentId, thinkingOptionId);
      this.sessionLogger.info(
        { agentId, thinkingOptionId, requestId },
        "session: set_agent_thinking_request success",
      );
      this.emit({
        type: "set_agent_thinking_response",
        payload: { requestId, agentId, accepted: true, error: null },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, agentId, thinkingOptionId, requestId },
        "session: set_agent_thinking_request error",
      );
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to set agent thinking option: ${error.message}`,
        },
      });
      this.emit({
        type: "set_agent_thinking_response",
        payload: {
          requestId,
          agentId,
          accepted: false,
          error: error?.message ? String(error.message) : "Failed to set agent thinking option",
        },
      });
    }
  }

  /**
   * Handle clearing agent attention flag
   */
  private async handleClearAgentAttention(
    agentId: string | string[],
    requestId?: string,
  ): Promise<void> {
    const agentIds = Array.isArray(agentId) ? agentId : [agentId];

    try {
      await Promise.all(agentIds.map((id) => this.agentManager.clearAgentAttention(id)));
      if (requestId) {
        const agents = (
          await Promise.all(
            agentIds.map(async (id) => {
              const agent = this.agentManager.getAgentSnapshot(id);
              return agent ? this.buildAgentPayload(agent) : null;
            }),
          )
        ).filter((payload): payload is NonNullable<typeof payload> => payload !== null);
        this.emit({
          type: "clear_agent_attention_response",
          payload: {
            requestId,
            agentId,
            agents,
          },
        });
      }
    } catch (error: any) {
      this.sessionLogger.error({ err: error, agentIds }, "Failed to clear agent attention");
      // Don't throw - this is not critical
    }
  }

  /**
   * Handle client heartbeat for activity tracking
   */
  private handleClientHeartbeat(msg: {
    deviceType: "web" | "mobile";
    focusedAgentId: string | null;
    lastActivityAt: string;
    appVisible: boolean;
    appVisibilityChangedAt?: string;
  }): void {
    const appVisibilityChangedAt = msg.appVisibilityChangedAt
      ? new Date(msg.appVisibilityChangedAt)
      : new Date(msg.lastActivityAt);
    this.clientActivity = {
      deviceType: msg.deviceType,
      focusedAgentId: msg.focusedAgentId,
      lastActivityAt: new Date(msg.lastActivityAt),
      appVisible: msg.appVisible,
      appVisibilityChangedAt,
    };
  }

  /**
   * Handle push token registration. Now upserts an entry that carries the
   * client's `deviceId` + per-event preferences alongside the token.
   */
  private handleRegisterPushToken(args: {
    token: string;
    deviceId?: string;
    preferences?: import("../shared/notification-events.js").NotificationPreferences;
  }): void {
    // Stamp the authoritative `ownerUserId` (pubkey-resolved) onto the entry
    // so per-agent fan-out can scope pushes to the user that registered the
    // device. Null on single-tenant daemons — entry is unscoped and behaves
    // like the legacy global fan-out.
    this.pushTokenStore.upsertToken({ ...args, userId: this.ownerUserId });
    this.sessionLogger.info(
      { deviceId: args.deviceId, ownerUserId: this.ownerUserId },
      "Registered push token",
    );
  }

  private handleUnregisterPushToken(token: string): void {
    this.pushTokenStore.removeToken(token);
    this.sessionLogger.info("Unregistered push token");
  }

  private handleRegisterWebPushSubscription(args: {
    subscription: { endpoint: string; keys: { p256dh: string; auth: string } };
    deviceId?: string;
    preferences?: import("../shared/notification-events.js").NotificationPreferences;
  }): void {
    // Stamp the authoritative `ownerUserId` (pubkey-resolved) onto the entry
    // so per-agent fan-out can scope pushes to the user that registered the
    // browser. Null on single-tenant daemons — entry is unscoped and behaves
    // like the legacy global fan-out.
    this.webPushStore.upsert({ ...args, userId: this.ownerUserId });
    this.sessionLogger.info(
      {
        endpoint: args.subscription.endpoint,
        deviceId: args.deviceId,
        ownerUserId: this.ownerUserId,
      },
      "Registered web push subscription",
    );
    void this.webPushService.validate(args.subscription);
  }

  private handleUnregisterWebPushSubscription(endpoint: string): void {
    this.webPushStore.remove(endpoint);
    this.sessionLogger.info({ endpoint }, "Unregistered web push subscription");
  }

  private handleUpdateNotificationPreferences(msg: {
    deviceId: string;
    token?: string;
    endpoint?: string;
    preferences: import("../shared/notification-events.js").NotificationPreferences;
  }): void {
    let touched = 0;
    if (this.pushTokenStore.updatePreferences(msg)) touched += 1;
    if (this.webPushStore.updatePreferences(msg)) touched += 1;
    this.sessionLogger.info(
      { deviceId: msg.deviceId, touched },
      "Updated notification preferences",
    );
  }

  private async handleSendTestWebPush(deviceId?: string): Promise<void> {
    const subs = deviceId
      ? this.webPushStore.getSubsForDevice(deviceId)
      : this.webPushStore.getAll();
    this.sessionLogger.info(
      { subCount: subs.length, deviceId },
      "Sending test web push (bypasses suppression policy)",
    );
    await this.webPushService.sendPush(subs, {
      title: "Appostle test",
      body: "Push from your daemon — looks good!",
      data: { type: "test_user" },
    });
  }

  private async handleSendTestPush(deviceId?: string): Promise<void> {
    const tokens = deviceId
      ? this.pushTokenStore.getTokensForDevice(deviceId)
      : this.pushTokenStore.getAllTokens();
    this.sessionLogger.info(
      { tokenCount: tokens.length, deviceId },
      "Sending test native push (bypasses suppression policy)",
    );
    await this.pushService.sendPush(tokens, {
      title: "Appostle test",
      body: "Push from your daemon — looks good!",
      data: { type: "test_user" },
    });
  }

  private handleGetVapidPublicKeyRequest(requestId: string): void {
    this.emit({
      type: "get_vapid_public_key_response",
      requestId,
      publicKey: this.webPushStore.getPublicKey(),
    });
  }

  /**
   * Handle list commands request for an agent
   */
  private async handleListCommandsRequest(
    msg: Extract<SessionInboundMessage, { type: "list_commands_request" }>,
  ): Promise<void> {
    const { agentId, requestId, draftConfig } = msg;
    this.sessionLogger.debug(
      { agentId, draftConfig },
      `Handling list commands request for agent ${agentId}`,
    );

    try {
      const agents = this.agentManager.listAgents();
      const agent = agents.find((a) => a.id === agentId);

      if (agent?.session?.listCommands) {
        const commands = await agent.session.listCommands();
        this.emit({
          type: "list_commands_response",
          payload: {
            agentId,
            commands,
            error: null,
            requestId,
          },
        });
        return;
      }

      if (!agent && draftConfig) {
        const sessionConfig: AgentSessionConfig = {
          provider: draftConfig.provider,
          cwd: expandTilde(draftConfig.cwd),
          ...(draftConfig.modeId ? { modeId: draftConfig.modeId } : {}),
          ...(draftConfig.model ? { model: draftConfig.model } : {}),
          ...(draftConfig.thinkingOptionId
            ? { thinkingOptionId: draftConfig.thinkingOptionId }
            : {}),
        };

        const commands = await this.agentManager.listDraftCommands(sessionConfig);
        this.emit({
          type: "list_commands_response",
          payload: {
            agentId,
            commands,
            error: null,
            requestId,
          },
        });
        return;
      }

      this.emit({
        type: "list_commands_response",
        payload: {
          agentId,
          commands: [],
          error: agent ? `Agent does not support listing commands` : `Agent not found: ${agentId}`,
          requestId,
        },
      });
    } catch (error: any) {
      this.sessionLogger.error({ err: error, agentId, draftConfig }, "Failed to list commands");
      this.emit({
        type: "list_commands_response",
        payload: {
          agentId,
          commands: [],
          error: error.message,
          requestId,
        },
      });
    }
  }

  /**
   * Handle list-skills request — scans claude/codex skill directories.
   */
  private async handleListSkillsRequest(
    msg: Extract<SessionInboundMessage, { type: "list_skills_request" }>,
  ): Promise<void> {
    const { provider, scope, workspaceRoot, requestId } = msg;
    try {
      const entries = await listSkills({
        provider,
        scope,
        ...(workspaceRoot ? { workspaceRoot: expandTilde(workspaceRoot) } : {}),
      });
      this.emit({
        type: "list_skills_response",
        payload: { entries, error: null, requestId },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, provider, scope }, "Failed to list skills");
      this.emit({
        type: "list_skills_response",
        payload: { entries: [], error: message, requestId },
      });
    }
  }

  /**
   * Handle create-skill request — mkdir + writes a starter SKILL.md (or
   * `<name>.md` for codex-prompts). Refuses if the target already exists.
   */
  private async handleCreateSkillRequest(
    msg: Extract<SessionInboundMessage, { type: "create_skill_request" }>,
  ): Promise<void> {
    const { provider, scope, workspaceRoot, name, requestId } = msg;
    try {
      const result = await createSkill({
        provider,
        scope,
        name,
        ...(workspaceRoot ? { workspaceRoot: expandTilde(workspaceRoot) } : {}),
      });
      this.emit({
        type: "create_skill_response",
        payload: { path: result.path, error: null, requestId },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, provider, scope, name }, "Failed to create skill");
      this.emit({
        type: "create_skill_response",
        payload: { path: "", error: message, requestId },
      });
    }
  }

  /**
   * Handle write-skill-frontmatter request — replaces ONLY the frontmatter
   * block of an existing skill file (body is preserved verbatim). The path
   * is gated to absolute locations inside an allowlisted skill root, so this
   * RPC can write to `~/.claude/skills/` even though the generic `writeFile`
   * RPC is workspace-cwd-scoped.
   */
  private async handleWriteSkillFrontmatterRequest(
    msg: Extract<SessionInboundMessage, { type: "write_skill_frontmatter_request" }>,
  ): Promise<void> {
    const { path: skillPath, workspaceRoot, frontmatter, requestId } = msg;
    try {
      await writeSkillFrontmatter(
        { path: skillPath, frontmatter },
        workspaceRoot ? expandTilde(workspaceRoot) : undefined,
      );
      this.emit({
        type: "write_skill_frontmatter_response",
        payload: { path: skillPath, error: null, requestId },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, path: skillPath },
        "Failed to write skill frontmatter",
      );
      this.emit({
        type: "write_skill_frontmatter_response",
        payload: { path: skillPath, error: message, requestId },
      });
    }
  }

  /**
   * Handle agent permission response from user
   */
  private async handleAgentPermissionResponse(
    agentId: string,
    requestId: string,
    response: AgentPermissionResponse,
  ): Promise<void> {
    this.sessionLogger.debug(
      { agentId, requestId },
      `Handling permission response for agent ${agentId}, request ${requestId}`,
    );

    try {
      const result = await this.agentManager.respondToPermission(agentId, requestId, response);
      this.sessionLogger.debug({ agentId }, `Permission response forwarded to agent ${agentId}`);

      if (result?.followUpPrompt) {
        this.sessionLogger.debug(
          { agentId },
          "Permission response requires follow-up turn, starting agent stream",
        );
        this.startAgentStream(agentId, result.followUpPrompt);
      }
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, agentId, requestId },
        "Failed to respond to permission",
      );
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Failed to respond to permission: ${error.message}`,
        },
      });
      throw error;
    }
  }

  private async handleCheckoutStatusRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_status_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;
    const resolvedCwd = expandTilde(cwd);

    try {
      // Route through the workspace-git-service so we share the cached
      // snapshot instead of spawning a fresh getCheckoutStatus (7-8 git
      // subprocesses) per request. The service dedupes concurrent loads
      // via its refreshPromise, so N parallel checkout_status_requests on
      // the same cwd collapse into a single underlying git run — which
      // was the root cause of the 39s tail latencies under page-load
      // concurrency.
      const snapshot = await this.workspaceGitService.getSnapshot(resolvedCwd);
      const git = snapshot.git;

      if (!git.isGit) {
        this.emit({
          type: "checkout_status_response",
          payload: {
            cwd,
            isGit: false,
            repoRoot: null,
            currentBranch: null,
            isDirty: null,
            baseRef: null,
            aheadBehind: null,
            aheadOfOrigin: null,
            behindOfOrigin: null,
            hasRemote: false,
            remoteUrl: null,
            isAppostleOwnedWorktree: false,
            error: null,
            requestId,
          },
        });
        return;
      }

      const hasRemote = git.remoteUrl !== null;

      if (git.isAppostleOwnedWorktree && git.mainRepoRoot && git.repoRoot && git.baseRef) {
        this.emit({
          type: "checkout_status_response",
          payload: {
            cwd,
            isGit: true,
            repoRoot: git.repoRoot,
            mainRepoRoot: git.mainRepoRoot,
            currentBranch: git.currentBranch,
            isDirty: git.isDirty ?? false,
            baseRef: git.baseRef,
            aheadBehind: git.aheadBehind,
            aheadOfOrigin: git.aheadOfOrigin,
            behindOfOrigin: git.behindOfOrigin,
            hasRemote,
            remoteUrl: git.remoteUrl,
            isAppostleOwnedWorktree: true,
            error: null,
            requestId,
          },
        });
        return;
      }

      this.emit({
        type: "checkout_status_response",
        payload: {
          cwd,
          isGit: true,
          repoRoot: git.repoRoot ?? "",
          currentBranch: git.currentBranch,
          isDirty: git.isDirty ?? false,
          baseRef: git.baseRef,
          aheadBehind: git.aheadBehind,
          aheadOfOrigin: git.aheadOfOrigin,
          behindOfOrigin: git.behindOfOrigin,
          hasRemote,
          remoteUrl: git.remoteUrl,
          isAppostleOwnedWorktree: false,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_status_response",
        payload: {
          cwd,
          isGit: false,
          repoRoot: null,
          currentBranch: null,
          isDirty: null,
          baseRef: null,
          aheadBehind: null,
          aheadOfOrigin: null,
          behindOfOrigin: null,
          hasRemote: false,
          remoteUrl: null,
          isAppostleOwnedWorktree: false,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleValidateBranchRequest(
    msg: Extract<SessionInboundMessage, { type: "validate_branch_request" }>,
  ): Promise<void> {
    const { cwd, branchName, requestId } = msg;

    try {
      const resolvedCwd = expandTilde(cwd);
      this.assertSafeGitRef(branchName, "branch");

      const resolution = await resolveBranchCheckout(resolvedCwd, branchName);
      switch (resolution.kind) {
        case "local":
          this.emit({
            type: "validate_branch_response",
            payload: {
              exists: true,
              resolvedRef: resolution.name,
              isRemote: false,
              error: null,
              requestId,
            },
          });
          return;
        case "remote-only":
          this.emit({
            type: "validate_branch_response",
            payload: {
              exists: true,
              resolvedRef: resolution.remoteRef,
              isRemote: true,
              error: null,
              requestId,
            },
          });
          return;
        case "not-found":
          this.emit({
            type: "validate_branch_response",
            payload: {
              exists: false,
              resolvedRef: null,
              isRemote: false,
              error: null,
              requestId,
            },
          });
          return;
        default: {
          const exhaustiveCheck: never = resolution;
          throw new Error(`Unhandled branch resolution: ${exhaustiveCheck}`);
        }
      }
    } catch (error) {
      this.emit({
        type: "validate_branch_response",
        payload: {
          exists: false,
          resolvedRef: null,
          isRemote: false,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleBranchSuggestionsRequest(
    msg: Extract<SessionInboundMessage, { type: "branch_suggestions_request" }>,
  ): Promise<void> {
    const { cwd, query, limit, requestId } = msg;

    try {
      const resolvedCwd = expandTilde(cwd);
      // Pull baseRef from the cached workspace snapshot so listBranchSuggestions
      // can mark which branches are already merged. If the snapshot isn't a git
      // checkout we just skip the merge check (UI hides delete affordance).
      const snapshot = await this.workspaceGitService.getSnapshot(resolvedCwd);
      const baseRef = snapshot.git.isGit ? (snapshot.git.baseRef ?? null) : null;
      const branchDetails = await listBranchSuggestions(resolvedCwd, {
        query,
        limit,
        baseRef,
      });
      this.emit({
        type: "branch_suggestions_response",
        payload: {
          branches: branchDetails.map((branch) => branch.name),
          branchDetails,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "branch_suggestions_response",
        payload: {
          branches: [],
          branchDetails: [],
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleGitHubSearchRequest(
    msg: Extract<SessionInboundMessage, { type: "github_search_request" }>,
  ): Promise<void> {
    const { cwd, query, limit, kinds, requestId } = msg;

    try {
      const resolvedCwd = expandTilde(cwd);
      const result = await searchGitHubIssuesAndPrs(resolvedCwd, query, limit, this.github, {
        kinds,
      });
      this.emit({
        type: "github_search_response",
        payload: {
          items: result.items,
          githubFeaturesEnabled: result.githubFeaturesEnabled,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "github_search_response",
        payload: {
          items: [],
          githubFeaturesEnabled: true,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleGitLabSearchRequest(
    msg: Extract<SessionInboundMessage, { type: "gitlab_search_request" }>,
  ): Promise<void> {
    const { cwd, query, limit, kinds, requestId } = msg;

    try {
      // Lazy refresh: if the OAuth token is within 60s of expiry, refresh it
      // before the shell-out so `glab` reads the new token from its config.
      // No-op when the user signed in via `glab auth login` (no stored creds).
      await this.oauth.refreshIfStale("gitlab");
      const resolvedCwd = expandTilde(cwd);
      const result = await searchGitLabIssuesAndMrs(resolvedCwd, query, limit, this.gitlab, {
        kinds,
      });
      this.emit({
        type: "gitlab_search_response",
        payload: {
          items: result.items,
          gitlabFeaturesEnabled: result.gitlabFeaturesEnabled,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "gitlab_search_response",
        payload: {
          items: [],
          gitlabFeaturesEnabled: true,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleCreateGitHubIssueRequest(
    msg: Extract<SessionInboundMessage, { type: "create_github_issue_request" }>,
  ): Promise<void> {
    const { cwd, title, body, labels, requestId } = msg;
    try {
      const resolvedCwd = expandTilde(cwd);
      const result = await createGitHubIssue(resolvedCwd, { title, body, labels }, this.github);
      this.emit({
        type: "create_github_issue_response",
        payload: {
          item: result.item,
          error: result.githubFeaturesEnabled
            ? null
            : "GitHub CLI (gh) is not installed or not authenticated.",
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "create_github_issue_response",
        payload: {
          item: null,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleOAuthInitiateRequest(
    msg: Extract<SessionInboundMessage, { type: "oauth_initiate_request" }>,
  ): Promise<void> {
    const { provider, requestId } = msg;
    try {
      const result = await this.oauth.initiate(provider);
      this.emit({
        type: "oauth_initiate_response",
        payload: { authorizeUrl: result.authorizeUrl, error: null, requestId },
      });
    } catch (error) {
      this.emit({
        type: "oauth_initiate_response",
        payload: {
          authorizeUrl: null,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleOAuthCompleteRequest(
    msg: Extract<SessionInboundMessage, { type: "oauth_complete_request" }>,
  ): Promise<void> {
    const { provider, code, state, requestId } = msg;
    try {
      const result = await this.oauth.complete(provider, code, state);
      // Token just changed — invalidate the gitlab service's auth-status cache
      // so the next searchGitLab call re-checks. We don't have a per-cwd
      // anchor here so use a representative cwd; the cache is keyed loosely.
      this.gitlab.invalidate({ cwd: "*" });
      this.emit({
        type: "oauth_complete_response",
        payload: { username: result.username, error: null, requestId },
      });
    } catch (error) {
      this.emit({
        type: "oauth_complete_response",
        payload: {
          username: null,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleOAuthStatusRequest(
    msg: Extract<SessionInboundMessage, { type: "oauth_status_request" }>,
  ): Promise<void> {
    const { provider, requestId } = msg;
    try {
      const result = await this.oauth.status(provider);
      this.emit({
        type: "oauth_status_response",
        payload: {
          signedIn: result.signedIn,
          username: result.username,
          expiresAt: result.expiresAt,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "oauth_status_response",
        payload: {
          signedIn: false,
          username: null,
          expiresAt: null,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleOAuthSignOutRequest(
    msg: Extract<SessionInboundMessage, { type: "oauth_sign_out_request" }>,
  ): Promise<void> {
    const { provider, requestId } = msg;
    try {
      await this.oauth.signOut(provider);
      this.gitlab.invalidate({ cwd: "*" });
      this.emit({
        type: "oauth_sign_out_response",
        payload: { error: null, requestId },
      });
    } catch (error) {
      this.emit({
        type: "oauth_sign_out_response",
        payload: {
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleCreateGitLabIssueRequest(
    msg: Extract<SessionInboundMessage, { type: "create_gitlab_issue_request" }>,
  ): Promise<void> {
    const { cwd, title, body, labels, requestId } = msg;
    try {
      await this.oauth.refreshIfStale("gitlab");
      const resolvedCwd = expandTilde(cwd);
      const result = await createGitLabIssue(resolvedCwd, { title, body, labels }, this.gitlab);
      this.emit({
        type: "create_gitlab_issue_response",
        payload: {
          item: result.item,
          error: result.gitlabFeaturesEnabled
            ? null
            : "GitLab CLI (glab) is not installed or not authenticated.",
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "create_gitlab_issue_response",
        payload: {
          item: null,
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleDirectorySuggestionsRequest(msg: DirectorySuggestionsRequest): Promise<void> {
    const { query, limit, requestId, cwd, includeFiles, includeDirectories } = msg;

    try {
      const workspaceCwd = cwd?.trim();
      const entries = workspaceCwd
        ? await searchWorkspaceEntries({
            cwd: expandTilde(workspaceCwd),
            query,
            limit,
            includeFiles,
            includeDirectories,
          })
        : (
            await searchHomeDirectories({
              homeDir: process.env.HOME ?? homedir(),
              query,
              limit,
            })
          ).map((path) => ({ path, kind: "directory" as const }));
      const directories = entries
        .filter((entry) => entry.kind === "directory")
        .map((entry) => entry.path);
      this.emit({
        type: "directory_suggestions_response",
        payload: {
          directories,
          entries,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "directory_suggestions_response",
        payload: {
          directories: [],
          entries: [],
          error: error instanceof Error ? error.message : String(error),
          requestId,
        },
      });
    }
  }

  private async handleListFilesystemRootsRequest(msg: ListFilesystemRootsRequest): Promise<void> {
    const { requestId } = msg;
    try {
      const roots = await listFilesystemRoots();
      this.emit({
        type: "list_filesystem_roots_response",
        payload: { requestId, roots, error: null },
      });
    } catch (error) {
      this.emit({
        type: "list_filesystem_roots_response",
        payload: {
          requestId,
          roots: [],
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  private async handleListDirectoryRequest(msg: ListDirectoryRequest): Promise<void> {
    const { requestId, path: rawPath, includeFiles, includeHidden, limit } = msg;
    try {
      const result = await listDirectoryContents({
        path: rawPath,
        includeFiles,
        includeHidden,
        limit,
      });
      this.emit({
        type: "list_directory_response",
        payload: {
          requestId,
          path: result.path,
          parent: result.parent,
          entries: result.entries,
          error: null,
        },
      });
    } catch (error) {
      this.emit({
        type: "list_directory_response",
        payload: {
          requestId,
          path: rawPath,
          parent: null,
          entries: [],
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  private closeWorkspaceGitWatchTarget(target: WorkspaceGitWatchTarget): void {
    if (target.debounceTimer) {
      clearTimeout(target.debounceTimer);
      target.debounceTimer = null;
    }
    for (const watcher of target.watchers) {
      try {
        watcher.close();
      } catch {
        // Ignore watcher close errors
      }
    }
    target.watchers.length = 0;
  }

  private async removeWorkspaceGitWatchTarget(cwd: string): Promise<void> {
    const normalizedCwd = normalizePersistedWorkspaceId(cwd);
    const target = this.workspaceGitWatchTargets.get(normalizedCwd);
    if (target) {
      this.closeWorkspaceGitWatchTarget(target);
      this.workspaceGitWatchTargets.delete(normalizedCwd);
    }
  }

  private removeWorkspaceGitSubscription(cwd: string): void {
    const normalizedCwd = normalizePersistedWorkspaceId(cwd);
    const target = this.workspaceGitWatchTargets.get(normalizedCwd);
    if (target) {
      const unsubscribeFetch = this.workspaceGitFetchSubscriptions.get(normalizedCwd);
      unsubscribeFetch?.();
      this.workspaceGitFetchSubscriptions.delete(normalizedCwd);
      this.closeWorkspaceGitWatchTarget(target);
      this.workspaceGitWatchTargets.delete(normalizedCwd);
    }
    this.workspaceGitSubscriptions.get(normalizedCwd)?.();
    this.workspaceGitSubscriptions.delete(normalizedCwd);
  }

  private workspaceGitDescriptorFingerprint(workspace: WorkspaceDescriptorPayload | null): string {
    if (!workspace) {
      return WORKSPACE_GIT_WATCH_REMOVED_FINGERPRINT;
    }
    return JSON.stringify([
      workspace.name,
      workspace.branchDiffStat
        ? [workspace.branchDiffStat.additions, workspace.branchDiffStat.deletions]
        : null,
      workspace.uncommittedDiffStat
        ? [workspace.uncommittedDiffStat.additions, workspace.uncommittedDiffStat.deletions]
        : null,
    ]);
  }

  private shouldSkipWorkspaceGitWatchUpdate(
    workspaceId: string,
    workspace: WorkspaceDescriptorPayload | null,
  ): boolean {
    const target = this.workspaceGitWatchTargets.get(workspaceId);
    if (!target) {
      return false;
    }
    const nextFingerprint = this.workspaceGitDescriptorFingerprint(workspace);
    if (target.latestFingerprint === nextFingerprint) {
      return true;
    }
    target.latestFingerprint = nextFingerprint;
    return false;
  }

  private rememberWorkspaceGitWatchFingerprint(
    workspaceId: string,
    workspace: WorkspaceDescriptorPayload | null,
  ): void {
    const target = this.workspaceGitWatchTargets.get(workspaceId);
    if (!target) {
      return;
    }
    target.latestFingerprint = this.workspaceGitDescriptorFingerprint(workspace);
    target.lastBranchName = workspace?.name ?? null;
  }

  private async primeWorkspaceGitWatchFingerprints(
    workspaces: Iterable<WorkspaceDescriptorPayload>,
  ): Promise<void> {
    for (const workspace of workspaces) {
      const persistedWorkspace = await this.workspaceRegistry.get(workspace.id);
      if (!persistedWorkspace) {
        continue;
      }
      await this.syncWorkspaceGitWatchTarget(persistedWorkspace.cwd, {
        isGit: workspace.projectKind === "git",
      });
      this.rememberWorkspaceGitWatchFingerprint(persistedWorkspace.cwd, workspace);
    }
  }

  private async syncWorkspaceGitWatchTarget(
    cwd: string,
    options: { isGit: boolean },
  ): Promise<void> {
    const normalizedCwd = normalizePersistedWorkspaceId(cwd);
    if (!options.isGit) {
      this.removeWorkspaceGitSubscription(normalizedCwd);
      return;
    }

    if (this.workspaceGitSubscriptions.has(normalizedCwd)) {
      return;
    }

    const subscription = await this.workspaceGitService.subscribe({ cwd: normalizedCwd }, () => {
      void this.emitWorkspaceUpdateForCwd(normalizedCwd);
    });
    this.workspaceGitSubscriptions.set(normalizedCwd, subscription.unsubscribe);
  }

  private async handleSubscribeCheckoutDiffRequest(
    msg: SubscribeCheckoutDiffRequest,
  ): Promise<void> {
    const cwd = expandTilde(msg.cwd);
    this.checkoutDiffSubscriptions.get(msg.subscriptionId)?.();
    this.checkoutDiffSubscriptions.delete(msg.subscriptionId);
    const subscription = await this.checkoutDiffManager.subscribe(
      { cwd, compare: msg.compare },
      (snapshot) => {
        this.emit({
          type: "checkout_diff_update",
          payload: {
            subscriptionId: msg.subscriptionId,
            ...snapshot,
          },
        });
      },
    );
    this.checkoutDiffSubscriptions.set(msg.subscriptionId, subscription.unsubscribe);

    this.emit({
      type: "subscribe_checkout_diff_response",
      payload: {
        subscriptionId: msg.subscriptionId,
        ...subscription.initial,
        requestId: msg.requestId,
      },
    });
  }

  private handleUnsubscribeCheckoutDiffRequest(msg: UnsubscribeCheckoutDiffRequest): void {
    this.checkoutDiffSubscriptions.get(msg.subscriptionId)?.();
    this.checkoutDiffSubscriptions.delete(msg.subscriptionId);
  }

  private async handleCheckoutSwitchBranchRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_switch_branch_request" }>,
  ): Promise<void> {
    const { cwd, branch, requestId } = msg;

    try {
      const checkoutResult = await this.checkoutExistingBranch(cwd, branch);
      this.github.invalidate({ cwd });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);

      // Push a workspace_update immediately so the sidebar/header reflect
      // the new branch name without waiting for the background git watcher.
      await this.emitWorkspaceUpdateForCwd(cwd);

      this.emit({
        type: "checkout_switch_branch_response",
        payload: {
          cwd,
          success: true,
          branch,
          source: checkoutResult.source,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_switch_branch_response",
        payload: {
          cwd,
          success: false,
          branch,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutCreateBranchRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_create_branch_request" }>,
  ): Promise<void> {
    const { cwd, newBranchName, baseBranch, requestId } = msg;

    try {
      await this.createBranchFromBase({
        cwd,
        baseBranch: baseBranch ?? "HEAD",
        newBranchName,
      });
      this.github.invalidate({ cwd });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);
      await this.emitWorkspaceUpdateForCwd(cwd);

      this.emit({
        type: "checkout_create_branch_response",
        payload: {
          cwd,
          success: true,
          branch: newBranchName,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_create_branch_response",
        payload: {
          cwd,
          success: false,
          branch: newBranchName,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Stash handlers
  // ---------------------------------------------------------------------------

  private static readonly APPOSTLE_STASH_PREFIX = "appostle-auto-stash:";

  private async handleStashSaveRequest(
    msg: Extract<SessionInboundMessage, { type: "stash_save_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;
    try {
      const branchLabel = msg.branch?.trim() ?? "";
      const message = branchLabel
        ? `${Session.APPOSTLE_STASH_PREFIX} ${branchLabel}`
        : `${Session.APPOSTLE_STASH_PREFIX} unnamed`;
      await execCommand("git", ["stash", "push", "--include-untracked", "-m", message], { cwd });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);
      this.emit({
        type: "stash_save_response",
        payload: { cwd, success: true, error: null, requestId },
      });
    } catch (error) {
      this.emit({
        type: "stash_save_response",
        payload: { cwd, success: false, error: toCheckoutError(error), requestId },
      });
    }
  }

  private async handleStashPopRequest(
    msg: Extract<SessionInboundMessage, { type: "stash_pop_request" }>,
  ): Promise<void> {
    const { cwd, stashIndex, requestId } = msg;
    try {
      await execCommand("git", ["stash", "pop", `stash@{${stashIndex}}`], { cwd });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);
      this.emit({
        type: "stash_pop_response",
        payload: { cwd, success: true, error: null, requestId },
      });
    } catch (error) {
      this.emit({
        type: "stash_pop_response",
        payload: { cwd, success: false, error: toCheckoutError(error), requestId },
      });
    }
  }

  private async handleStashListRequest(
    msg: Extract<SessionInboundMessage, { type: "stash_list_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;
    const appostleOnly = msg.appostleOnly !== false;
    try {
      const { stdout } = await execAsync("git stash list --format=%gd%x00%s", {
        cwd,
        env: READ_ONLY_GIT_ENV,
      });
      const lines = stdout.trim().split("\n").filter(Boolean);
      const entries: Array<{
        index: number;
        message: string;
        branch: string | null;
        isAppostle: boolean;
      }> = [];

      for (const line of lines) {
        const sepIdx = line.indexOf("\0");
        if (sepIdx < 0) continue;
        const refPart = line.slice(0, sepIdx);
        const subject = line.slice(sepIdx + 1);
        const indexMatch = refPart.match(/\{(\d+)\}/);
        if (!indexMatch) continue;
        const index = Number(indexMatch[1]);
        const prefixIdx = subject.indexOf(Session.APPOSTLE_STASH_PREFIX);
        const isAppostle = prefixIdx >= 0;
        const branch = isAppostle
          ? subject.slice(prefixIdx + Session.APPOSTLE_STASH_PREFIX.length).trim() || null
          : null;

        if (appostleOnly && !isAppostle) continue;
        entries.push({ index, message: subject, branch, isAppostle });
      }

      this.emit({
        type: "stash_list_response",
        payload: { cwd, entries, error: null, requestId },
      });
    } catch (error) {
      this.emit({
        type: "stash_list_response",
        payload: { cwd, entries: [], error: toCheckoutError(error), requestId },
      });
    }
  }

  private async handleCheckoutCommitRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_commit_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;

    try {
      let message = msg.message?.trim() ?? "";
      if (!message) {
        message = await this.generateCommitMessage(cwd);
      }
      if (!message) {
        throw new Error("Commit message is required");
      }

      await commitChanges(cwd, {
        message,
        addAll: msg.addAll ?? true,
      });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);

      this.emit({
        type: "checkout_commit_response",
        payload: {
          cwd,
          success: true,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_commit_response",
        payload: {
          cwd,
          success: false,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutGenerateCommitMessageRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_generate_commit_message_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;
    try {
      const message = await this.generateCommitMessage(cwd);
      this.emit({
        type: "checkout_generate_commit_message_response",
        payload: {
          cwd,
          message,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_generate_commit_message_response",
        payload: {
          cwd,
          message: null,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutMergeRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_merge_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;

    try {
      const status = await getCheckoutStatus(cwd, { appostleHome: this.appostleHome });
      if (!status.isGit) {
        try {
          await execAsync("git rev-parse --is-inside-work-tree", {
            cwd,
            env: READ_ONLY_GIT_ENV,
          });
        } catch (error) {
          const details =
            typeof (error as any)?.stderr === "string"
              ? String((error as any).stderr).trim()
              : error instanceof Error
                ? error.message
                : String(error);
          throw new Error(`Not a git repository: ${cwd}\n${details}`.trim());
        }
      }

      if (msg.requireCleanTarget) {
        const { stdout } = await execAsync("git status --porcelain", {
          cwd,
          env: READ_ONLY_GIT_ENV,
        });
        if (stdout.trim().length > 0) {
          throw new Error("Working directory has uncommitted changes.");
        }
      }

      let baseRef = msg.baseRef ?? (status.isGit ? status.baseRef : null);
      if (!baseRef) {
        throw new Error("Base branch is required for merge");
      }
      if (baseRef.startsWith("origin/")) {
        baseRef = baseRef.slice("origin/".length);
      }

      const mutatedCwd = await mergeToBase(
        cwd,
        {
          baseRef,
          mode: msg.strategy === "squash" ? "squash" : "merge",
        },
        { appostleHome: this.appostleHome },
      );
      this.github.invalidate({ cwd: mutatedCwd });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);
      // Force a fresh workspace snapshot. A fast-forward merge of another
      // branch into the base does not touch the current branch's working
      // tree, so the fs-watcher does not fire and the cached aheadBehind
      // would otherwise stay stale (showing "Merge into main" forever).
      await this.workspaceGitService.refresh(cwd);

      this.emit({
        type: "checkout_merge_response",
        payload: {
          cwd,
          success: true,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_merge_response",
        payload: {
          cwd,
          success: false,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutMergeFromBaseRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_merge_from_base_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;

    try {
      if (msg.requireCleanTarget ?? true) {
        const { stdout } = await execAsync("git status --porcelain", {
          cwd,
          env: READ_ONLY_GIT_ENV,
        });
        if (stdout.trim().length > 0) {
          throw new Error("Working directory has uncommitted changes.");
        }
      }

      await mergeFromBase(cwd, {
        baseRef: msg.baseRef,
        requireCleanTarget: msg.requireCleanTarget ?? true,
      });
      this.github.invalidate({ cwd });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);
      // Force a fresh workspace snapshot — see merge handler for rationale.
      await this.workspaceGitService.refresh(cwd);

      this.emit({
        type: "checkout_merge_from_base_response",
        payload: {
          cwd,
          success: true,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_merge_from_base_response",
        payload: {
          cwd,
          success: false,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutPullRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_pull_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;

    try {
      await pullCurrentBranch(cwd);
      this.github.invalidate({ cwd });
      this.checkoutDiffManager.scheduleRefreshForCwd(cwd);

      this.emit({
        type: "checkout_pull_response",
        payload: {
          cwd,
          success: true,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_pull_response",
        payload: {
          cwd,
          success: false,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutPushRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_push_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;

    try {
      await pushCurrentBranch(cwd);
      this.github.invalidate({ cwd });
      this.emit({
        type: "checkout_push_response",
        payload: {
          cwd,
          success: true,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_push_response",
        payload: {
          cwd,
          success: false,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutDeleteBranchRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_delete_branch_request" }>,
  ): Promise<void> {
    const { cwd, branchName, deleteRemote, requestId } = msg;

    try {
      const result = await deleteBranch(cwd, {
        branchName,
        deleteRemote: deleteRemote ?? true,
      });
      // Force a fresh workspace snapshot so the branch list / aheadBehind
      // reflect the deletion immediately. Same staleness pattern as merge.
      await this.workspaceGitService.refresh(cwd);

      this.emit({
        type: "checkout_delete_branch_response",
        payload: {
          cwd,
          branchName: result.branchName,
          localDeleted: result.localDeleted,
          remoteDeleted: result.remoteDeleted,
          remoteError: result.remoteError,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_delete_branch_response",
        payload: {
          cwd,
          branchName,
          localDeleted: false,
          remoteDeleted: false,
          remoteError: null,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutPrCreateRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_pr_create_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;

    try {
      let title = msg.title?.trim() ?? "";
      let body = msg.body?.trim() ?? "";

      if (!title || !body) {
        const generated = await this.generatePullRequestText(cwd, msg.baseRef);
        if (!title) title = generated.title;
        if (!body) body = generated.body;
      }

      const result = await createPullRequest(
        cwd,
        {
          title,
          body,
          base: msg.baseRef,
        },
        this.github,
      );

      this.emit({
        type: "checkout_pr_create_response",
        payload: {
          cwd,
          url: result.url ?? null,
          number: result.number ?? null,
          error: null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_pr_create_response",
        payload: {
          cwd,
          url: null,
          number: null,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handleCheckoutPrStatusRequest(
    msg: Extract<SessionInboundMessage, { type: "checkout_pr_status_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = msg;

    try {
      const snapshot = await this.workspaceGitService.getSnapshot(cwd);
      this.emit({
        type: "checkout_pr_status_response",
        payload: {
          cwd,
          status: normalizeCheckoutPrStatusPayload(snapshot.github.pullRequest),
          githubFeaturesEnabled: snapshot.github.featuresEnabled,
          error: snapshot.github.error
            ? {
                code: "UNKNOWN",
                message: snapshot.github.error.message,
              }
            : null,
          requestId,
        },
      });
    } catch (error) {
      this.emit({
        type: "checkout_pr_status_response",
        payload: {
          cwd,
          status: null,
          githubFeaturesEnabled: true,
          error: toCheckoutError(error),
          requestId,
        },
      });
    }
  }

  private async handlePullRequestTimelineRequest(
    msg: Extract<SessionInboundMessage, { type: "pull_request_timeline_request" }>,
  ): Promise<void> {
    const { cwd, prNumber, repoOwner, repoName, requestId } = msg;

    if (!isValidPullRequestTimelineIdentity({ prNumber, repoOwner, repoName })) {
      this.emit({
        type: "pull_request_timeline_response",
        payload: {
          cwd,
          prNumber,
          items: [],
          truncated: false,
          error: {
            kind: "unknown",
            message: "Pull request timeline request has invalid PR identity",
          },
          requestId,
          githubFeaturesEnabled: true,
        },
      });
      return;
    }

    const githubFeaturesEnabled = await this.github.isAuthenticated({ cwd });
    if (!githubFeaturesEnabled) {
      this.emit({
        type: "pull_request_timeline_response",
        payload: {
          cwd,
          prNumber,
          items: [],
          truncated: false,
          error: {
            kind: "unknown",
            message: "GitHub CLI is unavailable or not authenticated",
          },
          requestId,
          githubFeaturesEnabled: false,
        },
      });
      return;
    }

    try {
      const timeline = await this.github.getPullRequestTimeline({
        cwd,
        prNumber,
        repoOwner,
        repoName,
      });
      this.emit({
        type: "pull_request_timeline_response",
        payload: {
          cwd,
          prNumber: timeline.prNumber,
          items: timeline.items.map(toPullRequestTimelinePayloadItem),
          truncated: timeline.truncated,
          error: timeline.error,
          requestId,
          githubFeaturesEnabled: true,
        },
      });
    } catch (error) {
      this.emit({
        type: "pull_request_timeline_response",
        payload: {
          cwd,
          prNumber,
          items: [],
          truncated: false,
          error: {
            kind: "unknown",
            message: error instanceof Error ? error.message : String(error),
          },
          requestId,
          githubFeaturesEnabled: true,
        },
      });
    }
  }

  private async handleAppostleWorktreeListRequest(
    msg: Extract<SessionInboundMessage, { type: "appostle_worktree_list_request" }>,
  ): Promise<void> {
    return handleWorktreeListRequest(
      {
        emit: (message) => this.emit(message),
        appostleHome: this.appostleHome,
      },
      msg,
    );
  }

  private async handleAppostleWorktreeArchiveRequest(
    msg: Extract<SessionInboundMessage, { type: "appostle_worktree_archive_request" }>,
  ): Promise<void> {
    return handleWorktreeArchiveRequest(
      {
        appostleHome: this.appostleHome,
        github: this.github,
        agentManager: this.agentManager,
        agentStorage: this.agentStorage,
        archiveWorkspaceRecord: async (workspaceDirectory) => {
          const workspace = await this.findWorkspaceByDirectory(workspaceDirectory);
          if (workspace) {
            await this.archiveWorkspaceRecord(workspace.workspaceId);
          }
        },
        emit: (message) => this.emit(message),
        emitWorkspaceUpdatesForCwds: (cwds) => this.emitWorkspaceUpdatesForCwds(cwds),
        isPathWithinRoot: (rootPath, candidatePath) =>
          this.isPathWithinRoot(rootPath, candidatePath),
        killTerminalsUnderPath: (rootPath) => this.killTerminalsUnderPath(rootPath),
        sessionLogger: this.sessionLogger,
      },
      msg,
    );
  }

  /**
   * Handle read-only file explorer requests scoped to a workspace cwd
   */
  private async handleFileExplorerRequest(request: FileExplorerRequest): Promise<void> {
    const { cwd: workspaceCwd, path: requestedPath = ".", mode, requestId } = request;
    const cwd = workspaceCwd.trim();
    if (!cwd) {
      this.emit({
        type: "file_explorer_response",
        payload: {
          cwd: workspaceCwd,
          path: requestedPath,
          mode,
          directory: null,
          file: null,
          error: "cwd is required",
          requestId,
        },
      });
      return;
    }

    try {
      if (mode === "list") {
        const directory = await listDirectoryEntries({
          root: cwd,
          relativePath: requestedPath,
        });

        this.emit({
          type: "file_explorer_response",
          payload: {
            cwd,
            path: directory.path,
            mode,
            directory,
            file: null,
            error: null,
            requestId,
          },
        });
      } else {
        const file = await readExplorerFile({
          root: cwd,
          relativePath: requestedPath,
        });

        this.emit({
          type: "file_explorer_response",
          payload: {
            cwd,
            path: file.path,
            mode,
            directory: null,
            file,
            error: null,
            requestId,
          },
        });
      }
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, path: requestedPath },
        `Failed to fulfill file explorer request for workspace ${cwd}`,
      );
      this.emit({
        type: "file_explorer_response",
        payload: {
          cwd,
          path: requestedPath,
          mode,
          directory: null,
          file: null,
          error: error.message,
          requestId,
        },
      });
    }
  }

  /**
   * Handle recursive workspace file search.
   * Uses `fd` when available for speed, falls back to a Node fs.readdir walk.
   * Skips node_modules / .git / dist / build and honours .gitignore via fd.
   */
  private async handleWorkspaceSearchFilesRequest(
    request: WorkspaceSearchFilesRequest,
  ): Promise<void> {
    const { cwd, query, limit = 200, requestId } = request;
    const root = cwd.trim();

    if (!root || !query.trim()) {
      this.emit({
        type: "workspace_search_files_response",
        payload: { cwd, query, results: [], error: null, requestId },
      });
      return;
    }

    try {
      const results = await searchWorkspaceFiles({ root, query, limit });
      this.emit({
        type: "workspace_search_files_response",
        payload: { cwd, query, results, error: null, requestId },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, query },
        `Workspace file search failed for ${cwd}`,
      );
      this.emit({
        type: "workspace_search_files_response",
        payload: { cwd, query, results: [], error: error.message, requestId },
      });
    }
  }

  /**
   * Handle client-initiated file writes (currently only used by the plan-todos
   * UI to rewrite plan-file YAML frontmatter). The underlying service layer
   * allowlists extensions — this handler is just protocol glue.
   */
  private async handleFileWriteRequest(request: FileWriteRequest): Promise<void> {
    const { cwd: workspaceCwd, path: requestedPath, content, requestId } = request;
    const cwd = workspaceCwd.trim();
    if (!cwd) {
      this.emit({
        type: "file_write_response",
        payload: { cwd: workspaceCwd, path: requestedPath, error: "cwd is required", requestId },
      });
      return;
    }
    try {
      await writeTextFile({ root: cwd, relativePath: requestedPath, content });
      this.emit({
        type: "file_write_response",
        payload: { cwd, path: requestedPath, error: null, requestId },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, path: requestedPath },
        `Failed to write file ${requestedPath} in workspace ${cwd}`,
      );
      this.emit({
        type: "file_write_response",
        payload: {
          cwd,
          path: requestedPath,
          error: error?.message ?? "write failed",
          requestId,
        },
      });
    }
  }

  /**
   * Handle client-initiated file deletion (used by the Plans modal kebab menu
   * to remove plan markdown files from disk). The service layer scopes the
   * path to the workspace and allowlists `.md` extensions.
   */
  private async handleFileDeleteRequest(request: FileDeleteRequest): Promise<void> {
    const { cwd: workspaceCwd, path: requestedPath, requestId } = request;
    const cwd = workspaceCwd.trim();
    if (!cwd) {
      this.emit({
        type: "file_delete_response",
        payload: { cwd: workspaceCwd, path: requestedPath, error: "cwd is required", requestId },
      });
      return;
    }
    try {
      await deleteFile({ root: cwd, relativePath: requestedPath });
      this.emit({
        type: "file_delete_response",
        payload: { cwd, path: requestedPath, error: null, requestId },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, path: requestedPath },
        `Failed to delete file ${requestedPath} in workspace ${cwd}`,
      );
      this.emit({
        type: "file_delete_response",
        payload: {
          cwd,
          path: requestedPath,
          error: error?.message ?? "delete failed",
          requestId,
        },
      });
    }
  }

  private async handleFileExplorerDeleteRequest(request: FileExplorerDeleteRequest): Promise<void> {
    const { cwd: workspaceCwd, path: requestedPath, requestId } = request;
    const cwd = workspaceCwd.trim();
    if (!cwd) {
      this.emit({
        type: "file_explorer_delete_response",
        payload: { cwd: workspaceCwd, path: requestedPath, error: "cwd is required", requestId },
      });
      return;
    }
    try {
      await deleteEntry({ root: cwd, relativePath: requestedPath });
      this.emit({
        type: "file_explorer_delete_response",
        payload: { cwd, path: requestedPath, error: null, requestId },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, path: requestedPath },
        `Failed to delete entry ${requestedPath} in workspace ${cwd}`,
      );
      this.emit({
        type: "file_explorer_delete_response",
        payload: { cwd, path: requestedPath, error: error?.message ?? "delete failed", requestId },
      });
    }
  }

  private async handleFileMkdirRequest(request: FileMkdirRequest): Promise<void> {
    const { cwd: workspaceCwd, path: requestedPath, requestId } = request;
    const cwd = workspaceCwd.trim();
    if (!cwd) {
      this.emit({
        type: "file_mkdir_response",
        payload: { cwd: workspaceCwd, path: requestedPath, error: "cwd is required", requestId },
      });
      return;
    }
    try {
      await createDirectory({ root: cwd, relativePath: requestedPath });
      this.emit({
        type: "file_mkdir_response",
        payload: { cwd, path: requestedPath, error: null, requestId },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, path: requestedPath },
        `Failed to create directory ${requestedPath} in workspace ${cwd}`,
      );
      this.emit({
        type: "file_mkdir_response",
        payload: {
          cwd,
          path: requestedPath,
          error: error?.message ?? "mkdir failed",
          requestId,
        },
      });
    }
  }

  private async handleFileMoveRequest(request: FileMoveRequest): Promise<void> {
    const { cwd: workspaceCwd, sourcePath, destinationPath, requestId } = request;
    const cwd = workspaceCwd.trim();
    if (!cwd) {
      this.emit({
        type: "file_move_response",
        payload: {
          cwd: workspaceCwd,
          sourcePath,
          destinationPath,
          error: "cwd is required",
          requestId,
        },
      });
      return;
    }
    try {
      await moveEntry({ root: cwd, sourcePath, destinationPath });
      this.emit({
        type: "file_move_response",
        payload: { cwd, sourcePath, destinationPath, error: null, requestId },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, sourcePath, destinationPath },
        `Failed to move ${sourcePath} to ${destinationPath} in workspace ${cwd}`,
      );
      this.emit({
        type: "file_move_response",
        payload: {
          cwd,
          sourcePath,
          destinationPath,
          error: error?.message ?? "move failed",
          requestId,
        },
      });
    }
  }

  /**
   * Handle project icon request for a given cwd
   */
  private async handleProjectIconRequest(
    request: Extract<SessionInboundMessage, { type: "project_icon_request" }>,
  ): Promise<void> {
    const { cwd, requestId } = request;

    try {
      const icon = await getProjectIcon(cwd);
      this.emit({
        type: "project_icon_response",
        payload: {
          cwd,
          icon,
          error: null,
          requestId,
        },
      });
    } catch (error: any) {
      this.emit({
        type: "project_icon_response",
        payload: {
          cwd,
          icon: null,
          error: error.message,
          requestId,
        },
      });
    }
  }

  /**
   * Handle file download token request scoped to a workspace cwd
   */
  private async handleFileDownloadTokenRequest(request: FileDownloadTokenRequest): Promise<void> {
    const { cwd: workspaceCwd, path: requestedPath, requestId } = request;
    const cwd = workspaceCwd.trim();
    if (!cwd) {
      this.emit({
        type: "file_download_token_response",
        payload: {
          cwd: workspaceCwd,
          path: requestedPath,
          token: null,
          fileName: null,
          mimeType: null,
          size: null,
          error: "cwd is required",
          requestId,
        },
      });
      return;
    }

    this.sessionLogger.debug(
      { cwd, path: requestedPath },
      `Handling file download token request for workspace ${cwd} (${requestedPath})`,
    );

    try {
      const info = await getDownloadableFileInfo({
        root: cwd,
        relativePath: requestedPath,
      });

      // Files in `.appostle/tmp/uploaded_files/` are stored on disk as
      // `<id>__<sanitizedName>` to avoid collisions; the user expects to
      // download under the original sanitized name. Recover it here so the
      // browser/share-sheet/Save-As dialog shows the original.
      const recoveredFileName = originalUploadFileName(info.path) ?? info.fileName;

      const entry = this.downloadTokenStore.issueToken({
        path: info.path,
        absolutePath: info.absolutePath,
        fileName: recoveredFileName,
        mimeType: info.mimeType,
        size: info.size,
      });

      this.emit({
        type: "file_download_token_response",
        payload: {
          cwd,
          path: info.path,
          token: entry.token,
          fileName: entry.fileName,
          mimeType: entry.mimeType,
          size: entry.size,
          error: null,
          requestId,
        },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, cwd, path: requestedPath },
        `Failed to issue download token for workspace ${cwd}`,
      );
      this.emit({
        type: "file_download_token_response",
        payload: {
          cwd,
          path: requestedPath,
          token: null,
          fileName: null,
          mimeType: null,
          size: null,
          error: error.message,
          requestId,
        },
      });
    }
  }

  /**
   * Build the current agent list payload (live + persisted), optionally filtered by labels.
   */
  private async listAgentPayloads(filter?: {
    labels?: Record<string, string>;
  }): Promise<AgentSnapshotPayload[]> {
    // Multi-tenant: scope to the user owning this connection. Sessions are
    // always account-bound (websocket-server rejects unresolvable peers),
    // so this filter is always live — never a no-op pass-through. Agents
    // created by other users on the same daemon are filtered out here, and
    // per-agent mutations are gated below via `canUserAccessAgentById`.
    //
    // Internal agents (orchestrator queens / handoff workers) are still
    // included for this user's own scope — they ride out to the client as
    // normal AgentSnapshotPayload records with the `internal: true` flag
    // set. The display layer filters at the consumption point.
    const agentSnapshots = this.agentManager.listAgentsForUser(this.ownerUserId);
    const liveAgents = await Promise.all(
      agentSnapshots.map((agent) => this.buildAgentPayload(agent)),
    );

    // Add persisted agents that have not been lazily initialized yet.
    // Apply the SAME multi-tenant scoping as listAgentsForUser above —
    // archived agents typically live in persisted storage, and without
    // this filter they bleed into other users' sidebars.
    const registryRecords = await this.agentStorage.list();
    const requesterId = this.ownerUserId;
    const liveIds = new Set(agentSnapshots.map((a) => a.id));
    const persistedAgents = registryRecords
      .filter((record) => !liveIds.has(record.id))
      .filter((record) =>
        canUserAccessAgent(
          {
            ownerUserId: record.ownerUserId ?? null,
            sessionShares: record.sessionShares,
          },
          requesterId,
        ),
      )
      .map((record) => this.buildStoredAgentPayload(record));

    let agents = [...liveAgents, ...persistedAgents];

    agents = agents.filter((agent) => this.isProviderVisibleToClient(agent.provider));

    // Filter by labels if filter provided
    if (filter?.labels) {
      const filterLabels = filter.labels;
      agents = agents.filter((agent) =>
        Object.entries(filterLabels).every(([key, value]) => agent.labels[key] === value),
      );
    }

    return agents;
  }

  private async resolveAgentIdentifier(
    identifier: string,
  ): Promise<{ ok: true; agentId: string } | { ok: false; error: string }> {
    const trimmed = identifier.trim();
    if (!trimmed) {
      return { ok: false, error: "Agent identifier cannot be empty" };
    }

    // Quest cards can point at live internal agents. They should remain
    // absent from general listings, but when the client already has an exact
    // UUID for a live internal agent, allow it to resolve directly.
    if (this.agentManager.getAgentSnapshot(trimmed)) {
      // Multi-tenant gate: even with the exact id, a session must be
      // entitled to the agent before resolution succeeds.
      if (!this.agentManager.canUserAccessAgentById(trimmed, this.ownerUserId)) {
        return { ok: false, error: "Agent not found" };
      }
      return { ok: true, agentId: trimmed };
    }

    // Completed quest cards can also point at stored internal agents after
    // the runner closes them. Keep them hidden from list/search surfaces, but
    // allow exact-id lookup so the card can still open the transcript.
    const exactStored = await this.agentStorage.get(trimmed);
    if (exactStored) {
      // Multi-tenant gate: a known UUID is not authorization. Without this
      // check, any session that learns or guesses another user's archived
      // agent id can resolve it and reach downstream handlers (fetch,
      // cancel, fork, delete). `canUserAccessAgentById` is unsuitable here
      // because the agent is not live in `agentManager`; check the stored
      // record's owner / share list directly, mirroring the persisted
      // branch of `listAgents` above.
      if (
        !canUserAccessAgent(
          {
            ownerUserId: exactStored.ownerUserId ?? null,
            sessionShares: exactStored.sessionShares,
          },
          this.ownerUserId,
        )
      ) {
        return { ok: false, error: `Agent not found: ${trimmed}` };
      }
      return { ok: true, agentId: trimmed };
    }

    const stored = await this.agentStorage.list();
    const storedRecords = stored.filter((record) => !record.internal);
    const knownIds = new Set<string>();
    for (const record of storedRecords) {
      knownIds.add(record.id);
    }
    for (const agent of this.agentManager.listAgentsForUser(this.ownerUserId)) {
      knownIds.add(agent.id);
    }

    if (knownIds.has(trimmed)) {
      return { ok: true, agentId: trimmed };
    }

    const prefixMatches = Array.from(knownIds).filter((id) => id.startsWith(trimmed));
    if (prefixMatches.length === 1) {
      return { ok: true, agentId: prefixMatches[0] };
    }
    if (prefixMatches.length > 1) {
      return {
        ok: false,
        error: `Agent identifier "${trimmed}" is ambiguous (${prefixMatches
          .slice(0, 5)
          .map((id) => id.slice(0, 8))
          .join(", ")}${prefixMatches.length > 5 ? ", …" : ""})`,
      };
    }

    const titleMatches = storedRecords.filter((record) => record.title === trimmed);
    if (titleMatches.length === 1) {
      return { ok: true, agentId: titleMatches[0].id };
    }
    if (titleMatches.length > 1) {
      return {
        ok: false,
        error: `Agent title "${trimmed}" is ambiguous (${titleMatches
          .slice(0, 5)
          .map((r) => r.id.slice(0, 8))
          .join(", ")}${titleMatches.length > 5 ? ", …" : ""})`,
      };
    }

    return { ok: false, error: `Agent not found: ${trimmed}` };
  }

  private async getAgentPayloadById(agentId: string): Promise<AgentSnapshotPayload | null> {
    const live = this.agentManager.getAgentSnapshot(agentId);
    if (live) {
      const payload = await this.buildAgentPayload(live);
      return this.isProviderVisibleToClient(payload.provider) ? payload : null;
    }

    const record = await this.agentStorage.get(agentId);
    if (!record) {
      return null;
    }
    // Internal agents remain hidden from list/search surfaces, but callers
    // that already hold an exact agent id (for example a quest run card)
    // should still be able to open that agent's tab and inspect its history.
    const payload = this.buildStoredAgentPayload(record);
    return this.isProviderVisibleToClient(payload.provider) ? payload : null;
  }

  private normalizeFetchAgentsSort(
    sort: FetchAgentsRequestSort[] | undefined,
  ): FetchAgentsRequestSort[] {
    // Mirror the client default in `host-runtime.ts`. Workspace tabs are
    // derived from this order, and `created_at` ASC keeps them stable across
    // restarts/refreshes — `updated_at` would reshuffle them on every reply.
    const fallback: FetchAgentsRequestSort[] = [{ key: "created_at", direction: "asc" }];
    if (!sort || sort.length === 0) {
      return fallback;
    }

    const deduped: FetchAgentsRequestSort[] = [];
    const seen = new Set<string>();
    for (const entry of sort) {
      if (seen.has(entry.key)) {
        continue;
      }
      seen.add(entry.key);
      deduped.push(entry);
    }
    return deduped.length > 0 ? deduped : fallback;
  }

  private getStatusPriority(agent: AgentSnapshotPayload): number {
    const attentionReason = agent.attentionReason ?? null;
    const hasPendingPermission = (agent.pendingPermissions?.length ?? 0) > 0;
    if (hasPendingPermission || attentionReason === "permission") {
      return 0;
    }
    if (agent.status === "error" || attentionReason === "error") {
      return 1;
    }
    if (agent.status === "running") {
      return 2;
    }
    if (agent.status === "initializing") {
      return 3;
    }
    return 4;
  }

  private getFetchAgentsSortValue(
    entry: FetchAgentsResponseEntry,
    key: FetchAgentsRequestSort["key"],
  ): string | number | null {
    switch (key) {
      case "status_priority":
        return this.getStatusPriority(entry.agent);
      case "created_at":
        return Date.parse(entry.agent.createdAt);
      case "updated_at":
        return Date.parse(entry.agent.updatedAt);
      case "title":
        return entry.agent.title?.toLocaleLowerCase() ?? "";
    }
  }

  private getFetchAgentsSortValueFromAgent(
    agent: AgentSnapshotPayload,
    key: FetchAgentsRequestSort["key"],
  ): string | number | null {
    switch (key) {
      case "status_priority":
        return this.getStatusPriority(agent);
      case "created_at":
        return Date.parse(agent.createdAt);
      case "updated_at":
        return Date.parse(agent.updatedAt);
      case "title":
        return agent.title?.toLocaleLowerCase() ?? "";
    }
  }

  private compareSortValues(left: string | number | null, right: string | number | null): number {
    if (left === right) {
      return 0;
    }
    if (left === null) {
      return -1;
    }
    if (right === null) {
      return 1;
    }
    if (typeof left === "number" && typeof right === "number") {
      return left < right ? -1 : 1;
    }
    return String(left).localeCompare(String(right));
  }

  private compareFetchAgentsAgents(
    left: AgentSnapshotPayload,
    right: AgentSnapshotPayload,
    sort: FetchAgentsRequestSort[],
  ): number {
    for (const spec of sort) {
      const leftValue = this.getFetchAgentsSortValueFromAgent(left, spec.key);
      const rightValue = this.getFetchAgentsSortValueFromAgent(right, spec.key);
      const base = this.compareSortValues(leftValue, rightValue);
      if (base === 0) {
        continue;
      }
      return spec.direction === "asc" ? base : -base;
    }
    return left.id.localeCompare(right.id);
  }

  private encodeFetchAgentsCursor(
    entry: FetchAgentsResponseEntry,
    sort: FetchAgentsRequestSort[],
  ): string {
    const values: Record<string, string | number | null> = {};
    for (const spec of sort) {
      values[spec.key] = this.getFetchAgentsSortValue(entry, spec.key);
    }
    return Buffer.from(
      JSON.stringify({
        sort,
        values,
        id: entry.agent.id,
      }),
      "utf8",
    ).toString("base64url");
  }

  private decodeFetchAgentsCursor(
    cursor: string,
    sort: FetchAgentsRequestSort[],
  ): FetchAgentsCursor {
    let parsed: unknown;
    try {
      parsed = JSON.parse(Buffer.from(cursor, "base64url").toString("utf8"));
    } catch {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_agents cursor");
    }

    if (!parsed || typeof parsed !== "object") {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_agents cursor");
    }

    const payload = parsed as {
      sort?: unknown;
      values?: unknown;
      id?: unknown;
    };

    if (!Array.isArray(payload.sort) || typeof payload.id !== "string") {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_agents cursor");
    }
    if (!payload.values || typeof payload.values !== "object") {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_agents cursor");
    }

    const cursorSort: FetchAgentsRequestSort[] = [];
    for (const item of payload.sort) {
      if (
        !item ||
        typeof item !== "object" ||
        typeof (item as { key?: unknown }).key !== "string" ||
        typeof (item as { direction?: unknown }).direction !== "string"
      ) {
        throw new SessionRequestError("invalid_cursor", "Invalid fetch_agents cursor");
      }

      const key = (item as { key: string }).key;
      const direction = (item as { direction: string }).direction;
      if (
        (key !== "status_priority" &&
          key !== "created_at" &&
          key !== "updated_at" &&
          key !== "title") ||
        (direction !== "asc" && direction !== "desc")
      ) {
        throw new SessionRequestError("invalid_cursor", "Invalid fetch_agents cursor");
      }
      cursorSort.push({ key, direction });
    }

    if (
      cursorSort.length !== sort.length ||
      cursorSort.some(
        (entry, index) =>
          entry.key !== sort[index]?.key || entry.direction !== sort[index]?.direction,
      )
    ) {
      throw new SessionRequestError(
        "invalid_cursor",
        "fetch_agents cursor does not match current sort",
      );
    }

    return {
      sort: cursorSort,
      values: payload.values as Record<string, string | number | null>,
      id: payload.id,
    };
  }

  private compareAgentWithCursor(
    agent: AgentSnapshotPayload,
    cursor: FetchAgentsCursor,
    sort: FetchAgentsRequestSort[],
  ): number {
    for (const spec of sort) {
      const leftValue = this.getFetchAgentsSortValueFromAgent(agent, spec.key);
      const rightValue =
        cursor.values[spec.key] !== undefined ? (cursor.values[spec.key] ?? null) : null;
      const base = this.compareSortValues(leftValue, rightValue);
      if (base === 0) {
        continue;
      }
      return spec.direction === "asc" ? base : -base;
    }
    return agent.id.localeCompare(cursor.id);
  }

  private async listFetchAgentsEntries(
    request: Extract<SessionInboundMessage, { type: "fetch_agents_request" }>,
  ): Promise<{
    entries: FetchAgentsResponseEntry[];
    pageInfo: FetchAgentsResponsePageInfo;
  }> {
    const filter = request.filter;
    const sort = this.normalizeFetchAgentsSort(request.sort);

    const agents = await this.listAgentPayloads({
      labels: filter?.labels,
    });

    const placementByCwd = new Map<string, Promise<ProjectPlacementPayload | null>>();
    const getPlacement = (cwd: string): Promise<ProjectPlacementPayload | null> => {
      const existing = placementByCwd.get(cwd);
      if (existing) {
        return existing;
      }
      const placementPromise = this.buildProjectPlacementForCwd(cwd);
      placementByCwd.set(cwd, placementPromise);
      return placementPromise;
    };

    let candidates = [...agents];
    candidates.sort((left, right) => this.compareFetchAgentsAgents(left, right, sort));
    const cursorToken = request.page?.cursor;
    if (cursorToken) {
      const cursor = this.decodeFetchAgentsCursor(cursorToken, sort);
      candidates = candidates.filter(
        (agent) => this.compareAgentWithCursor(agent, cursor, sort) > 0,
      );
    }

    const limit = request.page?.limit ?? 200;

    const matchedEntries: FetchAgentsResponseEntry[] = [];
    const batchSize = 25;
    for (
      let start = 0;
      start < candidates.length && matchedEntries.length <= limit;
      start += batchSize
    ) {
      const batch = candidates.slice(start, start + batchSize);
      const batchEntries = await Promise.all(
        batch.map(async (agent) => {
          const project = await getPlacement(agent.cwd);
          return project ? { agent, project } : null;
        }),
      );
      for (const entry of batchEntries) {
        if (!entry) {
          continue;
        }
        if (
          !this.matchesAgentFilter({
            agent: entry.agent,
            project: entry.project,
            filter,
          })
        ) {
          continue;
        }
        matchedEntries.push(entry);
        if (matchedEntries.length > limit) {
          break;
        }
      }
    }

    const pagedEntries = matchedEntries.slice(0, limit);
    const hasMore = matchedEntries.length > limit;
    const nextCursor =
      hasMore && pagedEntries.length > 0
        ? this.encodeFetchAgentsCursor(pagedEntries[pagedEntries.length - 1], sort)
        : null;

    return {
      entries: pagedEntries,
      pageInfo: {
        nextCursor,
        prevCursor: request.page?.cursor ?? null,
        hasMore,
      },
    };
  }

  private readonly workspaceStatePriority: Record<WorkspaceStateBucket, number> = {
    needs_input: 0,
    failed: 1,
    running: 2,
    attention: 3,
    done: 4,
  };

  private deriveWorkspaceStateBucket(agent: AgentSnapshotPayload): WorkspaceStateBucket {
    const pendingPermissionCount = agent.pendingPermissions?.length ?? 0;
    if (pendingPermissionCount > 0 || agent.attentionReason === "permission") {
      return "needs_input";
    }
    if (agent.status === "error" || agent.attentionReason === "error") {
      return "failed";
    }
    if (agent.status === "running") {
      return "running";
    }
    if (agent.requiresAttention) {
      return "attention";
    }
    return "done";
  }

  private async describeWorkspaceRecord(
    workspace: PersistedWorkspaceRecord,
    projectRecord?: PersistedProjectRecord | null,
  ): Promise<WorkspaceDescriptorPayload> {
    const resolvedProjectRecord =
      projectRecord ?? (await this.projectRegistry.get(workspace.projectId));

    // Diff stats (both branch-level and uncommitted) are overlaid by
    // describeWorkspaceRecordWithGitData from the fresh workspace-git-service
    // snapshot. This lightweight path omits them; the listing renders
    // without numbers until the git snapshot arrives.
    return {
      id: workspace.workspaceId,
      projectId: workspace.projectId,
      projectDisplayName: resolvedProjectRecord?.displayName ?? String(workspace.projectId),
      projectRootPath: resolvedProjectRecord?.rootPath ?? workspace.cwd,
      workspaceDirectory: workspace.cwd,
      projectKind: (resolvedProjectRecord?.kind ?? "directory") === "git" ? "git" : "non_git",
      workspaceKind: workspace.kind,
      name: workspace.displayName,
      status: "done",
      activityAt: null,
      branchDiffStat: null,
      uncommittedDiffStat: null,
      scripts:
        this.scriptRouteStore && this.scriptRuntimeStore
          ? buildWorkspaceScriptPayloads({
              workspaceId: workspace.workspaceId,
              workspaceDirectory: workspace.cwd,
              routeStore: this.scriptRouteStore,
              runtimeStore: this.scriptRuntimeStore,
              daemonPort: this.getDaemonTcpPort?.() ?? null,
              resolveHealth: this.resolveScriptHealth ?? undefined,
            })
          : [],
    };
  }

  private buildWorkspaceGitRuntimePayload(
    snapshot: WorkspaceGitRuntimeSnapshot,
  ): NonNullable<WorkspaceDescriptorPayload["gitRuntime"]> | null {
    if (!snapshot.git.isGit) {
      return null;
    }

    return {
      currentBranch: snapshot.git.currentBranch,
      remoteUrl: snapshot.git.remoteUrl,
      isAppostleOwnedWorktree: snapshot.git.isAppostleOwnedWorktree,
      isDirty: snapshot.git.isDirty,
      aheadBehind: snapshot.git.aheadBehind,
      aheadOfOrigin: snapshot.git.aheadOfOrigin,
      behindOfOrigin: snapshot.git.behindOfOrigin,
    };
  }

  private buildWorkspaceGitHubRuntimePayload(
    snapshot: WorkspaceGitRuntimeSnapshot,
  ): NonNullable<WorkspaceDescriptorPayload["githubRuntime"]> {
    return {
      featuresEnabled: snapshot.github.featuresEnabled,
      pullRequest: snapshot.github.pullRequest,
      error: snapshot.github.error,
      refreshedAt: snapshot.github.refreshedAt,
    };
  }

  private async describeWorkspaceRecordWithGitData(
    workspace: PersistedWorkspaceRecord,
    projectRecord?: PersistedProjectRecord | null,
  ): Promise<WorkspaceDescriptorPayload> {
    const base = await this.describeWorkspaceRecord(workspace, projectRecord);
    let snapshot = this.workspaceGitService.peekSnapshot(workspace.cwd);
    if (!snapshot) {
      try {
        snapshot = await this.workspaceGitService.getSnapshot(workspace.cwd);
      } catch (error) {
        this.sessionLogger.warn(
          { err: error, cwd: workspace.cwd },
          "Failed to load git snapshot for workspace",
        );
        return base;
      }
    }

    if (!snapshot) {
      return base;
    }

    const checkout = checkoutLiteFromGitSnapshot(workspace.cwd, snapshot.git);
    const displayName = deriveWorkspaceDisplayName({ cwd: workspace.cwd, checkout });

    return {
      ...base,
      name: displayName,
      branchDiffStat: snapshot.git.branchDiffStat ?? null,
      uncommittedDiffStat: snapshot.git.uncommittedDiffStat ?? null,
      gitRuntime: this.buildWorkspaceGitRuntimePayload(snapshot) ?? undefined,
      githubRuntime: this.buildWorkspaceGitHubRuntimePayload(snapshot),
    };
  }

  private async buildWorkspaceDescriptor(input: {
    workspace: PersistedWorkspaceRecord;
    projectRecord?: PersistedProjectRecord | null;
    includeGitData: boolean;
  }): Promise<WorkspaceDescriptorPayload> {
    if (input.includeGitData && input.projectRecord?.kind === "git") {
      return this.describeWorkspaceRecordWithGitData(input.workspace, input.projectRecord);
    }
    return this.describeWorkspaceRecord(input.workspace, input.projectRecord);
  }

  private async buildWorkspaceDescriptorMap(options: {
    includeGitData: boolean;
    workspaceIds?: Iterable<string>;
  }): Promise<Map<string, WorkspaceDescriptorPayload>> {
    const [agents, persistedWorkspaces, persistedProjects] = await Promise.all([
      this.listAgentPayloads(),
      this.workspaceRegistry.list(),
      this.projectRegistry.list(),
    ]);

    const activeRecords = persistedWorkspaces.filter((workspace) => !workspace.archivedAt);
    const activeProjects = new Map(
      persistedProjects
        .filter((project) => !project.archivedAt)
        .map((project) => [project.projectId, project] as const),
    );
    const descriptorsByWorkspaceId = new Map<string, WorkspaceDescriptorPayload>();
    const workspaceIds = options.workspaceIds ? new Set(options.workspaceIds) : null;
    const workspaceIdsByDirectory = new Map(
      activeRecords.map((workspace) => [workspace.cwd, workspace.workspaceId] as const),
    );

    for (const workspace of activeRecords) {
      if (workspaceIds && !workspaceIds.has(workspace.workspaceId)) {
        continue;
      }
      const projectRecord = activeProjects.get(workspace.projectId) ?? null;
      descriptorsByWorkspaceId.set(
        workspace.workspaceId,
        await this.buildWorkspaceDescriptor({
          workspace,
          projectRecord,
          includeGitData: options.includeGitData,
        }),
      );
    }

    for (const agent of agents) {
      if (agent.archivedAt) {
        continue;
      }
      if (agent.status === "closed") {
        continue;
      }
      if (agent.internal) {
        continue;
      }
      if (!this.isProviderVisibleToClient(agent.provider)) {
        continue;
      }

      const workspaceId = workspaceIdsByDirectory.get(normalizePersistedWorkspaceId(agent.cwd));
      if (workspaceId === undefined) {
        continue;
      }
      const existing = descriptorsByWorkspaceId.get(workspaceId);
      if (!existing) {
        continue;
      }

      const bucket = this.deriveWorkspaceStateBucket(agent);
      if (this.workspaceStatePriority[bucket] < this.workspaceStatePriority[existing.status]) {
        existing.status = bucket;
      }
    }

    return descriptorsByWorkspaceId;
  }

  private resolveRegisteredWorkspaceIdForCwd(
    cwd: string,
    workspaces: PersistedWorkspaceRecord[],
  ): string {
    const normalizedCwd = normalizePersistedWorkspaceId(cwd);
    const exact = workspaces.find((workspace) => workspace.cwd === normalizedCwd);
    if (exact) {
      return exact.workspaceId;
    }

    let bestMatch: PersistedWorkspaceRecord | null = null;
    for (const workspace of workspaces) {
      const prefix = workspace.cwd.endsWith(sep) ? workspace.cwd : `${workspace.cwd}${sep}`;
      if (!normalizedCwd.startsWith(prefix)) {
        continue;
      }
      if (!bestMatch || workspace.cwd.length > bestMatch.cwd.length) {
        bestMatch = workspace;
      }
    }

    return bestMatch?.workspaceId ?? normalizedCwd;
  }

  private async listWorkspaceDescriptors(): Promise<WorkspaceDescriptorPayload[]> {
    return Array.from(
      (
        await this.buildWorkspaceDescriptorMap({
          includeGitData: true,
        })
      ).values(),
    );
  }

  private normalizeFetchWorkspacesSort(
    sort: FetchWorkspacesRequestSort[] | undefined,
  ): FetchWorkspacesRequestSort[] {
    const fallback: FetchWorkspacesRequestSort[] = [{ key: "activity_at", direction: "desc" }];
    if (!sort || sort.length === 0) {
      return fallback;
    }
    const deduped: FetchWorkspacesRequestSort[] = [];
    const seen = new Set<string>();
    for (const entry of sort) {
      if (seen.has(entry.key)) {
        continue;
      }
      seen.add(entry.key);
      deduped.push(entry);
    }
    return deduped.length > 0 ? deduped : fallback;
  }

  private getFetchWorkspacesSortValue(
    workspace: WorkspaceDescriptorPayload,
    key: FetchWorkspacesRequestSort["key"],
  ): string | number | null {
    switch (key) {
      case "status_priority":
        return this.workspaceStatePriority[workspace.status];
      case "activity_at":
        return workspace.activityAt ? Date.parse(workspace.activityAt) : null;
      case "name":
        return workspace.name.toLocaleLowerCase();
      case "project_id":
        return workspace.projectId.toLocaleLowerCase();
    }
  }

  private compareFetchWorkspacesEntries(
    left: WorkspaceDescriptorPayload,
    right: WorkspaceDescriptorPayload,
    sort: FetchWorkspacesRequestSort[],
  ): number {
    for (const spec of sort) {
      const leftValue = this.getFetchWorkspacesSortValue(left, spec.key);
      const rightValue = this.getFetchWorkspacesSortValue(right, spec.key);
      const base = this.compareSortValues(leftValue, rightValue);
      if (base === 0) {
        continue;
      }
      return spec.direction === "asc" ? base : -base;
    }
    return left.id.localeCompare(right.id);
  }

  private encodeFetchWorkspacesCursor(
    entry: FetchWorkspacesResponseEntry,
    sort: FetchWorkspacesRequestSort[],
  ): string {
    const values: Record<string, string | number | null> = {};
    for (const spec of sort) {
      values[spec.key] = this.getFetchWorkspacesSortValue(entry, spec.key);
    }
    return Buffer.from(
      JSON.stringify({
        sort,
        values,
        id: entry.id,
      }),
      "utf8",
    ).toString("base64url");
  }

  private decodeFetchWorkspacesCursor(
    cursor: string,
    sort: FetchWorkspacesRequestSort[],
  ): FetchWorkspacesCursor {
    let parsed: unknown;
    try {
      parsed = JSON.parse(Buffer.from(cursor, "base64url").toString("utf8"));
    } catch {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_workspaces cursor");
    }

    if (!parsed || typeof parsed !== "object") {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_workspaces cursor");
    }

    const payload = parsed as {
      sort?: unknown;
      values?: unknown;
      id?: unknown;
    };

    if (!Array.isArray(payload.sort) || typeof payload.id !== "string") {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_workspaces cursor");
    }
    if (!payload.values || typeof payload.values !== "object") {
      throw new SessionRequestError("invalid_cursor", "Invalid fetch_workspaces cursor");
    }

    const cursorSort: FetchWorkspacesRequestSort[] = [];
    for (const item of payload.sort) {
      if (
        !item ||
        typeof item !== "object" ||
        typeof (item as { key?: unknown }).key !== "string" ||
        typeof (item as { direction?: unknown }).direction !== "string"
      ) {
        throw new SessionRequestError("invalid_cursor", "Invalid fetch_workspaces cursor");
      }

      const key = (item as { key: string }).key;
      const direction = (item as { direction: string }).direction;
      if (
        (key !== "status_priority" &&
          key !== "activity_at" &&
          key !== "name" &&
          key !== "project_id") ||
        (direction !== "asc" && direction !== "desc")
      ) {
        throw new SessionRequestError("invalid_cursor", "Invalid fetch_workspaces cursor");
      }
      cursorSort.push({ key, direction });
    }

    if (
      cursorSort.length !== sort.length ||
      cursorSort.some(
        (entry, index) =>
          entry.key !== sort[index]?.key || entry.direction !== sort[index]?.direction,
      )
    ) {
      throw new SessionRequestError(
        "invalid_cursor",
        "fetch_workspaces cursor does not match current sort",
      );
    }

    return {
      sort: cursorSort,
      values: payload.values as Record<string, string | number | null>,
      id: String(payload.id),
    };
  }

  private compareWorkspaceWithCursor(
    workspace: WorkspaceDescriptorPayload,
    cursor: FetchWorkspacesCursor,
    sort: FetchWorkspacesRequestSort[],
  ): number {
    for (const spec of sort) {
      const leftValue = this.getFetchWorkspacesSortValue(workspace, spec.key);
      const rightValue =
        cursor.values[spec.key] !== undefined ? (cursor.values[spec.key] ?? null) : null;
      const base = this.compareSortValues(leftValue, rightValue);
      if (base === 0) {
        continue;
      }
      return spec.direction === "asc" ? base : -base;
    }
    return workspace.id.localeCompare(cursor.id);
  }

  private matchesWorkspaceFilter(input: {
    workspace: WorkspaceDescriptorPayload;
    filter: FetchWorkspacesRequestFilter | undefined;
  }): boolean {
    const { workspace, filter } = input;
    if (!filter) {
      return true;
    }

    if (filter.projectId && filter.projectId.trim().length > 0) {
      if (workspace.projectId !== filter.projectId.trim()) {
        return false;
      }
    }

    if (filter.idPrefix && filter.idPrefix.trim().length > 0) {
      if (!String(workspace.id).startsWith(filter.idPrefix.trim())) {
        return false;
      }
    }

    if (filter.query && filter.query.trim().length > 0) {
      const query = filter.query.trim().toLocaleLowerCase();
      const haystacks = [workspace.name, String(workspace.projectId), String(workspace.id)];
      if (!haystacks.some((value) => value.toLocaleLowerCase().includes(query))) {
        return false;
      }
    }

    return true;
  }

  private async listFetchWorkspacesEntries(
    request: Extract<SessionInboundMessage, { type: "fetch_workspaces_request" }>,
  ): Promise<{
    entries: FetchWorkspacesResponseEntry[];
    pageInfo: FetchWorkspacesResponsePageInfo;
  }> {
    const filter = request.filter;
    const sort = this.normalizeFetchWorkspacesSort(request.sort);
    let entries = await this.listWorkspaceDescriptors();
    const listedCount = entries.length;
    entries = entries.filter((workspace) => this.matchesWorkspaceFilter({ workspace, filter }));
    const filteredCount = entries.length;
    entries.sort((left, right) => this.compareFetchWorkspacesEntries(left, right, sort));

    const cursorToken = request.page?.cursor;
    if (cursorToken) {
      const cursor = this.decodeFetchWorkspacesCursor(cursorToken, sort);
      entries = entries.filter(
        (workspace) => this.compareWorkspaceWithCursor(workspace, cursor, sort) > 0,
      );
    }

    const limit = request.page?.limit ?? 200;
    const pagedEntries = entries.slice(0, limit);
    const hasMore = entries.length > limit;
    const nextCursor =
      hasMore && pagedEntries.length > 0
        ? this.encodeFetchWorkspacesCursor(pagedEntries[pagedEntries.length - 1], sort)
        : null;

    this.sessionLogger.debug(
      {
        requestId: request.requestId,
        filter: request.filter ?? null,
        sort,
        page: request.page ?? null,
        listedCount,
        filteredCount,
        returnedCount: pagedEntries.length,
        hasMore,
        nextCursor,
      },
      "fetch_workspaces_entries_listed",
    );

    return {
      entries: pagedEntries,
      pageInfo: {
        nextCursor,
        prevCursor: request.page?.cursor ?? null,
        hasMore,
      },
    };
  }

  private bufferOrEmitWorkspaceUpdate(
    subscription: WorkspaceUpdatesSubscriptionState,
    payload: WorkspaceUpdatePayload,
  ): void {
    if (subscription.isBootstrapping) {
      const workspaceId = payload.kind === "upsert" ? payload.workspace.id : payload.id;
      subscription.pendingUpdatesByWorkspaceId.set(workspaceId, payload);
      return;
    }
    const workspaceId = payload.kind === "upsert" ? payload.workspace.id : payload.id;
    subscription.lastEmittedByWorkspaceId.set(workspaceId, payload);
    this.emit({
      type: "workspace_update",
      payload,
    });
  }

  private flushBootstrappedWorkspaceUpdates(options?: {
    snapshotLatestActivityByWorkspaceId?: Map<string, number>;
  }): void {
    const subscription = this.workspaceUpdatesSubscription;
    if (!subscription || !subscription.isBootstrapping) {
      return;
    }

    subscription.isBootstrapping = false;
    const pending = Array.from(subscription.pendingUpdatesByWorkspaceId.values());
    subscription.pendingUpdatesByWorkspaceId.clear();

    for (const payload of pending) {
      if (payload.kind === "upsert") {
        const snapshotLatestActivity = options?.snapshotLatestActivityByWorkspaceId?.get(
          payload.workspace.id,
        );
        if (typeof snapshotLatestActivity === "number") {
          const updateLatestActivity = payload.workspace.activityAt
            ? Date.parse(payload.workspace.activityAt)
            : Number.NEGATIVE_INFINITY;
          if (
            !Number.isNaN(updateLatestActivity) &&
            updateLatestActivity <= snapshotLatestActivity
          ) {
            continue;
          }
        }
      }
      this.emit({
        type: "workspace_update",
        payload,
      });
    }
  }

  private async findOrCreateWorkspaceForDirectory(cwd: string): Promise<PersistedWorkspaceRecord> {
    const normalizedCwd = await this.resolveWorkspaceDirectory(cwd);
    const existingWorkspace = await this.findWorkspaceByDirectory(normalizedCwd);
    if (existingWorkspace) {
      const unarchived = await this.ensureWorkspaceRecordUnarchived(existingWorkspace);
      return this.reconcileWorkspaceGitClassification(unarchived);
    }

    const placement = await buildProjectPlacementForCwdStandalone({
      cwd: normalizedCwd,
      workspaceGitService: this.workspaceGitService,
    });
    const workspaceId = deriveWorkspaceId(normalizedCwd, placement.checkout);
    const timestamp = new Date().toISOString();

    const projectRecord = createPersistedProjectRecord({
      projectId: placement.projectKey,
      rootPath: deriveProjectRootPath({ cwd: normalizedCwd, checkout: placement.checkout }),
      kind: deriveProjectKind(placement.checkout),
      displayName: placement.projectName,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    await this.projectRegistry.upsert(projectRecord);
    // Seeding the brand schema writes into `<rootPath>/.appostle/brand/`. If the
    // workspace lives on a read-only / restricted drive (Windows A:, network
    // share, mounted backup volume, etc.) the mkdir fails — but the workspace
    // itself is still openable without the brand seed. Log and continue.
    try {
      await seedProjectBrand(projectRecord.rootPath, { logger: this.sessionLogger });
    } catch (err) {
      this.sessionLogger.warn(
        { err, rootPath: projectRecord.rootPath },
        "could not seed project brand — workspace directory is not writable; continuing without brand",
      );
    }

    const workspaceRecord = createPersistedWorkspaceRecord({
      workspaceId,
      projectId: placement.projectKey,
      cwd: normalizedCwd,
      kind: deriveWorkspaceKind(placement.checkout),
      displayName: deriveWorkspaceDisplayName({
        cwd: normalizedCwd,
        checkout: placement.checkout,
      }),
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    await this.workspaceRegistry.upsert(workspaceRecord);
    return workspaceRecord;
  }

  /**
   * Re-derive the git classification of an already-registered workspace from a
   * fresh git probe and migrate the registry if it changed.
   *
   * The registry's `kind` is written once at registration and was never
   * refreshed, so a folder added *before* `git init`/clone permanently kept its
   * `directory`/`non_git` classification even after becoming a real repo — the
   * git panel never appeared. Running this on every open (and after an explicit
   * init/clone) lets that state self-heal. Becoming a git repo can also change
   * the project grouping key (path → `remote:host/owner/repo`), so we migrate
   * the project + workspace records, pruning the now-orphaned originals.
   *
   * Best-effort: any failure returns the existing record unchanged so a flaky
   * git probe never blocks opening a workspace.
   */
  private async reconcileWorkspaceGitClassification(
    existing: PersistedWorkspaceRecord,
  ): Promise<PersistedWorkspaceRecord> {
    try {
      // Bust the git service's cached snapshot — it may predate the repo
      // creation we're trying to detect.
      await this.workspaceGitService.refresh(existing.cwd).catch(() => {});

      const placement = await buildProjectPlacementForCwdStandalone({
        cwd: existing.cwd,
        workspaceGitService: this.workspaceGitService,
      });
      const newWorkspaceId = deriveWorkspaceId(existing.cwd, placement.checkout);
      const newWorkspaceKind = deriveWorkspaceKind(placement.checkout);
      const newProjectKind = deriveProjectKind(placement.checkout);
      const existingProject = await this.projectRegistry.get(existing.projectId);

      const projectChanged =
        !existingProject ||
        existingProject.projectId !== placement.projectKey ||
        existingProject.kind !== newProjectKind;
      const workspaceChanged =
        existing.workspaceId !== newWorkspaceId ||
        existing.projectId !== placement.projectKey ||
        existing.kind !== newWorkspaceKind;

      if (!projectChanged && !workspaceChanged) {
        return existing;
      }

      const timestamp = new Date().toISOString();

      await this.projectRegistry.upsert(
        createPersistedProjectRecord({
          projectId: placement.projectKey,
          rootPath: deriveProjectRootPath({ cwd: existing.cwd, checkout: placement.checkout }),
          kind: newProjectKind,
          displayName: placement.projectName,
          createdAt: existingProject?.createdAt ?? existing.createdAt,
          updatedAt: timestamp,
        }),
      );

      const migrated = createPersistedWorkspaceRecord({
        workspaceId: newWorkspaceId,
        projectId: placement.projectKey,
        cwd: existing.cwd,
        kind: newWorkspaceKind,
        displayName: deriveWorkspaceDisplayName({
          cwd: existing.cwd,
          checkout: placement.checkout,
        }),
        createdAt: existing.createdAt,
        updatedAt: timestamp,
      });
      await this.workspaceRegistry.upsert(migrated);

      // Prune records the migration replaced (id changed) so we don't leave a
      // stale `directory` workspace or an orphaned `non_git` project behind.
      if (newWorkspaceId !== existing.workspaceId) {
        await this.workspaceRegistry.remove(existing.workspaceId);
      }
      if (placement.projectKey !== existing.projectId) {
        const siblings = (await this.workspaceRegistry.list()).filter(
          (workspace) => workspace.projectId === existing.projectId && !workspace.archivedAt,
        );
        if (siblings.length === 0) {
          await this.projectRegistry.remove(existing.projectId);
        }
      }

      this.sessionLogger.info(
        {
          cwd: existing.cwd,
          fromProjectId: existing.projectId,
          toProjectId: placement.projectKey,
          fromKind: existing.kind,
          toKind: newWorkspaceKind,
        },
        "Re-detected workspace git classification on open",
      );

      return migrated;
    } catch (error) {
      this.sessionLogger.warn(
        { err: error, cwd: existing.cwd },
        "Failed to reconcile workspace git classification; keeping existing record",
      );
      return existing;
    }
  }

  private async ensureWorkspaceRecordUnarchived(
    workspace: PersistedWorkspaceRecord,
  ): Promise<PersistedWorkspaceRecord> {
    const project = await this.projectRegistry.get(workspace.projectId);
    if (!workspace.archivedAt && (!project || !project.archivedAt)) {
      return workspace;
    }

    const timestamp = new Date().toISOString();
    let unarchivedWorkspace = workspace;
    if (workspace.archivedAt) {
      unarchivedWorkspace = { ...workspace, archivedAt: null, updatedAt: timestamp };
      await this.workspaceRegistry.upsert(unarchivedWorkspace);
    }
    if (project?.archivedAt) {
      await this.projectRegistry.upsert({
        ...project,
        archivedAt: null,
        updatedAt: timestamp,
      });
    }
    if (workspace.archivedAt) {
      // The archive fan-out deleted the cloud project (rows + stacks +
      // tabs) — re-backfill it so the workspace reappears in tab strips
      // and recents. Best-effort.
      try {
        this.onWorkspaceUnarchived?.(unarchivedWorkspace);
      } catch (err) {
        this.sessionLogger.warn(
          { err, workspaceId: workspace.workspaceId },
          "onWorkspaceUnarchived hook failed",
        );
      }
    }
    return unarchivedWorkspace;
  }

  private async createAppostleWorktree(
    input: CreateAppostleWorktreeInput,
    options?: {
      resolveRepositoryDefaultBranch?: (repoRoot: string) => Promise<string>;
    },
  ): Promise<CreateAppostleWorktreeResult> {
    const coreDeps = createWorktreeCoreDeps(this.github);
    return createAppostleWorktree(input, {
      ...coreDeps,
      ...(options?.resolveRepositoryDefaultBranch
        ? { resolveRepositoryDefaultBranch: options.resolveRepositoryDefaultBranch }
        : {}),
      projectRegistry: this.projectRegistry,
      workspaceRegistry: this.workspaceRegistry,
      workspaceGitService: this.workspaceGitService,
      primeWorkspaceGitWatchFingerprints: (workspace) =>
        this.primeWorkspaceGitWatchFingerprintForWorkspace(workspace),
      broadcastWorkspaceUpdate: (workspaceId) =>
        this.emitWorkspaceUpdateForWorkspaceId(workspaceId),
    });
  }

  private async archiveWorkspaceRecord(workspaceId: string, archivedAt?: string): Promise<void> {
    const existingWorkspace = await this.workspaceRegistry.get(workspaceId);
    if (!existingWorkspace || existingWorkspace.archivedAt) {
      this.removeWorkspaceGitSubscription(workspaceId);
      return;
    }

    const nextArchivedAt = archivedAt ?? new Date().toISOString();
    await this.workspaceRegistry.archive(workspaceId, nextArchivedAt);
    await this.removeWorkspaceGitWatchTarget(existingWorkspace.cwd);
    this.scriptRuntimeStore?.removeForWorkspace(existingWorkspace.cwd);
    this.removeWorkspaceGitSubscription(workspaceId);

    const siblingWorkspaces = (await this.workspaceRegistry.list()).filter(
      (workspace) => workspace.projectId === existingWorkspace.projectId && !workspace.archivedAt,
    );
    if (siblingWorkspaces.length === 0) {
      await this.projectRegistry.archive(existingWorkspace.projectId, nextArchivedAt);
    }

    // Fan the removal out to the auth-server (delete project row + stacks +
    // tabs) so the project doesn't linger in recents / tab strips. Best-effort.
    try {
      this.onWorkspaceArchived?.(existingWorkspace);
    } catch (err) {
      this.sessionLogger.warn({ err, workspaceId }, "onWorkspaceArchived hook failed");
    }
  }

  private async reconcileAndEmitWorkspaceUpdates(): Promise<void> {
    if (!this.workspaceUpdatesSubscription) {
      return;
    }
    try {
      const changedWorkspaceIds = await this.reconcileActiveWorkspaceRecords();
      if (changedWorkspaceIds.size === 0) {
        return;
      }
      await this.emitWorkspaceUpdatesForWorkspaceIds(changedWorkspaceIds, {
        skipReconcile: true,
      });
    } catch (error) {
      this.sessionLogger.error({ err: error }, "Background workspace reconciliation failed");
    }
  }

  private async reconcileActiveWorkspaceRecords(): Promise<Set<string>> {
    return new Set();
  }

  private async emitWorkspaceUpdatesForWorkspaceIds(
    workspaceIds: Iterable<string>,
    options?: { skipReconcile?: boolean; dedupeGitState?: boolean },
  ): Promise<void> {
    const subscription = this.workspaceUpdatesSubscription;
    if (!subscription) {
      return;
    }

    const uniqueWorkspaceIds = new Set(Array.from(workspaceIds));
    if (uniqueWorkspaceIds.size === 0) {
      return;
    }

    const descriptorsByWorkspaceId = await this.buildWorkspaceDescriptorMap({
      workspaceIds: uniqueWorkspaceIds,
      includeGitData: true,
    });

    for (const workspaceId of uniqueWorkspaceIds) {
      const workspace = descriptorsByWorkspaceId.get(workspaceId);
      const nextWorkspace =
        workspace && this.matchesWorkspaceFilter({ workspace, filter: subscription.filter })
          ? workspace
          : null;
      if (
        options?.dedupeGitState &&
        this.shouldSkipWorkspaceGitWatchUpdate(workspaceId, nextWorkspace)
      ) {
        continue;
      }
      const watchTarget = this.workspaceGitWatchTargets.get(workspaceId);
      if (watchTarget && this.onBranchChanged) {
        const newBranchName = nextWorkspace?.name ?? null;
        if (newBranchName !== watchTarget.lastBranchName) {
          this.onBranchChanged(workspaceId, watchTarget.lastBranchName, newBranchName);
        }
      }
      this.rememberWorkspaceGitWatchFingerprint(workspaceId, nextWorkspace);

      if (!nextWorkspace) {
        subscription.lastEmittedByWorkspaceId.delete(workspaceId);
        this.bufferOrEmitWorkspaceUpdate(subscription, {
          kind: "remove",
          id: workspaceId,
        });
        continue;
      }

      const nextPayload: WorkspaceUpdatePayload = {
        kind: "upsert",
        workspace: nextWorkspace,
      };

      const lastEmitted = subscription.lastEmittedByWorkspaceId.get(workspaceId);
      if (
        lastEmitted &&
        lastEmitted.kind === "upsert" &&
        equal(lastEmitted.workspace, nextWorkspace)
      ) {
        continue;
      }

      this.bufferOrEmitWorkspaceUpdate(subscription, nextPayload);
    }

    if (!options?.skipReconcile) {
      void this.reconcileAndEmitWorkspaceUpdates();
    }
  }

  private async emitWorkspaceUpdateForCwd(
    cwd: string,
    options?: { skipReconcile?: boolean; dedupeGitState?: boolean },
  ): Promise<void> {
    const workspaces = await this.workspaceRegistry.list();
    const workspaceId = this.resolveRegisteredWorkspaceIdForCwd(cwd, workspaces);
    await this.emitWorkspaceUpdatesForWorkspaceIds([workspaceId], options);
  }

  private async emitWorkspaceUpdatesForCwds(cwds: Iterable<string>): Promise<void> {
    const workspaces = await this.workspaceRegistry.list();
    const uniqueWorkspaceIds = new Set<string>();
    for (const cwd of cwds) {
      uniqueWorkspaceIds.add(this.resolveRegisteredWorkspaceIdForCwd(cwd, workspaces));
    }
    await this.emitWorkspaceUpdatesForWorkspaceIds(uniqueWorkspaceIds);
  }

  private async handleFetchAgents(
    request: Extract<SessionInboundMessage, { type: "fetch_agents_request" }>,
  ): Promise<void> {
    const requestedSubscriptionId = request.subscribe?.subscriptionId?.trim();
    const subscriptionId = request.subscribe
      ? requestedSubscriptionId && requestedSubscriptionId.length > 0
        ? requestedSubscriptionId
        : uuidv4()
      : null;

    try {
      if (subscriptionId) {
        this.agentUpdatesSubscription = {
          subscriptionId,
          filter: request.filter,
          isBootstrapping: true,
          pendingUpdatesByAgentId: new Map(),
        };
      }

      const payload = await this.listFetchAgentsEntries(request);
      const snapshotUpdatedAtByAgentId = new Map<string, number>();
      for (const entry of payload.entries) {
        const parsedUpdatedAt = Date.parse(entry.agent.updatedAt);
        if (!Number.isNaN(parsedUpdatedAt)) {
          snapshotUpdatedAtByAgentId.set(entry.agent.id, parsedUpdatedAt);
        }
      }

      this.emit({
        type: "fetch_agents_response",
        payload: {
          requestId: request.requestId,
          ...(subscriptionId ? { subscriptionId } : {}),
          ...payload,
        },
      });

      if (subscriptionId && this.agentUpdatesSubscription?.subscriptionId === subscriptionId) {
        this.flushBootstrappedAgentUpdates({ snapshotUpdatedAtByAgentId });
      }
    } catch (error) {
      if (subscriptionId && this.agentUpdatesSubscription?.subscriptionId === subscriptionId) {
        this.agentUpdatesSubscription = null;
      }
      const code = error instanceof SessionRequestError ? error.code : "fetch_agents_failed";
      const message = error instanceof Error ? error.message : "Failed to fetch agents";
      this.sessionLogger.error({ err: error }, "Failed to handle fetch_agents_request");
      this.emit({
        type: "rpc_error",
        payload: {
          requestId: request.requestId,
          requestType: request.type,
          error: message,
          code,
        },
      });
    }
  }

  private async handleFetchWorkspacesRequest(
    request: Extract<SessionInboundMessage, { type: "fetch_workspaces_request" }>,
  ): Promise<void> {
    const requestedSubscriptionId = request.subscribe?.subscriptionId?.trim();
    const subscriptionId = request.subscribe
      ? requestedSubscriptionId && requestedSubscriptionId.length > 0
        ? requestedSubscriptionId
        : uuidv4()
      : null;

    try {
      this.sessionLogger.debug(
        {
          requestId: request.requestId,
          subscribeRequested: Boolean(request.subscribe),
          filter: request.filter ?? null,
          sort: request.sort ?? null,
          page: request.page ?? null,
        },
        "fetch_workspaces_request_received",
      );
      if (subscriptionId) {
        this.workspaceUpdatesSubscription = {
          subscriptionId,
          filter: request.filter,
          isBootstrapping: true,
          pendingUpdatesByWorkspaceId: new Map(),
          lastEmittedByWorkspaceId: new Map(),
        };
      }

      const payload = await this.listFetchWorkspacesEntries(request);
      await this.primeWorkspaceGitWatchFingerprints(payload.entries);
      this.sessionLogger.debug(
        {
          requestId: request.requestId,
          subscriptionId,
          pageInfo: payload.pageInfo,
          payload: summarizeFetchWorkspacesEntries(payload.entries),
        },
        "fetch_workspaces_response_ready",
      );
      const snapshotLatestActivityByWorkspaceId = new Map<string, number>();
      for (const entry of payload.entries) {
        const parsedLatestActivity = entry.activityAt
          ? Date.parse(entry.activityAt)
          : Number.NEGATIVE_INFINITY;
        if (!Number.isNaN(parsedLatestActivity)) {
          snapshotLatestActivityByWorkspaceId.set(entry.id, parsedLatestActivity);
        }
      }

      this.emit({
        type: "fetch_workspaces_response",
        payload: {
          requestId: request.requestId,
          ...(subscriptionId ? { subscriptionId } : {}),
          ...payload,
        },
      });

      // Push initial tab stacks state for every workspace the client just
      // learned about. From here on, mutations broadcast their own
      // `workspace_stacks_state` deltas; this is the bootstrap.
      void this.emitInitialStacksStateForAllWorkspaces();

      if (subscriptionId && this.workspaceUpdatesSubscription?.subscriptionId === subscriptionId) {
        this.flushBootstrappedWorkspaceUpdates({ snapshotLatestActivityByWorkspaceId });
        void this.reconcileAndEmitWorkspaceUpdates();
      }
    } catch (error) {
      if (subscriptionId && this.workspaceUpdatesSubscription?.subscriptionId === subscriptionId) {
        this.workspaceUpdatesSubscription = null;
      }
      const code = error instanceof SessionRequestError ? error.code : "fetch_workspaces_failed";
      const message = error instanceof Error ? error.message : "Failed to fetch workspaces";
      this.sessionLogger.error({ err: error }, "Failed to handle fetch_workspaces_request");
      this.emit({
        type: "rpc_error",
        payload: {
          requestId: request.requestId,
          requestType: request.type,
          error: message,
          code,
        },
      });
    }
  }

  private async handleOpenProjectRequest(
    request: Extract<SessionInboundMessage, { type: "open_project_request" }>,
  ): Promise<void> {
    try {
      const workspace = await this.findOrCreateWorkspaceForDirectory(request.cwd);
      await this.emitWorkspaceUpdateForCwd(workspace.cwd);
      const descriptor = await this.describeWorkspaceRecordWithGitData(workspace);
      this.emit({
        type: "open_project_response",
        payload: {
          requestId: request.requestId,
          workspace: descriptor,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to open project";
      this.sessionLogger.error({ err: error, cwd: request.cwd }, "Failed to open project");
      this.emit({
        type: "open_project_response",
        payload: {
          requestId: request.requestId,
          workspace: null,
          error: message,
        },
      });
    }
  }

  private async handleInitRepoRequest(
    request: Extract<SessionInboundMessage, { type: "init_repo_request" }>,
  ): Promise<void> {
    try {
      const cwd = normalizePersistedWorkspaceId(request.cwd);
      await initRepoWithEmptyCommit(cwd);
      // The folder may already be a registered `directory` workspace (added
      // before init) — findOrCreate reconciles it into a git workspace; for a
      // brand-new folder it registers fresh.
      const workspace = await this.findOrCreateWorkspaceForDirectory(cwd);
      await this.emitWorkspaceUpdateForCwd(workspace.cwd);
      const descriptor = await this.describeWorkspaceRecordWithGitData(workspace);
      this.emit({
        type: "init_repo_response",
        payload: { requestId: request.requestId, workspace: descriptor, error: null },
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to initialize git repository";
      this.sessionLogger.error({ err: error, cwd: request.cwd }, "Failed to init git repository");
      this.emit({
        type: "init_repo_response",
        payload: { requestId: request.requestId, workspace: null, error: message },
      });
    }
  }

  private async handleCloneRepoRequest(
    request: Extract<SessionInboundMessage, { type: "clone_repo_request" }>,
  ): Promise<void> {
    try {
      const destPath = normalizePersistedWorkspaceId(request.destPath);
      await cloneRepo(request.remoteUrl, destPath);
      const workspace = await this.findOrCreateWorkspaceForDirectory(destPath);
      await this.emitWorkspaceUpdateForCwd(workspace.cwd);
      const descriptor = await this.describeWorkspaceRecordWithGitData(workspace);
      this.emit({
        type: "clone_repo_response",
        payload: { requestId: request.requestId, workspace: descriptor, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to clone repository";
      this.sessionLogger.error(
        { err: error, destPath: request.destPath },
        "Failed to clone git repository",
      );
      this.emit({
        type: "clone_repo_response",
        payload: { requestId: request.requestId, workspace: null, error: message },
      });
    }
  }

  private buildWorkspaceScriptPayloadSnapshot(
    workspaceId: string,
    workspaceDirectory: string,
  ): WorkspaceDescriptorPayload["scripts"] {
    if (!this.scriptRouteStore || !this.scriptRuntimeStore) {
      return [];
    }
    return buildWorkspaceScriptPayloads({
      workspaceId,
      workspaceDirectory,
      routeStore: this.scriptRouteStore,
      runtimeStore: this.scriptRuntimeStore,
      daemonPort: this.getDaemonTcpPort?.() ?? null,
      resolveHealth: this.resolveScriptHealth ?? undefined,
    });
  }

  private emitWorkspaceScriptStatusUpdate(workspaceId: string, workspaceDirectory: string): void {
    this.emit({
      type: "script_status_update",
      payload: {
        workspaceId,
        scripts: this.buildWorkspaceScriptPayloadSnapshot(workspaceId, workspaceDirectory),
      },
    });
  }

  async getAvailableEditorTargets() {
    return this.filterEditorsForClient(await listAvailableEditorTargets());
  }

  async openEditorTarget(options: { editorId: EditorTargetId; path: string }): Promise<void> {
    await openInEditorTarget(options);
  }

  private async handleStartWorkspaceScriptRequest(
    request: StartWorkspaceScriptRequest,
  ): Promise<void> {
    try {
      if (!this.terminalManager || !this.scriptRouteStore || !this.scriptRuntimeStore) {
        throw new Error("Workspace scripts are not available on this daemon");
      }

      const workspace = await this.workspaceRegistry.get(request.workspaceId);
      if (!workspace) {
        throw new Error(`Workspace not found: ${request.workspaceId}`);
      }

      const serviceResult = await spawnWorkspaceScript({
        repoRoot: workspace.cwd,
        workspaceId: workspace.workspaceId,
        projectSlug: deriveProjectSlug(workspace.cwd),
        branchName: readGitCommand(workspace.cwd, "git symbolic-ref --short HEAD"),
        scriptName: request.scriptName,
        daemonPort: this.getDaemonTcpPort?.() ?? null,
        daemonListenHost: this.getDaemonTcpHost?.() ?? null,
        routeStore: this.scriptRouteStore,
        runtimeStore: this.scriptRuntimeStore,
        terminalManager: this.terminalManager,
        logger: this.sessionLogger,
        onLifecycleChanged: () => {
          this.emitWorkspaceScriptStatusUpdate(workspace.workspaceId, workspace.cwd);
        },
      });

      this.emitWorkspaceScriptStatusUpdate(workspace.workspaceId, workspace.cwd);
      this.emit({
        type: "start_workspace_script_response",
        payload: {
          requestId: request.requestId,
          workspaceId: request.workspaceId,
          scriptName: request.scriptName,
          terminalId: serviceResult.terminalId,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to start workspace script";
      this.sessionLogger.error(
        {
          err: error,
          workspaceId: request.workspaceId,
          scriptName: request.scriptName,
        },
        "Failed to start workspace script",
      );
      this.emit({
        type: "start_workspace_script_response",
        payload: {
          requestId: request.requestId,
          workspaceId: request.workspaceId,
          scriptName: request.scriptName,
          terminalId: null,
          error: message,
        },
      });
    }
  }

  private async handleListAvailableEditorsRequest(
    request: Extract<SessionInboundMessage, { type: "list_available_editors_request" }>,
  ): Promise<void> {
    try {
      const editors = await this.getAvailableEditorTargets();
      this.emit({
        type: "list_available_editors_response",
        payload: {
          requestId: request.requestId,
          editors,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to list available editors";
      this.sessionLogger.error(
        { err: error, requestType: request.type },
        "Failed to list available editors",
      );
      this.emit({
        type: "list_available_editors_response",
        payload: {
          requestId: request.requestId,
          editors: [],
          error: message,
        },
      });
    }
  }

  private async handleOpenInEditorRequest(
    request: Extract<SessionInboundMessage, { type: "open_in_editor_request" }>,
  ): Promise<void> {
    try {
      await this.openEditorTarget({ editorId: request.editorId, path: request.path });
      this.emit({
        type: "open_in_editor_response",
        payload: {
          requestId: request.requestId,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to open in editor";
      this.sessionLogger.error(
        {
          err: error,
          editorId: request.editorId,
          path: request.path,
          requestType: request.type,
        },
        "Failed to open in editor",
      );
      this.emit({
        type: "open_in_editor_response",
        payload: {
          requestId: request.requestId,
          error: message,
        },
      });
    }
  }

  private async handleCreateAppostleWorktreeRequest(
    request: Extract<SessionInboundMessage, { type: "create_appostle_worktree_request" }>,
  ): Promise<void> {
    return handleCreateWorktreeRequest(
      {
        appostleHome: this.appostleHome,
        describeWorkspaceRecord: (workspace) => this.describeWorkspaceRecordWithGitData(workspace),
        emit: (message) => this.emit(message),
        createAppostleWorktree: (input) => this.createAppostleWorktree(input),
        sessionLogger: this.sessionLogger,
        runWorktreeSetupInBackground: (options) => this.runWorktreeSetupInBackground(options),
      },
      request,
    );
  }

  private async runWorktreeSetupInBackground(options: {
    requestCwd: string;
    repoRoot: string;
    workspaceId: string;
    worktree: { branchName: string; worktreePath: string };
    shouldBootstrap: boolean;
    slug: string;
    worktreePath: string;
  }): Promise<void> {
    return runWorktreeSetupInBackgroundSession(
      {
        appostleHome: this.appostleHome,
        emitWorkspaceUpdateForCwd: (cwd, emitOptions) =>
          this.emitWorkspaceUpdateForCwd(cwd, emitOptions),
        cacheWorkspaceSetupSnapshot: (workspaceId, snapshot) => {
          this.workspaceSetupSnapshots.set(workspaceId, snapshot);
        },
        emit: (message) => this.emit(message),
        sessionLogger: this.sessionLogger,
        terminalManager: this.terminalManager,
        archiveWorkspaceRecord: (workspaceId) => this.archiveWorkspaceRecord(workspaceId),
        scriptRouteStore: this.scriptRouteStore,
        scriptRuntimeStore: this.scriptRuntimeStore,
        getDaemonTcpPort: this.getDaemonTcpPort,
        getDaemonTcpHost: this.getDaemonTcpHost,
        onScriptsChanged: (workspaceId, workspaceDirectory) => {
          this.emitWorkspaceScriptStatusUpdate(workspaceId, workspaceDirectory);
        },
      },
      options,
    );
  }

  private async handleWorkspaceSetupStatusRequest(
    request: Extract<SessionInboundMessage, { type: "workspace_setup_status_request" }>,
  ): Promise<void> {
    return handleWorkspaceSetupStatusRequestMessage(
      {
        emit: (message) => this.emit(message),
        workspaceSetupSnapshots: this.workspaceSetupSnapshots,
      },
      request,
    );
  }

  private async handleArchiveWorkspaceRequest(
    request: Extract<SessionInboundMessage, { type: "archive_workspace_request" }>,
  ): Promise<void> {
    try {
      const existing = await this.workspaceRegistry.get(request.workspaceId);
      if (!existing) {
        throw new Error(`Workspace not found: ${request.workspaceId}`);
      }
      if (existing.kind === "worktree") {
        throw new Error("Use worktree archive for Appostle worktrees");
      }
      const archivedAt = new Date().toISOString();
      await this.archiveWorkspaceRecord(existing.workspaceId, archivedAt);
      await this.emitWorkspaceUpdateForCwd(existing.cwd);
      this.emit({
        type: "archive_workspace_response",
        payload: {
          requestId: request.requestId,
          workspaceId: request.workspaceId,
          archivedAt,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to archive workspace";
      this.sessionLogger.error(
        { err: error, workspaceId: request.workspaceId },
        "Failed to archive workspace",
      );
      this.emit({
        type: "archive_workspace_response",
        payload: {
          requestId: request.requestId,
          workspaceId: request.workspaceId,
          archivedAt: null,
          error: message,
        },
      });
    }
  }

  private async handleFetchAgent(agentIdOrIdentifier: string, requestId: string): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(agentIdOrIdentifier);
    if (!resolved.ok) {
      this.emit({
        type: "fetch_agent_response",
        payload: { requestId, agent: null, project: null, error: resolved.error },
      });
      return;
    }

    const agent = await this.getAgentPayloadById(resolved.agentId);
    if (!agent) {
      this.emit({
        type: "fetch_agent_response",
        payload: {
          requestId,
          agent: null,
          project: null,
          error: `Agent not found: ${resolved.agentId}`,
        },
      });
      return;
    }

    const project = await this.buildProjectPlacementForCwd(agent.cwd);
    this.emit({
      type: "fetch_agent_response",
      payload: { requestId, agent, project, error: null },
    });
  }

  private async handleFetchAgentTimelineRequest(
    msg: Extract<SessionInboundMessage, { type: "fetch_agent_timeline_request" }>,
  ): Promise<void> {
    const direction: AgentTimelineFetchDirection = msg.direction ?? (msg.cursor ? "after" : "tail");
    const projection: TimelineProjectionMode = msg.projection ?? "projected";
    const requestedLimit = msg.limit;
    const limit = requestedLimit ?? (direction === "after" ? 0 : undefined);
    const shouldLimitByProjectedWindow =
      projection === "canonical" &&
      direction === "tail" &&
      typeof requestedLimit === "number" &&
      requestedLimit > 0;
    const cursor: AgentTimelineCursor | undefined = msg.cursor
      ? {
          epoch: msg.cursor.epoch,
          seq: msg.cursor.seq,
        }
      : undefined;

    try {
      const snapshot = await ensureAgentLoaded(msg.agentId, {
        agentManager: this.agentManager,
        agentStorage: this.agentStorage,
        logger: this.sessionLogger,
      });
      const agentPayload = await this.buildAgentPayload(snapshot);

      let timeline = this.agentManager.fetchTimeline(msg.agentId, {
        direction,
        cursor,
        limit:
          shouldLimitByProjectedWindow && typeof requestedLimit === "number"
            ? Math.max(1, Math.floor(requestedLimit))
            : limit,
      });
      let hasOlder = timeline.hasOlder;
      let hasNewer = timeline.hasNewer;
      let startCursor: { epoch: string; seq: number } | null = null;
      let endCursor: { epoch: string; seq: number } | null = null;
      let entries: ReturnType<typeof projectTimelineRows>;

      if (shouldLimitByProjectedWindow) {
        const projectedLimit = Math.max(1, Math.floor(requestedLimit));
        let fetchLimit = projectedLimit;
        let projectedWindow = selectTimelineWindowByProjectedLimit({
          rows: timeline.rows,
          provider: snapshot.provider,
          direction,
          limit: projectedLimit,
          collapseToolLifecycle: false,
        });

        while (timeline.hasOlder) {
          const needsMoreProjectedEntries =
            projectedWindow.projectedEntries.length < projectedLimit;
          const firstLoadedRow = timeline.rows[0];
          const firstSelectedRow = projectedWindow.selectedRows[0];
          const startsAtLoadedBoundary =
            firstLoadedRow != null &&
            firstSelectedRow != null &&
            firstSelectedRow.seq === firstLoadedRow.seq;
          const boundaryIsAssistantChunk =
            startsAtLoadedBoundary && firstLoadedRow.item.type === "assistant_message";

          if (!needsMoreProjectedEntries && !boundaryIsAssistantChunk) {
            break;
          }

          const maxRows = Math.max(0, timeline.window.maxSeq - timeline.window.minSeq + 1);
          const nextFetchLimit = Math.min(maxRows, fetchLimit * 2);
          if (nextFetchLimit <= fetchLimit) {
            break;
          }

          fetchLimit = nextFetchLimit;
          timeline = this.agentManager.fetchTimeline(msg.agentId, {
            direction,
            cursor,
            limit: fetchLimit,
          });
          projectedWindow = selectTimelineWindowByProjectedLimit({
            rows: timeline.rows,
            provider: snapshot.provider,
            direction,
            limit: projectedLimit,
            collapseToolLifecycle: false,
          });
        }

        const selectedRows = projectedWindow.selectedRows;

        entries = projectTimelineRows(selectedRows, snapshot.provider, projection);

        if (projectedWindow.minSeq !== null && projectedWindow.maxSeq !== null) {
          startCursor = { epoch: timeline.epoch, seq: projectedWindow.minSeq };
          endCursor = { epoch: timeline.epoch, seq: projectedWindow.maxSeq };
          hasOlder = projectedWindow.minSeq > timeline.window.minSeq;
          hasNewer = false;
        }
      } else {
        const firstRow = timeline.rows[0];
        const lastRow = timeline.rows[timeline.rows.length - 1];
        startCursor = firstRow ? { epoch: timeline.epoch, seq: firstRow.seq } : null;
        endCursor = lastRow ? { epoch: timeline.epoch, seq: lastRow.seq } : null;
        entries = projectTimelineRows(timeline.rows, snapshot.provider, projection);
      }

      this.emit({
        type: "fetch_agent_timeline_response",
        payload: {
          requestId: msg.requestId,
          agentId: msg.agentId,
          agent: agentPayload,
          direction,
          projection,
          epoch: timeline.epoch,
          reset: timeline.reset,
          staleCursor: timeline.staleCursor,
          gap: timeline.gap,
          window: timeline.window,
          startCursor,
          endCursor,
          hasOlder,
          hasNewer,
          entries,
          error: null,
        },
      });
    } catch (error) {
      this.sessionLogger.error(
        { err: error, agentId: msg.agentId },
        "Failed to handle fetch_agent_timeline_request",
      );
      this.emit({
        type: "fetch_agent_timeline_response",
        payload: {
          requestId: msg.requestId,
          agentId: msg.agentId,
          agent: null,
          direction,
          projection,
          epoch: "",
          reset: false,
          staleCursor: false,
          gap: false,
          window: { minSeq: 0, maxSeq: 0, nextSeq: 0 },
          startCursor: null,
          endCursor: null,
          hasOlder: false,
          hasNewer: false,
          entries: [],
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  private async handleSendAgentMessageRequest(
    msg: Extract<SessionInboundMessage, { type: "send_agent_message_request" }>,
  ): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(msg.agentId);
    if (!resolved.ok) {
      this.emit({
        type: "send_agent_message_response",
        payload: {
          requestId: msg.requestId,
          agentId: msg.agentId,
          accepted: false,
          error: resolved.error,
        },
      });
      return;
    }

    try {
      const agentId = resolved.agentId;

      // Persist file + image uploads (if any) so the prompt footer can
      // reference file paths and so any client can later fetch image bytes
      // by id. Mirrors `handleSendAgentMessage` — kept in sync so the two
      // send paths persist attachments identically.
      let persistedFiles: SessionUpload[] = [];
      let persistedImages: SessionImage[] = [];
      const hasFiles = (msg.files?.length ?? 0) > 0;
      const hasImages = (msg.images?.length ?? 0) > 0;
      if (hasFiles || hasImages) {
        const agent = this.agentManager.getAgentSnapshot(agentId);
        if (!agent) {
          this.emit({
            type: "send_agent_message_response",
            payload: {
              requestId: msg.requestId,
              agentId,
              accepted: false,
              error: `Agent ${agentId} not found; cannot persist attachments`,
            },
          });
          return;
        }
        try {
          if (hasFiles && msg.files) {
            persistedFiles = await this.uploadStore.persistFiles({
              cwd: agent.cwd,
              agentId,
              messageId: msg.messageId ?? null,
              files: msg.files,
            });
          }
          if (hasImages && msg.images) {
            persistedImages = await this.imageStore.persistImages({
              cwd: agent.cwd,
              agentId,
              messageId: msg.messageId ?? null,
              images: msg.images,
            });
          }
        } catch (error) {
          const message =
            error instanceof UploadSizeError
              ? `Attachments exceed the ${MAX_UPLOAD_BYTES_PER_MESSAGE} byte limit per message`
              : error instanceof Error
                ? `Failed to persist attachments: ${error.message}`
                : "Failed to persist attachments";
          this.emit({
            type: "send_agent_message_response",
            payload: {
              requestId: msg.requestId,
              agentId,
              accepted: false,
              error: message,
            },
          });
          return;
        }
      }

      // See `handleSendAgentMessage` — prompt image bytes come from disk
      // after persistence; the wire payload is just the upload conduit.
      const promptImages =
        persistedImages.length > 0
          ? await this.loadPromptImagesFromDisk(persistedImages, agentId)
          : msg.images;
      const prompt = this.buildAgentPrompt(msg.text, promptImages, msg.attachments, persistedFiles);
      // See `handleSendAgentMessage` for why an empty user-message text needs
      // a synthesized fallback when files were uploaded.
      const recordedText = synthesizeRecordedUserText(
        msg.text,
        persistedFiles,
        msg.images?.length ?? 0,
      );

      // Bytes-stripped chip metadata for reload re-render. Same shape as
      // `handleSendAgentMessage` — kept in sync so both send-paths persist
      // chips identically. `daemonRef` lets any client fetch bytes by id
      // via `fetch_attachment_bytes_request`.
      const userMessageImages = buildImageEcho(msg.images ?? [], persistedImages);
      const userMessageFiles = persistedFiles.map((f) => ({
        id: f.id,
        fileName: f.fileName,
        mimeType: f.mimeType,
        size: f.size,
        daemonRef: {
          kind: "file" as const,
          attachmentId: f.id,
          relativePath: f.relativePath,
        },
      }));

      this.sessionLogger.trace(
        {
          agentId,
          messageId: msg.messageId,
          textPrefix: msg.text.slice(0, 80),
          fileCount: persistedFiles.length,
          echoImageCount: userMessageImages.length,
        },
        "send_agent_message_request: dispatching shared sendPromptToAgent",
      );
      const recordedTextIsSynthesized =
        msg.text.trim().length === 0 &&
        (userMessageImages.length > 0 || userMessageFiles.length > 0);
      try {
        await sendPromptToAgent({
          agentManager: this.agentManager,
          agentStorage: this.agentStorage,
          agentId,
          userMessageText: recordedText,
          prompt,
          messageId: msg.messageId,
          ...(userMessageImages.length > 0 ? { userMessageImages } : {}),
          ...(userMessageFiles.length > 0 ? { userMessageFiles } : {}),
          ...(recordedTextIsSynthesized ? { userMessageTextIsSynthesized: true } : {}),
          logger: this.sessionLogger,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        this.handleAgentRunError(agentId, error, "Failed to send agent message");
        this.emit({
          type: "send_agent_message_response",
          payload: {
            requestId: msg.requestId,
            agentId,
            accepted: false,
            error: message,
          },
        });
        return;
      }

      const startAbort = new AbortController();
      const startTimeoutMs = 15_000;
      const startTimeout = setTimeout(() => startAbort.abort("timeout"), startTimeoutMs);
      try {
        await this.agentManager.waitForAgentRunStart(agentId, { signal: startAbort.signal });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "Unknown error";
        this.emit({
          type: "send_agent_message_response",
          payload: {
            requestId: msg.requestId,
            agentId,
            accepted: false,
            error: message,
          },
        });
        return;
      } finally {
        clearTimeout(startTimeout);
      }

      this.emit({
        type: "send_agent_message_response",
        payload: {
          requestId: msg.requestId,
          agentId,
          accepted: true,
          error: null,
        },
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unknown error";
      this.emit({
        type: "send_agent_message_response",
        payload: {
          requestId: msg.requestId,
          agentId: resolved.agentId,
          accepted: false,
          error: message,
        },
      });
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Session uploads — backs the in-app "Files" tab inside an active session.
  // Both handlers resolve `agentId` against the agent registry first so we
  // get the same accept-prefix-or-title behaviour as send_agent_message, and
  // both fail closed on a missing agent (no implicit "list everything" that
  // could leak across sessions).
  // ──────────────────────────────────────────────────────────────────────

  private async handleListSessionUploadsRequest(
    msg: Extract<SessionInboundMessage, { type: "list_session_uploads_request" }>,
  ): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(msg.agentId);
    if (!resolved.ok) {
      this.emit({
        type: "list_session_uploads_response",
        payload: { requestId: msg.requestId, ok: false, error: resolved.error },
      });
      return;
    }
    const agent = this.agentManager.getAgentSnapshot(resolved.agentId);
    if (!agent) {
      this.emit({
        type: "list_session_uploads_response",
        payload: { requestId: msg.requestId, ok: false, error: "Agent not found" },
      });
      return;
    }
    try {
      const uploads = await this.uploadStore.listUploads({
        cwd: agent.cwd,
        agentId: resolved.agentId,
      });
      this.emit({
        type: "list_session_uploads_response",
        payload: { requestId: msg.requestId, ok: true, uploads },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "list_session_uploads_response",
        payload: { requestId: msg.requestId, ok: false, error: message },
      });
    }
  }

  private async handleDeleteSessionUploadRequest(
    msg: Extract<SessionInboundMessage, { type: "delete_session_upload_request" }>,
  ): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(msg.agentId);
    if (!resolved.ok) {
      this.emit({
        type: "delete_session_upload_response",
        payload: { requestId: msg.requestId, ok: false, error: resolved.error },
      });
      return;
    }
    const agent = this.agentManager.getAgentSnapshot(resolved.agentId);
    if (!agent) {
      this.emit({
        type: "delete_session_upload_response",
        payload: { requestId: msg.requestId, ok: false, error: "Agent not found" },
      });
      return;
    }
    try {
      const result = await this.uploadStore.deleteUpload({
        cwd: agent.cwd,
        agentId: resolved.agentId,
        uploadId: msg.uploadId,
      });
      if (!result.deleted) {
        this.emit({
          type: "delete_session_upload_response",
          payload: { requestId: msg.requestId, ok: false, error: "Upload not found" },
        });
        return;
      }
      this.emit({
        type: "delete_session_upload_response",
        payload: { requestId: msg.requestId, ok: true },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "delete_session_upload_response",
        payload: { requestId: msg.requestId, ok: false, error: message },
      });
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Session images — backs the "Images" tab inside the uploads modal. Same
  // shape as the file handlers above; the store has no manifest, so listing
  // is a directory scan and delete is `unlink` (traversal-guarded).
  // ──────────────────────────────────────────────────────────────────────

  private async handleListSessionImagesRequest(
    msg: Extract<SessionInboundMessage, { type: "list_session_images_request" }>,
  ): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(msg.agentId);
    if (!resolved.ok) {
      this.emit({
        type: "list_session_images_response",
        payload: { requestId: msg.requestId, ok: false, error: resolved.error },
      });
      return;
    }
    const agent = this.agentManager.getAgentSnapshot(resolved.agentId);
    if (!agent) {
      this.emit({
        type: "list_session_images_response",
        payload: { requestId: msg.requestId, ok: false, error: "Agent not found" },
      });
      return;
    }
    try {
      const images = await this.imageStore.listImages({ cwd: agent.cwd });
      this.emit({
        type: "list_session_images_response",
        payload: { requestId: msg.requestId, ok: true, images },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "list_session_images_response",
        payload: { requestId: msg.requestId, ok: false, error: message },
      });
    }
  }

  private async handleDeleteSessionImageRequest(
    msg: Extract<SessionInboundMessage, { type: "delete_session_image_request" }>,
  ): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(msg.agentId);
    if (!resolved.ok) {
      this.emit({
        type: "delete_session_image_response",
        payload: { requestId: msg.requestId, ok: false, error: resolved.error },
      });
      return;
    }
    const agent = this.agentManager.getAgentSnapshot(resolved.agentId);
    if (!agent) {
      this.emit({
        type: "delete_session_image_response",
        payload: { requestId: msg.requestId, ok: false, error: "Agent not found" },
      });
      return;
    }
    try {
      const result = await this.imageStore.deleteImage({
        cwd: agent.cwd,
        relativePath: msg.relativePath,
      });
      if (!result.deleted) {
        this.emit({
          type: "delete_session_image_response",
          payload: { requestId: msg.requestId, ok: false, error: "Image not found" },
        });
        return;
      }
      this.emit({
        type: "delete_session_image_response",
        payload: { requestId: msg.requestId, ok: true },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "delete_session_image_response",
        payload: { requestId: msg.requestId, ok: false, error: message },
      });
    }
  }

  /**
   * Serve attachment bytes by id. The daemon is the canonical source — every
   * paired client (Electron after Cmd-R, phone, web) hits this RPC to render
   * thumbnails or download files. Bytes never round-trip through the chat
   * timeline; they're fetched on demand.
   *
   * `relativePath` comes from the user_message timeline echo's `daemonRef`;
   * the store's read methods traversal-guard it so it can't escape the
   * dedicated subdirectories.
   */
  private async handleFetchAttachmentBytesRequest(
    msg: Extract<SessionInboundMessage, { type: "fetch_attachment_bytes_request" }>,
  ): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(msg.agentId);
    if (!resolved.ok) {
      this.emit({
        type: "fetch_attachment_bytes_response",
        payload: { requestId: msg.requestId, ok: false, error: resolved.error },
      });
      return;
    }
    const agent = this.agentManager.getAgentSnapshot(resolved.agentId);
    if (!agent) {
      this.emit({
        type: "fetch_attachment_bytes_response",
        payload: { requestId: msg.requestId, ok: false, error: "Agent not found" },
      });
      return;
    }
    try {
      if (msg.kind === "image") {
        const { data, mimeType, byteSize } = await this.imageStore.readImageBytes({
          cwd: agent.cwd,
          relativePath: msg.relativePath,
        });
        this.emit({
          type: "fetch_attachment_bytes_response",
          payload: {
            requestId: msg.requestId,
            ok: true,
            attachmentId: msg.attachmentId,
            kind: "image",
            data: data.toString("base64"),
            mimeType,
            byteSize,
          },
        });
      } else {
        const { data, mimeType, byteSize, fileName } = await this.uploadStore.readUploadBytes({
          cwd: agent.cwd,
          relativePath: msg.relativePath,
        });
        this.emit({
          type: "fetch_attachment_bytes_response",
          payload: {
            requestId: msg.requestId,
            ok: true,
            attachmentId: msg.attachmentId,
            kind: "file",
            data: data.toString("base64"),
            mimeType,
            byteSize,
            fileName,
          },
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.warn(
        { agentId: resolved.agentId, attachmentId: msg.attachmentId, kind: msg.kind, err: error },
        "fetch_attachment_bytes_request: failed to read",
      );
      this.emit({
        type: "fetch_attachment_bytes_response",
        payload: { requestId: msg.requestId, ok: false, error: message },
      });
    }
  }

  private async handlePlanUsageRequest(
    msg: Extract<SessionInboundMessage, { type: "plan_usage_request" }>,
  ): Promise<void> {
    const probe = getClaudeUsageProbe();
    if (!probe) {
      this.emit({
        type: "plan_usage_response",
        payload: { requestId: msg.requestId, snapshot: null, error: "probe_unavailable" },
      });
      return;
    }
    try {
      const snapshot = msg.refresh
        ? await probe.triggerProbe()
        : (probe.getLastSnapshot() ?? (await probe.triggerProbe()));
      this.emit({
        type: "plan_usage_response",
        payload: {
          requestId: msg.requestId,
          snapshot: snapshot
            ? {
                provider: "claude",
                buckets: snapshot.rateLimits,
                capturedAt: snapshot.capturedAt,
              }
            : null,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.warn({ err: error }, "plan_usage_request: probe failed");
      this.emit({
        type: "plan_usage_response",
        payload: { requestId: msg.requestId, snapshot: null, error: message },
      });
    }
  }

  private async handleWaitForFinish(
    agentIdOrIdentifier: string,
    requestId: string,
    timeoutMs?: number,
  ): Promise<void> {
    const resolved = await this.resolveAgentIdentifier(agentIdOrIdentifier);
    if (!resolved.ok) {
      this.emit({
        type: "wait_for_finish_response",
        payload: {
          requestId,
          status: "error",
          final: null,
          error: resolved.error,
          lastMessage: null,
        },
      });
      return;
    }

    const agentId = resolved.agentId;
    const live = this.agentManager.getAgentSnapshot(agentId);
    if (!live) {
      const record = await this.agentStorage.get(agentId);
      if (!record || record.internal) {
        this.emit({
          type: "wait_for_finish_response",
          payload: {
            requestId,
            status: "error",
            final: null,
            error: `Agent not found: ${agentId}`,
            lastMessage: null,
          },
        });
        return;
      }
      const final = this.buildStoredAgentPayload(record);
      const status =
        record.attentionReason === "permission"
          ? "permission"
          : record.lastStatus === "error"
            ? "error"
            : "idle";
      const error = resolveWaitForFinishError({ status, final });
      this.emit({
        type: "wait_for_finish_response",
        payload: { requestId, status, final, error, lastMessage: null },
      });
      return;
    }

    const abortController = new AbortController();
    const hasTimeout = typeof timeoutMs === "number" && timeoutMs > 0;
    const timeoutHandle = hasTimeout
      ? setTimeout(() => {
          abortController.abort("timeout");
        }, timeoutMs)
      : null;

    try {
      let result = await this.agentManager.waitForAgentEvent(agentId, {
        signal: abortController.signal,
        waitForActive: true,
      });
      let final = await this.getAgentPayloadById(agentId);
      if (!final) {
        throw new Error(`Agent ${agentId} disappeared while waiting`);
      }

      let status: "permission" | "error" | "idle" = result.permission
        ? "permission"
        : result.status === "error"
          ? "error"
          : "idle";
      const error = resolveWaitForFinishError({ status, final });

      this.emit({
        type: "wait_for_finish_response",
        payload: { requestId, status, final, error, lastMessage: result.lastMessage },
      });
    } catch (error) {
      const isAbort =
        error instanceof Error &&
        (error.name === "AbortError" || error.message.toLowerCase().includes("aborted"));
      if (!isAbort) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "Unknown error";
        this.sessionLogger.error({ err: error, agentId }, "wait_for_finish_request failed");
        const final = await this.getAgentPayloadById(agentId);
        this.emit({
          type: "wait_for_finish_response",
          payload: {
            requestId,
            status: "error",
            final,
            error: message,
            lastMessage: null,
          },
        });
        return;
      }

      const final = await this.getAgentPayloadById(agentId);
      if (!final) {
        throw new Error(`Agent ${agentId} disappeared while waiting`);
      }
      this.emit({
        type: "wait_for_finish_response",
        payload: { requestId, status: "timeout", final, error: null, lastMessage: null },
      });
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    }
  }

  /**
   * Handle audio chunk for buffering and transcription
   */
  private async handleAudioChunk(
    msg: Extract<SessionInboundMessage, { type: "voice_audio_chunk" }>,
  ): Promise<void> {
    if (!this.isVoiceMode) {
      this.sessionLogger.warn(
        "Received voice_audio_chunk while voice mode is disabled; transcript will be emitted but voice assistant turn is skipped",
      );
    }

    const chunkFormat = msg.format || "audio/wav";

    if (this.isVoiceMode) {
      if (!this.voiceTurnController) {
        throw new Error("Voice mode is enabled but the voice turn controller is not running");
      }
      const chunkBytes = Buffer.byteLength(msg.audio, "base64");
      this.voiceInputChunkCount += 1;
      this.voiceInputBytes += chunkBytes;
      if (this.voiceInputChunkCount === 1) {
        this.sessionLogger.info(
          {
            format: chunkFormat,
            audioBytes: chunkBytes,
          },
          "Received first voice_audio_chunk for active voice mode",
        );
      }
      const now = Date.now();
      if (this.voiceInputChunkCount % 50 === 0 || now - this.voiceInputWindowStartedAt >= 1000) {
        this.sessionLogger.info(
          {
            chunkCount: this.voiceInputChunkCount,
            audioBytes: this.voiceInputBytes,
            windowMs: now - this.voiceInputWindowStartedAt,
            format: chunkFormat,
          },
          "Voice input chunk summary",
        );
        this.voiceInputWindowStartedAt = now;
        this.voiceInputChunkCount = 0;
        this.voiceInputBytes = 0;
      }
      await this.voiceTurnController.appendClientChunk({
        audioBase64: msg.audio,
        format: chunkFormat,
      });
      return;
    }

    const chunkBuffer = Buffer.from(msg.audio, "base64");
    const isPCMChunk = chunkFormat.toLowerCase().includes("pcm");

    if (!this.audioBuffer) {
      this.audioBuffer = {
        chunks: [],
        format: chunkFormat,
        isPCM: isPCMChunk,
        totalPCMBytes: 0,
      };
    }

    // If the format changes mid-stream, flush what we have first
    if (this.audioBuffer.isPCM !== isPCMChunk) {
      this.sessionLogger.debug(
        {
          oldFormat: this.audioBuffer.isPCM ? "pcm" : this.audioBuffer.format,
          newFormat: chunkFormat,
        },
        `Audio format changed mid-stream, flushing current buffer`,
      );
      const finalized = this.finalizeBufferedAudio();
      if (finalized) {
        await this.processCompletedAudio(finalized.audio, finalized.format);
      }
      this.audioBuffer = {
        chunks: [],
        format: chunkFormat,
        isPCM: isPCMChunk,
        totalPCMBytes: 0,
      };
    } else if (!this.audioBuffer.isPCM) {
      // Keep latest format info for non-PCM blobs
      this.audioBuffer.format = chunkFormat;
    }

    this.audioBuffer.chunks.push(chunkBuffer);
    if (this.audioBuffer.isPCM) {
      this.audioBuffer.totalPCMBytes += chunkBuffer.length;
    }

    // In non-voice mode, use streaming threshold to process chunks
    const reachedStreamingThreshold =
      !this.isVoiceMode &&
      this.audioBuffer.isPCM &&
      this.audioBuffer.totalPCMBytes >= MIN_STREAMING_SEGMENT_BYTES;

    if (!msg.isLast && reachedStreamingThreshold) {
      return;
    }

    const bufferedState = this.audioBuffer;
    const finalized = this.finalizeBufferedAudio();
    if (!finalized) {
      return;
    }

    if (!msg.isLast && reachedStreamingThreshold) {
      this.sessionLogger.debug(
        {
          minDuration: MIN_STREAMING_SEGMENT_DURATION_MS,
          pcmBytes: bufferedState?.totalPCMBytes ?? 0,
        },
        `Minimum chunk duration reached (~${MIN_STREAMING_SEGMENT_DURATION_MS}ms, ${
          bufferedState?.totalPCMBytes ?? 0
        } PCM bytes) – triggering STT`,
      );
    } else {
      this.sessionLogger.debug(
        { audioBytes: finalized.audio.length, chunks: bufferedState?.chunks.length ?? 0 },
        `Complete audio segment (${finalized.audio.length} bytes, ${bufferedState?.chunks.length ?? 0} chunk(s))`,
      );
    }

    await this.processCompletedAudio(finalized.audio, finalized.format);
  }

  private finalizeBufferedAudio(): { audio: Buffer; format: string } | null {
    if (!this.audioBuffer) {
      return null;
    }

    const bufferState = this.audioBuffer;
    this.audioBuffer = null;

    if (bufferState.isPCM) {
      const pcmBuffer = Buffer.concat(bufferState.chunks);
      const wavBuffer = convertPCMToWavBuffer(
        pcmBuffer,
        PCM_SAMPLE_RATE,
        PCM_CHANNELS,
        PCM_BITS_PER_SAMPLE,
      );
      return {
        audio: wavBuffer,
        format: "audio/wav",
      };
    }

    return {
      audio: Buffer.concat(bufferState.chunks),
      format: bufferState.format,
    };
  }

  private async processCompletedAudio(audio: Buffer, format: string): Promise<void> {
    if (this.processingPhase === "transcribing") {
      this.sessionLogger.debug(
        { phase: this.processingPhase, segmentCount: this.pendingAudioSegments.length + 1 },
        `Buffering audio segment (phase: ${this.processingPhase})`,
      );
      this.pendingAudioSegments.push({
        audio,
        format,
      });
      this.setBufferTimeout();
      return;
    }

    if (this.pendingAudioSegments.length > 0) {
      this.pendingAudioSegments.push({
        audio,
        format,
      });
      this.sessionLogger.debug(
        { segmentCount: this.pendingAudioSegments.length },
        `Processing ${this.pendingAudioSegments.length} buffered segments together`,
      );

      const pendingSegments = [...this.pendingAudioSegments];
      this.pendingAudioSegments = [];
      this.clearBufferTimeout();

      const combinedAudio = Buffer.concat(pendingSegments.map((segment) => segment.audio));
      const combinedFormat = pendingSegments[pendingSegments.length - 1].format;

      await this.processAudio(combinedAudio, combinedFormat);
      return;
    }

    await this.processAudio(audio, format);
  }

  private async flushPendingAudioSegments(reason: string): Promise<void> {
    if (this.processingPhase === "transcribing" || this.pendingAudioSegments.length === 0) {
      return;
    }

    const pendingSegments = [...this.pendingAudioSegments];
    this.pendingAudioSegments = [];
    this.clearBufferTimeout();

    this.sessionLogger.debug(
      { reason, segmentCount: pendingSegments.length },
      `Flushing ${pendingSegments.length} buffered audio segment(s)`,
    );

    const combinedAudio = Buffer.concat(pendingSegments.map((segment) => segment.audio));
    const combinedFormat = pendingSegments[pendingSegments.length - 1]!.format;

    await this.processAudio(combinedAudio, combinedFormat);
  }

  /**
   * Process audio through STT and then LLM
   */
  private async processAudio(audio: Buffer, format: string): Promise<void> {
    this.setPhase("transcribing");

    this.emit({
      type: "activity_log",
      payload: {
        id: uuidv4(),
        timestamp: new Date(),
        type: "system",
        content: "Transcribing audio...",
      },
    });

    try {
      const requestId = uuidv4();
      const result = await this.sttManager.transcribe(audio, format, {
        requestId,
        label: this.isVoiceMode ? "voice" : "buffered",
      });

      const transcriptText = result.text.trim();
      this.sessionLogger.info(
        {
          requestId,
          isVoiceMode: this.isVoiceMode,
          transcriptLength: transcriptText.length,
          transcript: transcriptText,
        },
        "Transcription result",
      );

      await this.handleTranscriptionResultPayload({
        text: result.text,
        language: result.language,
        duration: result.duration,
        requestId,
        avgLogprob: result.avgLogprob,
        isLowConfidence: result.isLowConfidence,
        byteLength: result.byteLength,
        format: result.format,
        debugRecordingPath: result.debugRecordingPath,
      });
    } catch (error: any) {
      this.setPhase("idle");
      this.clearSpeechInProgress("transcription error");
      await this.flushPendingAudioSegments("transcription error");
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "error",
          content: `Transcription error: ${error.message}`,
        },
      });
      throw error;
    }
  }

  private async handleTranscriptionResultPayload(
    result: VoiceTranscriptionResultPayload,
  ): Promise<void> {
    const transcriptText = result.text.trim();

    this.emit({
      type: "transcription_result",
      payload: {
        text: result.text,
        ...(result.language ? { language: result.language } : {}),
        ...(result.duration !== undefined ? { duration: result.duration } : {}),
        requestId: result.requestId,
        ...(result.avgLogprob !== undefined ? { avgLogprob: result.avgLogprob } : {}),
        ...(result.isLowConfidence !== undefined
          ? { isLowConfidence: result.isLowConfidence }
          : {}),
        ...(result.byteLength !== undefined ? { byteLength: result.byteLength } : {}),
        ...(result.format ? { format: result.format } : {}),
        ...(result.debugRecordingPath ? { debugRecordingPath: result.debugRecordingPath } : {}),
      },
    });

    if (!transcriptText) {
      this.sessionLogger.debug("Empty transcription (false positive), not aborting");
      this.setPhase("idle");
      this.clearSpeechInProgress("empty transcription");
      await this.flushPendingAudioSegments("empty transcription");
      return;
    }

    // Has content - abort any in-progress stream now
    this.createAbortController();

    if (result.debugRecordingPath) {
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "system",
          content: `Saved input audio: ${result.debugRecordingPath}`,
          metadata: {
            recordingPath: result.debugRecordingPath,
            ...(result.format ? { format: result.format } : {}),
            requestId: result.requestId,
          },
        },
      });
    }

    this.emit({
      type: "activity_log",
      payload: {
        id: uuidv4(),
        timestamp: new Date(),
        type: "transcript",
        content: result.text,
        metadata: {
          ...(result.language ? { language: result.language } : {}),
          ...(result.duration !== undefined ? { duration: result.duration } : {}),
        },
      },
    });

    this.clearSpeechInProgress("transcription complete");
    this.setPhase("idle");
    if (!this.isVoiceMode) {
      this.sessionLogger.debug(
        { requestId: result.requestId },
        "Skipping voice agent processing because voice mode is disabled",
      );
      await this.flushPendingAudioSegments("voice mode disabled");
      return;
    }

    const agentId = this.voiceModeAgentId;
    if (!agentId) {
      this.sessionLogger.warn(
        { requestId: result.requestId },
        "Skipping voice agent processing because no agent is currently voice-enabled",
      );
      await this.flushPendingAudioSegments("no active voice agent");
      return;
    }

    await this.handleSendAgentMessage(
      agentId,
      result.text,
      undefined,
      undefined,
      undefined,
      undefined,
      { spokenInput: true },
    );
    await this.flushPendingAudioSegments("transcription complete");
  }

  private registerVoiceBridgeForAgent(agentId: string): void {
    this.registerVoiceSpeakHandler?.(agentId, async ({ text, signal }) => {
      this.sessionLogger.info(
        {
          agentId,
          textLength: text.length,
          preview: text.slice(0, 160),
        },
        "Voice speak tool call received by session handler",
      );
      const abortSignal = signal ?? this.abortController.signal;
      await this.ttsManager.generateAndWaitForPlayback(
        text,
        (msg) => this.emit(msg),
        abortSignal,
        true,
      );
      this.sessionLogger.info(
        { agentId, textLength: text.length },
        "Voice speak tool call finished playback",
      );
      this.emit({
        type: "activity_log",
        payload: {
          id: uuidv4(),
          timestamp: new Date(),
          type: "assistant",
          content: text,
        },
      });
    });

    this.registerVoiceCallerContext?.(agentId, {
      childAgentDefaultLabels: {},
      allowCustomCwd: false,
      enableVoiceTools: true,
    });
  }

  /**
   * Handle abort request from client
   */
  private async handleAbort(): Promise<void> {
    this.sessionLogger.info(
      { phase: this.processingPhase },
      `Abort request, phase: ${this.processingPhase}`,
    );

    this.abortController.abort();
    this.ttsManager.cancelPendingPlaybacks("abort request");

    // Voice abort should always interrupt active agent output immediately.
    if (this.isVoiceMode && this.voiceModeAgentId) {
      try {
        await this.interruptAgentIfRunning(this.voiceModeAgentId);
      } catch (error) {
        this.sessionLogger.warn(
          { err: error, agentId: this.voiceModeAgentId },
          "Failed to interrupt active voice-mode agent on abort",
        );
      }
    }

    if (this.processingPhase === "transcribing") {
      // Still in STT phase - we'll buffer the next audio
      this.sessionLogger.debug("Will buffer next audio (currently transcribing)");
      // Phase stays as 'transcribing', handleAudioChunk will handle buffering
      return;
    }

    // Reset phase to idle and clear pending non-voice buffers.
    this.setPhase("idle");
    this.pendingAudioSegments = [];
    this.clearBufferTimeout();
  }

  /**
   * Handle audio playback confirmation from client
   */
  private handleAudioPlayed(id: string): void {
    this.ttsManager.confirmAudioPlayed(id);
  }

  /**
   * Mark speech detection start and abort any active playback/agent run.
   */
  private async handleVoiceSpeechStart(): Promise<void> {
    if (this.speechInProgress) {
      return;
    }

    const chunkReceivedAt = Date.now();
    const phaseBeforeAbort = this.processingPhase;
    const hadActiveStream = this.hasActiveAgentRun(this.voiceModeAgentId);

    this.speechInProgress = true;
    this.sessionLogger.debug("Voice speech detected – aborting playback and active agent run");

    if (this.pendingAudioSegments.length > 0) {
      this.sessionLogger.debug(
        { segmentCount: this.pendingAudioSegments.length },
        `Dropping ${this.pendingAudioSegments.length} buffered audio segment(s) due to voice speech`,
      );
      this.pendingAudioSegments = [];
    }

    if (this.audioBuffer) {
      this.sessionLogger.debug(
        { chunks: this.audioBuffer.chunks.length, pcmBytes: this.audioBuffer.totalPCMBytes },
        `Clearing partial audio buffer (${this.audioBuffer.chunks.length} chunk(s)${
          this.audioBuffer.isPCM ? `, ${this.audioBuffer.totalPCMBytes} PCM bytes` : ""
        })`,
      );
      this.audioBuffer = null;
    }

    this.clearBufferTimeout();

    this.abortController.abort();
    await this.handleAbort();

    const latencyMs = Date.now() - chunkReceivedAt;
    this.sessionLogger.debug(
      { latencyMs, phaseBeforeAbort, hadActiveStream },
      "[Telemetry] barge_in.llm_abort_latency",
    );
  }

  /**
   * Clear speech-in-progress flag once the user turn has completed
   */
  private clearSpeechInProgress(reason: string): void {
    this.clearPendingVoiceSpeechStart(`clear-speech-in-progress:${reason}`);
    if (!this.speechInProgress) {
      return;
    }

    this.speechInProgress = false;
    this.sessionLogger.debug({ reason }, `Speech turn complete (${reason}) – resuming TTS`);
  }

  /**
   * Create new AbortController, aborting the previous one
   */
  private createAbortController(): AbortController {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.ttsDebugStreams.clear();
    return this.abortController;
  }

  /**
   * Set the processing phase
   */
  private setPhase(phase: ProcessingPhase): void {
    this.processingPhase = phase;
    this.sessionLogger.debug({ phase }, `Phase: ${phase}`);
  }

  /**
   * Set timeout to process buffered audio segments
   */
  private setBufferTimeout(): void {
    this.clearBufferTimeout();

    this.bufferTimeout = setTimeout(async () => {
      this.sessionLogger.debug("Buffer timeout reached, processing pending segments");

      if (this.processingPhase === "transcribing") {
        this.sessionLogger.debug(
          { segmentCount: this.pendingAudioSegments.length },
          "Buffer timeout deferred because transcription is still in progress",
        );
        this.setBufferTimeout();
        return;
      }

      if (this.pendingAudioSegments.length > 0) {
        const segments = [...this.pendingAudioSegments];
        this.pendingAudioSegments = [];
        this.bufferTimeout = null;

        const combined = Buffer.concat(segments.map((s) => s.audio));
        await this.processAudio(combined, segments[0].format);
      }
    }, 10000); // 10 second timeout
  }

  /**
   * Clear buffer timeout
   */
  private clearBufferTimeout(): void {
    if (this.bufferTimeout) {
      clearTimeout(this.bufferTimeout);
      this.bufferTimeout = null;
    }
  }

  /**
   * Emit a message to the client
   */
  private emit(msg: SessionOutboundMessage): void {
    this.sessionLogger.trace(
      { messageType: msg.type, payloadBytes: JSON.stringify(msg).length },
      "outbound message",
    );
    if (
      msg.type === "audio_output" &&
      (process.env.TTS_DEBUG_AUDIO_DIR || isAppostleDictationDebugEnabled()) &&
      msg.payload.groupId &&
      typeof msg.payload.audio === "string"
    ) {
      const groupId = msg.payload.groupId;
      const existing =
        this.ttsDebugStreams.get(groupId) ??
        ({ format: msg.payload.format, chunks: [] } satisfies {
          format: string;
          chunks: Buffer[];
        });

      try {
        existing.chunks.push(Buffer.from(msg.payload.audio, "base64"));
        existing.format = msg.payload.format;
        this.ttsDebugStreams.set(groupId, existing);
      } catch {
        // ignore malformed base64
      }

      if (msg.payload.isLastChunk) {
        const final = this.ttsDebugStreams.get(groupId);
        this.ttsDebugStreams.delete(groupId);
        if (final && final.chunks.length > 0) {
          void (async () => {
            const recordingPath = await maybePersistTtsDebugAudio(
              Buffer.concat(final.chunks),
              { sessionId: this.sessionId, groupId, format: final.format },
              this.sessionLogger,
            );
            if (recordingPath) {
              this.onMessage({
                type: "activity_log",
                payload: {
                  id: uuidv4(),
                  timestamp: new Date(),
                  type: "system",
                  content: `Saved TTS audio: ${recordingPath}`,
                  metadata: { recordingPath, format: final.format, groupId },
                },
              });
            }
          })();
        }
      }
    }
    this.onMessage(msg);
  }

  private emitBinary(frame: Uint8Array): void {
    if (!this.onBinaryMessage) {
      return;
    }
    try {
      this.onBinaryMessage(frame);
    } catch (error) {
      this.sessionLogger.error({ err: error }, "Failed to emit binary frame");
    }
  }

  /**
   * Clean up session resources
   */
  public async cleanup(): Promise<void> {
    this.sessionLogger.trace("Cleaning up");

    if (this.unsubscribeAgentEvents) {
      this.unsubscribeAgentEvents();
      this.unsubscribeAgentEvents = null;
    }
    if (this.unsubscribeProviderSnapshotEvents) {
      this.unsubscribeProviderSnapshotEvents();
      this.unsubscribeProviderSnapshotEvents = null;
    }

    // Abort any ongoing operations
    this.abortController.abort();

    // Clear timeouts
    this.clearBufferTimeout();

    // Clear buffers
    this.pendingAudioSegments = [];
    this.audioBuffer = null;
    await this.stopVoiceTurnController();

    // Cleanup managers
    this.ttsManager.cleanup();
    this.sttManager.cleanup();
    this.dictationStreamManager.cleanupAll();

    // Close MCP clients
    if (this.agentMcpClient) {
      try {
        await this.agentMcpClient.close();
      } catch (error) {
        this.sessionLogger.error({ err: error }, "Failed to close Agent MCP client");
      }
      this.agentMcpClient = null;
      this.agentTools = null;
    }

    await this.disableVoiceModeForActiveAgent(true);
    this.isVoiceMode = false;

    // Unsubscribe from all terminals
    if (this.unsubscribeTerminalsChanged) {
      this.unsubscribeTerminalsChanged();
      this.unsubscribeTerminalsChanged = null;
    }
    this.subscribedTerminalDirectories.clear();

    for (const unsubscribeExit of this.terminalExitSubscriptions.values()) {
      unsubscribeExit();
    }
    this.terminalExitSubscriptions.clear();
    this.disposeTerminalSubscriptions();

    for (const unsubscribe of this.checkoutDiffSubscriptions.values()) {
      unsubscribe();
    }
    this.checkoutDiffSubscriptions.clear();

    for (const unsubscribe of this.workspaceGitSubscriptions.values()) {
      unsubscribe();
    }
    this.workspaceGitSubscriptions.clear();

    this.subscribedTodoWorkspaceIds.clear();
  }

  // ----------------------------------------------------------------------------
  // Terminal Handlers
  // ----------------------------------------------------------------------------

  private ensureTerminalExitSubscription(terminal: TerminalSession): void {
    if (this.terminalExitSubscriptions.has(terminal.id)) {
      return;
    }

    const unsubscribeExit = terminal.onExit(() => {
      this.handleTerminalExited(terminal.id);
    });
    this.terminalExitSubscriptions.set(terminal.id, unsubscribeExit);
  }

  private handleTerminalExited(terminalId: string): void {
    const unsubscribeExit = this.terminalExitSubscriptions.get(terminalId);
    if (unsubscribeExit) {
      unsubscribeExit();
      this.terminalExitSubscriptions.delete(terminalId);
    }

    this.detachTerminalStream(terminalId, { emitExit: true });
  }

  private emitChatRpcError(request: { requestId: string; type: string }, error: unknown): void {
    const message = error instanceof Error ? error.message : "Chat request failed";
    const code = error instanceof ChatServiceError ? error.code : "chat_request_failed";
    this.sessionLogger.error({ err: error, requestType: request.type }, "Chat request failed");
    this.emit({
      type: "rpc_error",
      payload: {
        requestId: request.requestId,
        requestType: request.type,
        error: message,
        code,
      },
    });
  }

  private async handleChatCreateRequest(
    request: Extract<SessionInboundMessage, { type: "chat/create" }>,
  ): Promise<void> {
    try {
      const room = await this.chatService.createRoom({
        name: request.name,
        purpose: request.purpose,
        ownerUserId: this.ownerUserId,
      });
      this.emit({
        type: "chat/create/response",
        payload: {
          requestId: request.requestId,
          room,
          error: null,
        },
      });
    } catch (error) {
      this.emitChatRpcError(request, error);
    }
  }

  private async handleChatListRequest(
    request: Extract<SessionInboundMessage, { type: "chat/list" }>,
  ): Promise<void> {
    try {
      const rooms = await this.chatService.listRooms(this.ownerUserId);
      this.emit({
        type: "chat/list/response",
        payload: {
          requestId: request.requestId,
          rooms,
          error: null,
        },
      });
    } catch (error) {
      this.emitChatRpcError(request, error);
    }
  }

  private async handleChatInspectRequest(
    request: Extract<SessionInboundMessage, { type: "chat/inspect" }>,
  ): Promise<void> {
    try {
      const result = await this.chatService.inspectRoom({
        room: request.room,
        requesterUserId: this.ownerUserId,
      });
      this.emit({
        type: "chat/inspect/response",
        payload: {
          requestId: request.requestId,
          room: result.room,
          error: null,
        },
      });
    } catch (error) {
      this.emitChatRpcError(request, error);
    }
  }

  private async handleChatDeleteRequest(
    request: Extract<SessionInboundMessage, { type: "chat/delete" }>,
  ): Promise<void> {
    try {
      const result = await this.chatService.deleteRoom({
        room: request.room,
        requesterUserId: this.ownerUserId,
      });
      this.emit({
        type: "chat/delete/response",
        payload: {
          requestId: request.requestId,
          room: result.room,
          error: null,
        },
      });
    } catch (error) {
      this.emitChatRpcError(request, error);
    }
  }

  private async handleChatPostRequest(
    request: Extract<SessionInboundMessage, { type: "chat/post" }>,
  ): Promise<void> {
    try {
      const authorAgentId = request.authorAgentId?.trim() || this.clientId;
      const message = await this.chatService.postMessage({
        room: request.room,
        authorAgentId,
        body: request.body,
        replyToMessageId: request.replyToMessageId,
        requesterUserId: this.ownerUserId,
      });
      this.emit({
        type: "chat/post/response",
        payload: {
          requestId: request.requestId,
          message,
          error: null,
        },
      });
      void notifyChatMentions({
        room: request.room,
        authorAgentId,
        body: request.body,
        mentionAgentIds: message.mentionAgentIds,
        logger: this.sessionLogger,
        listStoredAgents: () => this.agentStorage.list(),
        listLiveAgents: () => this.agentManager.listAgents(),
        resolveAgentIdentifier: (identifier) => this.resolveAgentIdentifier(identifier),
        sendAgentMessage: async (agentId, text) => {
          await this.handleSendAgentMessage(agentId, text);
        },
      });
    } catch (error) {
      this.emitChatRpcError(request, error);
    }
  }

  private async handleChatReadRequest(
    request: Extract<SessionInboundMessage, { type: "chat/read" }>,
  ): Promise<void> {
    try {
      const messages = await this.chatService.readMessages({
        room: request.room,
        limit: request.limit,
        since: request.since,
        authorAgentId: request.authorAgentId,
        requesterUserId: this.ownerUserId,
      });
      this.emit({
        type: "chat/read/response",
        payload: {
          requestId: request.requestId,
          messages,
          error: null,
        },
      });
    } catch (error) {
      this.emitChatRpcError(request, error);
    }
  }

  private async handleChatWaitRequest(
    request: Extract<SessionInboundMessage, { type: "chat/wait" }>,
  ): Promise<void> {
    try {
      const messages = await this.chatService.waitForMessages({
        room: request.room,
        afterMessageId: request.afterMessageId,
        timeoutMs: request.timeoutMs,
        requesterUserId: this.ownerUserId,
      });
      this.emit({
        type: "chat/wait/response",
        payload: {
          requestId: request.requestId,
          messages,
          timedOut: messages.length === 0,
          error: null,
        },
      });
    } catch (error) {
      this.emitChatRpcError(request, error);
    }
  }

  private describePreviewError(error: unknown): { message: string; code: string } {
    if (error instanceof PreviewServiceError) {
      return { message: error.message, code: error.code };
    }
    return {
      message: error instanceof Error ? error.message : "Preview request failed",
      code: "preview_failed",
    };
  }

  private emitPreviewRpcError(request: { requestId: string; type: string }, error: unknown): void {
    const { message, code } = this.describePreviewError(error);
    this.sessionLogger.error({ err: error, requestType: request.type }, "Preview request failed");
    this.emit({
      type: "rpc_error",
      payload: {
        requestId: request.requestId,
        requestType: request.type,
        error: message,
        code,
      },
    });
  }

  private async handlePreviewOpenRequest(
    request: Extract<SessionInboundMessage, { type: "preview/open" }>,
  ): Promise<void> {
    try {
      const { sessionId, frame } = await this.previewService.open({
        url: request.url,
        ...(typeof request.width === "number" ? { width: request.width } : {}),
        ...(typeof request.height === "number" ? { height: request.height } : {}),
        onFrame: (f) => {
          this.emit({
            type: "preview/frame_push",
            payload: {
              sessionId,
              data: f.data,
              width: f.width,
              height: f.height,
              scrollX: f.scrollX,
              scrollY: f.scrollY,
            },
          });
        },
      });
      this.emit({
        type: "preview/open/response",
        payload: { requestId: request.requestId, sessionId, frame, error: null },
      });
    } catch (error) {
      const { message } = this.describePreviewError(error);
      this.emit({
        type: "preview/open/response",
        payload: { requestId: request.requestId, sessionId: null, frame: null, error: message },
      });
    }
  }

  private async handlePreviewInputRequest(
    request: Extract<SessionInboundMessage, { type: "preview/input" }>,
  ): Promise<void> {
    try {
      await this.previewService.input(request.sessionId, {
        action: request.action,
        ...(typeof request.x === "number" ? { x: request.x } : {}),
        ...(typeof request.y === "number" ? { y: request.y } : {}),
        ...(typeof request.dx === "number" ? { dx: request.dx } : {}),
        ...(typeof request.dy === "number" ? { dy: request.dy } : {}),
        ...(request.key ? { key: request.key } : {}),
        ...(request.text ? { text: request.text } : {}),
      });
    } catch (error) {
      // Input is fire-and-forget; log but never surface a per-event error.
      this.sessionLogger.debug({ err: error }, "Preview input dispatch failed");
    }
  }

  private async handlePreviewResizeRequest(
    request: Extract<SessionInboundMessage, { type: "preview/resize" }>,
  ): Promise<void> {
    try {
      await this.previewService.resize(request.sessionId, request.width, request.height);
      this.emit({
        type: "preview/resize/response",
        payload: {
          requestId: request.requestId,
          width: request.width,
          height: request.height,
          error: null,
        },
      });
    } catch (error) {
      const { message } = this.describePreviewError(error);
      this.emit({
        type: "preview/resize/response",
        payload: {
          requestId: request.requestId,
          width: request.width,
          height: request.height,
          error: message,
        },
      });
    }
  }

  private async handlePreviewFrameRequest(
    request: Extract<SessionInboundMessage, { type: "preview/frame" }>,
  ): Promise<void> {
    try {
      const frame = await this.previewService.frame(request.sessionId);
      this.emit({
        type: "preview/frame/response",
        payload: { requestId: request.requestId, frame, error: null },
      });
    } catch (error) {
      const { message } = this.describePreviewError(error);
      this.emit({
        type: "preview/frame/response",
        payload: { requestId: request.requestId, frame: null, error: message },
      });
    }
  }

  private async handlePreviewScrollRequest(
    request: Extract<SessionInboundMessage, { type: "preview/scroll" }>,
  ): Promise<void> {
    try {
      const frame = await this.previewService.scroll(request.sessionId, request.dx, request.dy);
      this.emit({
        type: "preview/scroll/response",
        payload: { requestId: request.requestId, frame, error: null },
      });
    } catch (error) {
      const { message } = this.describePreviewError(error);
      this.emit({
        type: "preview/scroll/response",
        payload: { requestId: request.requestId, frame: null, error: message },
      });
    }
  }

  private async handlePreviewPickRequest(
    request: Extract<SessionInboundMessage, { type: "preview/pick" }>,
  ): Promise<void> {
    try {
      const element = await this.previewService.pick(request.sessionId, request.x, request.y);
      this.emit({
        type: "preview/pick/response",
        payload: { requestId: request.requestId, element, error: null },
      });
    } catch (error) {
      const { message } = this.describePreviewError(error);
      this.emit({
        type: "preview/pick/response",
        payload: { requestId: request.requestId, element: null, error: message },
      });
    }
  }

  private async handlePreviewCloseRequest(
    request: Extract<SessionInboundMessage, { type: "preview/close" }>,
  ): Promise<void> {
    try {
      await this.previewService.close(request.sessionId);
      this.emit({
        type: "preview/close/response",
        payload: { requestId: request.requestId, error: null },
      });
    } catch (error) {
      this.emitPreviewRpcError(request, error);
    }
  }

  private toScheduleSummary(
    schedule: Awaited<ReturnType<ScheduleService["inspect"]>>,
  ): Extract<
    SessionOutboundMessage,
    { type: "schedule/list/response" }
  >["payload"]["schedules"][number] {
    const { runs: _runs, ...summary } = schedule;
    return summary;
  }

  private emitScheduleRpcError(
    request: Extract<
      SessionInboundMessage,
      {
        type:
          | "schedule/create"
          | "schedule/list"
          | "schedule/inspect"
          | "schedule/logs"
          | "schedule/pause"
          | "schedule/resume"
          | "schedule/delete";
      }
    >,
    error: unknown,
  ): void {
    const message = error instanceof Error ? error.message : String(error);
    this.sessionLogger.error({ err: error, requestType: request.type }, "Schedule request failed");
    this.emit({
      type: "rpc_error",
      payload: {
        requestId: request.requestId,
        requestType: request.type,
        error: message,
        code: "schedule_request_failed",
      },
    });
  }

  private async handleScheduleCreateRequest(
    request: Extract<SessionInboundMessage, { type: "schedule/create" }>,
  ): Promise<void> {
    try {
      const target =
        request.target.type === "self"
          ? { type: "agent" as const, agentId: request.target.agentId }
          : request.target;
      const schedule = await this.scheduleService.create({
        prompt: request.prompt,
        name: request.name,
        cadence: request.cadence,
        target,
        maxRuns: request.maxRuns,
        expiresAt: request.expiresAt,
        // Stamp the schedule with the creating session's authoritative
        // user-id so every subsequent RPC (list / inspect / logs / pause /
        // resume / delete) is gated to the owner. Without this, any
        // co-tenant on a shared host could see and control the job. Null
        // on single-tenant daemons (no auth-server linkage) — schedule
        // stays globally visible, matching legacy behavior.
        ownerUserId: this.ownerUserId,
        ownerUsername: this.username ?? null,
      });
      this.emit({
        type: "schedule/create/response",
        payload: {
          requestId: request.requestId,
          schedule: this.toScheduleSummary(schedule),
          error: null,
        },
      });
    } catch (error) {
      this.emitScheduleRpcError(request, error);
    }
  }

  private async handleScheduleListRequest(
    request: Extract<SessionInboundMessage, { type: "schedule/list" }>,
  ): Promise<void> {
    try {
      // Per-user ACL: pass the session's authoritative userId so the
      // service filters out other tenants' schedules. Null on single-
      // tenant daemons — service returns everything (legacy behavior).
      const schedules = await this.scheduleService.list(this.ownerUserId);
      this.emit({
        type: "schedule/list/response",
        payload: {
          requestId: request.requestId,
          schedules: schedules.map((schedule) => this.toScheduleSummary(schedule)),
          error: null,
        },
      });
    } catch (error) {
      this.emitScheduleRpcError(request, error);
    }
  }

  private async handleScheduleInspectRequest(
    request: Extract<SessionInboundMessage, { type: "schedule/inspect" }>,
  ): Promise<void> {
    try {
      const schedule = await this.scheduleService.inspect(request.scheduleId, this.ownerUserId);
      this.emit({
        type: "schedule/inspect/response",
        payload: {
          requestId: request.requestId,
          schedule,
          error: null,
        },
      });
    } catch (error) {
      this.emitScheduleRpcError(request, error);
    }
  }

  private async handleScheduleLogsRequest(
    request: Extract<SessionInboundMessage, { type: "schedule/logs" }>,
  ): Promise<void> {
    try {
      const runs = await this.scheduleService.logs(request.scheduleId, this.ownerUserId);
      this.emit({
        type: "schedule/logs/response",
        payload: {
          requestId: request.requestId,
          runs,
          error: null,
        },
      });
    } catch (error) {
      this.emitScheduleRpcError(request, error);
    }
  }

  private async handleSchedulePauseRequest(
    request: Extract<SessionInboundMessage, { type: "schedule/pause" }>,
  ): Promise<void> {
    try {
      const schedule = await this.scheduleService.pause(request.scheduleId, this.ownerUserId);
      this.emit({
        type: "schedule/pause/response",
        payload: {
          requestId: request.requestId,
          schedule: this.toScheduleSummary(schedule),
          error: null,
        },
      });
    } catch (error) {
      this.emitScheduleRpcError(request, error);
    }
  }

  private async handleScheduleResumeRequest(
    request: Extract<SessionInboundMessage, { type: "schedule/resume" }>,
  ): Promise<void> {
    try {
      const schedule = await this.scheduleService.resume(request.scheduleId, this.ownerUserId);
      this.emit({
        type: "schedule/resume/response",
        payload: {
          requestId: request.requestId,
          schedule: this.toScheduleSummary(schedule),
          error: null,
        },
      });
    } catch (error) {
      this.emitScheduleRpcError(request, error);
    }
  }

  private async handleScheduleDeleteRequest(
    request: Extract<SessionInboundMessage, { type: "schedule/delete" }>,
  ): Promise<void> {
    try {
      await this.scheduleService.delete(request.scheduleId, this.ownerUserId);
      this.emit({
        type: "schedule/delete/response",
        payload: {
          requestId: request.requestId,
          scheduleId: request.scheduleId,
          error: null,
        },
      });
    } catch (error) {
      this.emitScheduleRpcError(request, error);
    }
  }

  private emitLoopRpcError(
    request: Extract<
      SessionInboundMessage,
      {
        type: "loop/run" | "loop/list" | "loop/inspect" | "loop/logs" | "loop/stop";
      }
    >,
    error: unknown,
  ): void {
    const message = error instanceof Error ? error.message : String(error);
    this.sessionLogger.error({ err: error, requestType: request.type }, "Loop request failed");
    this.emit({
      type: "rpc_error",
      payload: {
        requestId: request.requestId,
        requestType: request.type,
        error: message,
        code: "loop_request_failed",
      },
    });
  }

  private async handleLoopRunRequest(
    request: Extract<SessionInboundMessage, { type: "loop/run" }>,
  ): Promise<void> {
    try {
      const loop = await this.loopService.runLoop({
        prompt: request.prompt,
        cwd: request.cwd,
        provider: request.provider,
        model: request.model,
        workerProvider: request.workerProvider,
        workerModel: request.workerModel,
        verifierProvider: request.verifierProvider,
        verifierModel: request.verifierModel,
        verifyPrompt: request.verifyPrompt,
        verifyChecks: request.verifyChecks,
        archive: request.archive,
        name: request.name,
        sleepMs: request.sleepMs,
        maxIterations: request.maxIterations,
        maxTimeMs: request.maxTimeMs,
        // Stamp the session's owner identity onto the loop so its internal
        // worker/verifier agents inherit the same scoping — otherwise they
        // leak into every other user's sidebar on a shared host.
        ownerUserId: this.ownerUserId,
        ownerUsername: this.username,
      });
      this.emit({
        type: "loop/run/response",
        payload: {
          requestId: request.requestId,
          loop,
          error: null,
        },
      });
    } catch (error) {
      this.emitLoopRpcError(request, error);
    }
  }

  private async handleLoopListRequest(
    request: Extract<SessionInboundMessage, { type: "loop/list" }>,
  ): Promise<void> {
    try {
      const loops = await this.loopService.listLoops(this.ownerUserId);
      this.emit({
        type: "loop/list/response",
        payload: {
          requestId: request.requestId,
          loops,
          error: null,
        },
      });
    } catch (error) {
      this.emitLoopRpcError(request, error);
    }
  }

  private async handleLoopInspectRequest(
    request: Extract<SessionInboundMessage, { type: "loop/inspect" }>,
  ): Promise<void> {
    try {
      const loop = await this.loopService.inspectLoop(request.id, this.ownerUserId);
      this.emit({
        type: "loop/inspect/response",
        payload: {
          requestId: request.requestId,
          loop,
          error: null,
        },
      });
    } catch (error) {
      this.emitLoopRpcError(request, error);
    }
  }

  private async handleLoopLogsRequest(
    request: Extract<SessionInboundMessage, { type: "loop/logs" }>,
  ): Promise<void> {
    try {
      const result = await this.loopService.getLoopLogs(
        request.id,
        this.ownerUserId,
        request.afterSeq ?? 0,
      );
      this.emit({
        type: "loop/logs/response",
        payload: {
          requestId: request.requestId,
          loop: result.loop,
          entries: result.entries,
          nextCursor: result.nextCursor,
          error: null,
        },
      });
    } catch (error) {
      this.emitLoopRpcError(request, error);
    }
  }

  private async handleLoopStopRequest(
    request: Extract<SessionInboundMessage, { type: "loop/stop" }>,
  ): Promise<void> {
    try {
      const loop = await this.loopService.stopLoop(request.id, this.ownerUserId);
      this.emit({
        type: "loop/stop/response",
        payload: {
          requestId: request.requestId,
          loop,
          error: null,
        },
      });
    } catch (error) {
      this.emitLoopRpcError(request, error);
    }
  }

  private emitQuestRpcError(request: { requestId: string; type: string }, error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    const responseType = `${request.type}/response` as
      | "quest/run/response"
      | "quest/list/response"
      | "quest/inspect/response"
      | "quest/stop/response"
      | "roles/list/response";
    if (responseType === "quest/list/response") {
      this.emit({
        type: responseType,
        payload: { requestId: request.requestId, quests: [], error: message },
      });
      return;
    }
    if (responseType === "roles/list/response") {
      this.emit({
        type: responseType,
        payload: { requestId: request.requestId, roles: [], error: message },
      });
      return;
    }
    this.emit({
      type: responseType,
      payload: { requestId: request.requestId, quest: null, error: message },
    });
  }

  private async handleQuestRunRequest(
    request: Extract<SessionInboundMessage, { type: "quest/run" }>,
  ): Promise<void> {
    try {
      const quest = await this.questService.run({
        kind: request.kind,
        cwd: request.cwd,
        prompt: request.prompt,
        defaultProvider: request.defaultProvider,
        defaultModel: request.defaultModel ?? null,
        termination: request.termination,
        cards: request.cards,
        summarizerEnabled: request.summarizerEnabled,
        // Stamp the session's owner identity onto the quest so every child
        // agent the runner spawns (cards, summarizer, queen, queen-spawned
        // workers) inherits the same scoping — otherwise they leak into
        // every other user's sidebar on a shared host.
        ownerUserId: this.ownerUserId,
        ownerUsername: this.username,
      });
      this.emit({
        type: "quest/run/response",
        payload: { requestId: request.requestId, quest, error: null },
      });
    } catch (error) {
      this.emitQuestRpcError(request, error);
    }
  }

  private async handleQuestListRequest(
    request: Extract<SessionInboundMessage, { type: "quest/list" }>,
  ): Promise<void> {
    try {
      const quests = this.questService.list(this.ownerUserId).map((w) => ({
        id: w.id,
        kind: w.kind,
        status: w.status,
        cwd: w.cwd,
        prompt: w.prompt,
        name: w.name,
        createdAt: w.createdAt,
        updatedAt: w.updatedAt,
      }));
      this.emit({
        type: "quest/list/response",
        payload: { requestId: request.requestId, quests, error: null },
      });
    } catch (error) {
      this.emitQuestRpcError(request, error);
    }
  }

  private async handleQuestInspectRequest(
    request: Extract<SessionInboundMessage, { type: "quest/inspect" }>,
  ): Promise<void> {
    try {
      const quest = this.questService.inspect(request.id, this.ownerUserId);
      this.emit({
        type: "quest/inspect/response",
        payload: { requestId: request.requestId, quest, error: null },
      });
    } catch (error) {
      this.emitQuestRpcError(request, error);
    }
  }

  private async handleQuestStopRequest(
    request: Extract<SessionInboundMessage, { type: "quest/stop" }>,
  ): Promise<void> {
    try {
      const quest = await this.questService.stop(request.id, this.ownerUserId);
      this.emit({
        type: "quest/stop/response",
        payload: { requestId: request.requestId, quest, error: null },
      });
    } catch (error) {
      this.emitQuestRpcError(request, error);
    }
  }

  private async handleRolesListRequest(
    request: Extract<SessionInboundMessage, { type: "roles/list" }>,
  ): Promise<void> {
    try {
      const roles = await listRoles({ workspaceRoot: request.workspaceRoot });
      this.emit({
        type: "roles/list/response",
        payload: { requestId: request.requestId, roles, error: null },
      });
    } catch (error) {
      this.emitQuestRpcError(request, error);
    }
  }

  private async handleRolesListScopeRequest(
    request: Extract<SessionInboundMessage, { type: "roles/list-scope" }>,
  ): Promise<void> {
    const { scope, workspaceRoot, requestId } = request;
    try {
      const roles = await listRoles({
        scope,
        ...(workspaceRoot ? { workspaceRoot: expandTilde(workspaceRoot) } : {}),
      });
      this.emit({
        type: "roles/list-scope/response",
        payload: { requestId, roles, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, scope }, "Failed to list roles by scope");
      this.emit({
        type: "roles/list-scope/response",
        payload: { requestId, roles: [], error: message },
      });
    }
  }

  private async handleRoleCreateRequest(
    request: Extract<SessionInboundMessage, { type: "roles/create" }>,
  ): Promise<void> {
    const { scope, name, category, workspaceRoot, requestId } = request;
    try {
      const result = await createRole({
        scope,
        name,
        ...(category ? { category } : {}),
        ...(workspaceRoot ? { workspaceRoot: expandTilde(workspaceRoot) } : {}),
      });
      this.emit({
        type: "roles/create/response",
        payload: { requestId, path: result.path, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, scope, name }, "Failed to create role");
      this.emit({
        type: "roles/create/response",
        payload: { requestId, path: "", error: message },
      });
    }
  }

  private async handleRoleWriteFrontmatterRequest(
    request: Extract<SessionInboundMessage, { type: "roles/write-frontmatter" }>,
  ): Promise<void> {
    const { path: rolePath, workspaceRoot, frontmatter, requestId } = request;
    try {
      await writeRoleFrontmatter(
        { path: rolePath, frontmatter },
        workspaceRoot ? expandTilde(workspaceRoot) : undefined,
      );
      this.emit({
        type: "roles/write-frontmatter/response",
        payload: { requestId, path: rolePath, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, path: rolePath }, "Failed to write role frontmatter");
      this.emit({
        type: "roles/write-frontmatter/response",
        payload: { requestId, path: rolePath, error: message },
      });
    }
  }

  private async handleRoleMoveRequest(
    request: Extract<SessionInboundMessage, { type: "roles/move" }>,
  ): Promise<void> {
    const { path: rolePath, newName, newCategory, workspaceRoot, requestId } = request;
    try {
      const result = await moveRole(
        { path: rolePath, newName, newCategory },
        workspaceRoot ? expandTilde(workspaceRoot) : undefined,
      );
      this.emit({
        type: "roles/move/response",
        payload: { requestId, path: result.path, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, path: rolePath }, "Failed to move role");
      this.emit({
        type: "roles/move/response",
        payload: { requestId, path: rolePath, error: message },
      });
    }
  }

  private async handleBrandsListScopeRequest(
    request: Extract<SessionInboundMessage, { type: "brands/list-scope" }>,
  ): Promise<void> {
    const { workspaceRoot, requestId } = request;
    try {
      const brands = await listBrands({
        ...(workspaceRoot ? { workspaceRoot: expandTilde(workspaceRoot) } : {}),
      });
      this.emit({
        type: "brands/list-scope/response",
        payload: { requestId, brands, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error }, "Failed to list brands");
      this.emit({
        type: "brands/list-scope/response",
        payload: { requestId, brands: [], error: message },
      });
    }
  }

  private async handleBrandCreateRequest(
    request: Extract<SessionInboundMessage, { type: "brands/create" }>,
  ): Promise<void> {
    const { name, workspaceRoot, requestId } = request;
    try {
      const result = await createBrand({
        name,
        ...(workspaceRoot ? { workspaceRoot: expandTilde(workspaceRoot) } : {}),
      });
      this.emit({
        type: "brands/create/response",
        payload: { requestId, path: result.path, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, name }, "Failed to create brand");
      this.emit({
        type: "brands/create/response",
        payload: { requestId, path: "", error: message },
      });
    }
  }

  private async handleBrandWriteFrontmatterRequest(
    request: Extract<SessionInboundMessage, { type: "brands/write-frontmatter" }>,
  ): Promise<void> {
    const { path: brandPath, workspaceRoot, frontmatter, requestId } = request;
    try {
      const expandedRoot = workspaceRoot ? expandTilde(workspaceRoot) : undefined;
      if (expandedRoot && !brandPath.endsWith(".md") && (await tokensJsonExists(expandedRoot))) {
        // tokens.json-backed brand — write through the inverse adapter
        await writeBrandTokens(frontmatter, expandedRoot);
      } else {
        // .md-backed brand (prose or legacy) — preserve existing YAML frontmatter behavior
        await writeBrandFrontmatter({ path: brandPath, frontmatter }, expandedRoot);
      }
      this.emit({
        type: "brands/write-frontmatter/response",
        payload: { requestId, path: brandPath, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, path: brandPath },
        "Failed to write brand frontmatter",
      );
      this.emit({
        type: "brands/write-frontmatter/response",
        payload: { requestId, path: brandPath, error: message },
      });
    }
  }

  private async handleBrandAssetCopyRequest(
    request: Extract<SessionInboundMessage, { type: "brands/asset-copy" }>,
  ): Promise<void> {
    const { workspaceRoot, sourcePath, targetName, category, derive, requestId } = request;
    try {
      const result = await copyBrandAsset({
        workspaceRoot: expandTilde(workspaceRoot),
        sourcePath: expandTilde(sourcePath),
        targetName,
        ...(category ? { category } : {}),
        ...(derive && derive.length > 0 ? { derive } : {}),
      });
      this.emit({
        type: "brands/asset-copy/response",
        payload: {
          requestId,
          relativePath: result.relativePath,
          absolutePath: result.absolutePath,
          derived: result.derived,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, sourcePath, targetName },
        "Failed to copy brand asset",
      );
      this.emit({
        type: "brands/asset-copy/response",
        payload: { requestId, relativePath: "", absolutePath: "", derived: [], error: message },
      });
    }
  }

  private async handleBrandAssetUploadRequest(
    request: Extract<SessionInboundMessage, { type: "brands/asset-upload" }>,
  ): Promise<void> {
    const { workspaceRoot, targetName, sourceName, dataBase64, category, derive, requestId } =
      request;
    try {
      const result = await uploadBrandAsset({
        workspaceRoot: expandTilde(workspaceRoot),
        targetName,
        sourceName,
        dataBase64,
        ...(category ? { category } : {}),
        ...(derive && derive.length > 0 ? { derive } : {}),
      });
      this.emit({
        type: "brands/asset-upload/response",
        payload: {
          requestId,
          relativePath: result.relativePath,
          absolutePath: result.absolutePath,
          derived: result.derived,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, targetName, sourceName },
        "Failed to upload brand asset",
      );
      this.emit({
        type: "brands/asset-upload/response",
        payload: { requestId, relativePath: "", absolutePath: "", derived: [], error: message },
      });
    }
  }

  private async handleBrandAssetDeleteRequest(
    request: Extract<SessionInboundMessage, { type: "brands/asset-delete" }>,
  ): Promise<void> {
    const { workspaceRoot, relativePath, requestId } = request;
    try {
      await deleteBrandAsset({
        workspaceRoot: expandTilde(workspaceRoot),
        relativePath,
      });
      this.emit({
        type: "brands/asset-delete/response",
        payload: { requestId, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, relativePath }, "Failed to delete brand asset");
      this.emit({
        type: "brands/asset-delete/response",
        payload: { requestId, error: message },
      });
    }
  }

  private async handleBrandAssetRenameRequest(
    request: Extract<SessionInboundMessage, { type: "brands/asset-rename" }>,
  ): Promise<void> {
    const { workspaceRoot, fromRelativePath, toTargetName, requestId } = request;
    try {
      const result = await renameBrandAsset({
        workspaceRoot: expandTilde(workspaceRoot),
        fromRelativePath,
        toTargetName,
      });
      this.emit({
        type: "brands/asset-rename/response",
        payload: {
          requestId,
          relativePath: result.relativePath,
          absolutePath: result.absolutePath,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error(
        { err: error, fromRelativePath, toTargetName },
        "Failed to rename brand asset",
      );
      this.emit({
        type: "brands/asset-rename/response",
        payload: { requestId, relativePath: "", absolutePath: "", error: message },
      });
    }
  }

  private async handleBrandGenerateTokensRequest(
    request: Extract<SessionInboundMessage, { type: "brands/generate-tokens" }>,
  ): Promise<void> {
    const { requestId, workspaceRoot, brandPath, mode, prompt } = request;
    try {
      const result = await generateAndApplyBrandTokens({
        agentManager: this.agentManager,
        workspaceRoot: expandTilde(workspaceRoot),
        brandPath: expandTilde(brandPath),
        mode,
        ...(prompt ? { prompt } : {}),
        logger: this.sessionLogger,
        ownerUserId: this.ownerUserId,
      });
      this.emit({
        type: "brands/generate-tokens/response",
        payload: {
          requestId,
          generatedCount: result.generatedCount,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, brandPath, mode }, "Failed to generate brand tokens");
      this.emit({
        type: "brands/generate-tokens/response",
        payload: {
          requestId,
          generatedCount: 0,
          error: message,
        },
      });
    }
  }

  /**
   * Probe for a still-running brand-token internal agent. Cheap synchronous
   * lookup — no agent spawn, no LLM call. The modal polls this on mount so a
   * close/reopen cycle can resume the "thinking" UI instead of showing idle
   * while the daemon is mid-generate.
   *
   * Identifier is (title, cwd, ownerUserId). brandPath is accepted in the
   * request for forward-compat but isn't on the agent record (the generator
   * spawns at workspace cwd; only one brand-token job per workspace makes
   * sense in practice).
   */
  private async handleBrandTokenGenerationStatusRequest(
    request: Extract<SessionInboundMessage, { type: "brands/token-generation-status" }>,
  ): Promise<void> {
    const { requestId, workspaceRoot } = request;
    try {
      const active = this.agentManager.hasActiveInternalAgent({
        title: "Brand token generator",
        cwd: expandTilde(workspaceRoot),
        ownerUserId: this.ownerUserId,
      });
      this.emit({
        type: "brands/token-generation-status/response",
        payload: { requestId, active, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.emit({
        type: "brands/token-generation-status/response",
        payload: { requestId, active: false, error: message },
      });
    }
  }

  private async handleBrandGenerateLayoutRequest(
    request: Extract<SessionInboundMessage, { type: "brands/generate-layout" }>,
  ): Promise<void> {
    const { requestId, workspaceRoot, brandPath, prompt } = request;
    try {
      const result = await generateAndApplyLayout({
        agentManager: this.agentManager,
        workspaceRoot: expandTilde(workspaceRoot),
        brandPath: expandTilde(brandPath),
        prompt,
        logger: this.sessionLogger,
        ownerUserId: this.ownerUserId,
      });
      this.emit({
        type: "brands/generate-layout/response",
        payload: {
          requestId,
          roleContent: result.roleContent,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, brandPath }, "Failed to generate layout");
      this.emit({
        type: "brands/generate-layout/response",
        payload: {
          requestId,
          error: message,
        },
      });
    }
  }

  private async handleBrandLayoutQaNextRequest(
    request: Extract<SessionInboundMessage, { type: "brands/layout-qa/next" }>,
  ): Promise<void> {
    const { requestId, workspaceRoot, brandPath, conversation } = request;
    try {
      const result = await layoutQaNext({
        agentManager: this.agentManager,
        workspaceRoot: expandTilde(workspaceRoot),
        brandPath: expandTilde(brandPath),
        conversation,
        logger: this.sessionLogger,
        ownerUserId: this.ownerUserId,
      });
      this.emit({
        type: "brands/layout-qa/next/response",
        payload: {
          requestId,
          done: result.done,
          question: result.question,
          options: result.options,
          roleContent: result.roleContent,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, brandPath }, "Failed layout Q&A next");
      this.emit({
        type: "brands/layout-qa/next/response",
        payload: {
          requestId,
          done: false,
          error: message,
        },
      });
    }
  }

  private async handleBrandGenerateWireframeRequest(
    request: Extract<SessionInboundMessage, { type: "brands/generate-wireframe" }>,
  ): Promise<void> {
    const { requestId, workspaceRoot, brandPath } = request;
    try {
      const wireframe = await generateWireframe({
        agentManager: this.agentManager,
        workspaceRoot: expandTilde(workspaceRoot),
        brandPath: expandTilde(brandPath),
        logger: this.sessionLogger,
        ownerUserId: this.ownerUserId,
      });
      this.emit({
        type: "brands/generate-wireframe/response",
        payload: {
          requestId,
          wireframe,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, brandPath }, "Failed wireframe generation");
      this.emit({
        type: "brands/generate-wireframe/response",
        payload: {
          requestId,
          error: message,
        },
      });
    }
  }

  private async handleBrandAnalyzePhotographyRequest(
    request: Extract<SessionInboundMessage, { type: "brands/analyze-photography" }>,
  ): Promise<void> {
    const { requestId, workspaceRoot, brandPath, brief, referenceDescriptions } = request;
    try {
      const result = await analyzeAndApplyPhotographyStyle({
        agentManager: this.agentManager,
        workspaceRoot: expandTilde(workspaceRoot),
        brandPath: expandTilde(brandPath),
        brief,
        referenceDescriptions,
        logger: this.sessionLogger,
        ownerUserId: this.ownerUserId,
      });
      this.emit({
        type: "brands/analyze-photography/response",
        payload: {
          requestId,
          updatedCount: result.updatedCount,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, brandPath }, "Failed to analyze photography style");
      this.emit({
        type: "brands/analyze-photography/response",
        payload: {
          requestId,
          updatedCount: 0,
          error: message,
        },
      });
    }
  }

  private async handleRightFontLibraryRequest(
    request: Extract<SessionInboundMessage, { type: "rightfont/library" }>,
  ): Promise<void> {
    const { requestId, libraryPath } = request;
    try {
      const { readRightFontLibrary } = await import("./brand/rightfont-service.js");
      const data = readRightFontLibrary(expandTilde(libraryPath));
      this.emit({
        type: "rightfont/library/response",
        payload: { requestId, ...data, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error }, "Failed to read RightFont library");
      this.emit({
        type: "rightfont/library/response",
        payload: { requestId, families: [], collections: [], totalFonts: 0, error: message },
      });
    }
  }

  private async handleGoogleFontsCatalogRequest(
    request: Extract<SessionInboundMessage, { type: "google-fonts/catalog" }>,
  ): Promise<void> {
    const { requestId } = request;
    try {
      const { fetchGoogleFontsCatalog } = await import("./brand/google-fonts-service.js");
      const data = await fetchGoogleFontsCatalog();
      this.emit({
        type: "google-fonts/catalog/response",
        payload: { requestId, ...data, error: null },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error }, "Failed to fetch Google Fonts catalog");
      this.emit({
        type: "google-fonts/catalog/response",
        payload: { requestId, families: [], totalFonts: 0, error: message },
      });
    }
  }

  private async handleGoogleFontsDownloadRequest(
    request: Extract<SessionInboundMessage, { type: "google-fonts/download" }>,
  ): Promise<void> {
    const { requestId, workspaceRoot, family, weight, italic, targetName } = request;
    try {
      const { downloadGoogleFont } = await import("./brand/google-fonts-service.js");
      // Canonical font destination is .appostle/brand/assets/typefaces/<file>.woff2
      // — same `assets/<category>/` pattern every other brand asset (logo,
      // shape) uses via brands/asset-{copy,upload}. The brands publisher
      // reads from this exact location.
      const destDir = join(expandTilde(workspaceRoot), ".appostle", "brand", "assets", "typefaces");
      const result = await downloadGoogleFont({
        family,
        weight,
        italic: italic ?? false,
        destDir,
        targetName,
      });
      this.emit({
        type: "google-fonts/download/response",
        payload: {
          requestId,
          relativePath: `assets/typefaces/${result.fileName}`,
          absolutePath: result.filePath,
          error: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.sessionLogger.error({ err: error, family, weight }, "Failed to download Google Font");
      this.emit({
        type: "google-fonts/download/response",
        payload: { requestId, relativePath: "", absolutePath: "", error: message },
      });
    }
  }

  private emitTerminalsChangedSnapshot(input: {
    cwd: string;
    terminals: Array<{ id: string; name: string; title?: string }>;
  }): void {
    this.emit({
      type: "terminals_changed",
      payload: {
        cwd: input.cwd,
        terminals: input.terminals,
      },
    });
  }

  private filterStandaloneTerminals<T extends { id: string }>(terminals: T[]): T[] {
    return terminals;
  }

  private toTerminalInfo(terminal: Pick<TerminalSession, "id" | "name" | "getTitle">): {
    id: string;
    name: string;
    title?: string;
  } {
    const title = terminal.getTitle();
    return {
      id: terminal.id,
      name: terminal.name,
      ...(title ? { title } : {}),
    };
  }

  private handleTerminalsChanged(event: TerminalsChangedEvent): void {
    if (!this.subscribedTerminalDirectories.has(event.cwd)) {
      return;
    }

    this.emitTerminalsChangedSnapshot({
      cwd: event.cwd,
      terminals: this.filterStandaloneTerminals(event.terminals).map((terminal) => ({
        id: terminal.id,
        name: terminal.name,
        ...(terminal.title ? { title: terminal.title } : {}),
      })),
    });
  }

  private handleSubscribeTerminalsRequest(msg: SubscribeTerminalsRequest): void {
    this.subscribedTerminalDirectories.add(msg.cwd);
    void this.emitInitialTerminalsChangedSnapshot(msg.cwd);
  }

  private handleUnsubscribeTerminalsRequest(msg: UnsubscribeTerminalsRequest): void {
    this.subscribedTerminalDirectories.delete(msg.cwd);
  }

  private async emitInitialTerminalsChangedSnapshot(cwd: string): Promise<void> {
    if (!this.terminalManager || !this.subscribedTerminalDirectories.has(cwd)) {
      return;
    }

    try {
      const terminals = this.filterStandaloneTerminals(
        await this.terminalManager.getTerminals(cwd),
      );
      for (const terminal of terminals) {
        this.ensureTerminalExitSubscription(terminal);
      }

      if (!this.subscribedTerminalDirectories.has(cwd)) {
        return;
      }

      this.emitTerminalsChangedSnapshot({
        cwd,
        terminals: terminals.map((terminal) => this.toTerminalInfo(terminal)),
      });
    } catch (error) {
      this.sessionLogger.warn({ err: error, cwd }, "Failed to emit initial terminal snapshot");
    }
  }

  private async handleListTerminalsRequest(msg: ListTerminalsRequest): Promise<void> {
    if (!this.terminalManager) {
      this.emit({
        type: "list_terminals_response",
        payload: {
          ...(msg.cwd ? { cwd: msg.cwd } : {}),
          terminals: [],
          requestId: msg.requestId,
        },
      });
      return;
    }

    try {
      const terminals = this.filterStandaloneTerminals(
        typeof msg.cwd === "string"
          ? await this.terminalManager.getTerminals(msg.cwd)
          : await this.getAllTerminalSessions(),
      );
      for (const terminal of terminals) {
        this.ensureTerminalExitSubscription(terminal);
      }
      this.emit({
        type: "list_terminals_response",
        payload: {
          ...(msg.cwd ? { cwd: msg.cwd } : {}),
          terminals: terminals.map((terminal) => this.toTerminalInfo(terminal)),
          requestId: msg.requestId,
        },
      });
    } catch (error: any) {
      this.sessionLogger.error({ err: error, cwd: msg.cwd }, "Failed to list terminals");
      this.emit({
        type: "list_terminals_response",
        payload: {
          ...(msg.cwd ? { cwd: msg.cwd } : {}),
          terminals: [],
          requestId: msg.requestId,
        },
      });
    }
  }

  private async getAllTerminalSessions(): Promise<TerminalSession[]> {
    if (!this.terminalManager) {
      return [];
    }

    const directories = this.terminalManager.listDirectories();
    const terminalsByDirectory = await Promise.all(
      directories.map((cwd) => this.terminalManager!.getTerminals(cwd)),
    );
    return terminalsByDirectory.flat();
  }

  private async handleCreateTerminalRequest(msg: CreateTerminalRequest): Promise<void> {
    if (!this.terminalManager) {
      this.emit({
        type: "create_terminal_response",
        payload: {
          terminal: null,
          error: "Terminal manager not available",
          requestId: msg.requestId,
        },
      });
      return;
    }

    try {
      if (msg.agentId) {
        this.emit({
          type: "create_terminal_response",
          payload: {
            terminal: null,
            error: `Agent-backed terminals are no longer supported for agent ${msg.agentId}`,
            requestId: msg.requestId,
          },
        });
        return;
      }

      const session = await this.terminalManager.createTerminal({
        cwd: msg.cwd,
        name: msg.name,
        command: msg.command,
        args: msg.args,
      });
      this.ensureTerminalExitSubscription(session);
      this.emit({
        type: "create_terminal_response",
        payload: {
          terminal: {
            id: session.id,
            name: session.name,
            cwd: session.cwd,
            ...(session.getTitle() ? { title: session.getTitle() } : {}),
          },
          error: null,
          requestId: msg.requestId,
        },
      });
    } catch (error: any) {
      this.sessionLogger.error({ err: error, cwd: msg.cwd }, "Failed to create terminal");
      this.emit({
        type: "create_terminal_response",
        payload: {
          terminal: null,
          error: error.message,
          requestId: msg.requestId,
        },
      });
    }
  }

  private async handleSubscribeTerminalRequest(msg: SubscribeTerminalRequest): Promise<void> {
    if (!this.terminalManager) {
      this.emit({
        type: "subscribe_terminal_response",
        payload: {
          terminalId: msg.terminalId,
          error: "Terminal manager not available",
          requestId: msg.requestId,
        },
      });
      return;
    }

    const session = this.terminalManager.getTerminal(msg.terminalId);
    if (!session) {
      this.emit({
        type: "subscribe_terminal_response",
        payload: {
          terminalId: msg.terminalId,
          error: "Terminal not found",
          requestId: msg.requestId,
        },
      });
      return;
    }
    this.ensureTerminalExitSubscription(session);

    const slot = this.bindActiveTerminalStream(session);
    if (slot === null) {
      this.sessionLogger.warn(
        {
          terminalId: msg.terminalId,
          activeTerminalStreamCount: this.activeTerminalStreams.size,
        },
        "Terminal stream slot exhaustion",
      );
      this.emit({
        type: "subscribe_terminal_response",
        payload: {
          terminalId: msg.terminalId,
          error: "No terminal stream slots available",
          requestId: msg.requestId,
        },
      });
      return;
    }

    this.emit({
      type: "subscribe_terminal_response",
      payload: {
        terminalId: msg.terminalId,
        slot,
        error: null,
        requestId: msg.requestId,
      },
    });

    const activeStream = this.activeTerminalStreams.get(slot);
    if (activeStream) {
      this.trySendTerminalSnapshot(activeStream);
    }
  }

  private handleUnsubscribeTerminalRequest(msg: UnsubscribeTerminalRequest): void {
    this.detachTerminalStream(msg.terminalId, { emitExit: false });
  }

  private handleTerminalInput(msg: TerminalInput): void {
    if (!this.terminalManager) {
      return;
    }

    const session = this.terminalManager.getTerminal(msg.terminalId);
    if (!session) {
      this.sessionLogger.warn({ terminalId: msg.terminalId }, "Terminal not found for input");
      return;
    }
    this.ensureTerminalExitSubscription(session);

    if (msg.message.type === "resize") {
      const currentSize = session.getSize();
      if (currentSize.rows === msg.message.rows && currentSize.cols === msg.message.cols) {
        return;
      }
    }

    session.send(msg.message);
  }

  private killTrackedTerminal(terminalId: string, options?: { emitExit: boolean }): void {
    this.detachTerminalStream(terminalId, { emitExit: options?.emitExit ?? true });
    this.terminalManager?.killTerminal(terminalId);
  }

  private async killTerminalsUnderPath(rootPath: string): Promise<void> {
    return killWorktreeTerminalsUnderPath(
      {
        isPathWithinRoot: (pathRoot, candidatePath) =>
          this.isPathWithinRoot(pathRoot, candidatePath),
        killTrackedTerminal: (terminalId, options) => this.killTrackedTerminal(terminalId, options),
        detachTerminalStream: (terminalId, options) =>
          void this.detachTerminalStream(terminalId, options),
        sessionLogger: this.sessionLogger,
        terminalManager: this.terminalManager,
      },
      rootPath,
    );
  }

  private async handleKillTerminalRequest(msg: KillTerminalRequest): Promise<void> {
    const result = this.killTerminalForClose(msg.terminalId);
    this.emit({
      type: "kill_terminal_response",
      payload: {
        terminalId: result.terminalId,
        success: result.success,
        requestId: msg.requestId,
      },
    });
  }

  private killTerminalForClose(terminalId: string): { terminalId: string; success: boolean } {
    if (!this.terminalManager) {
      return {
        terminalId,
        success: false,
      };
    }

    this.killTrackedTerminal(terminalId, { emitExit: true });
    return {
      terminalId,
      success: true,
    };
  }

  private async handleCaptureTerminalRequest(msg: CaptureTerminalRequest): Promise<void> {
    if (!this.terminalManager) {
      this.emit({
        type: "capture_terminal_response",
        payload: {
          terminalId: msg.terminalId,
          lines: [],
          totalLines: 0,
          requestId: msg.requestId,
        },
      });
      return;
    }

    const session = this.terminalManager.getTerminal(msg.terminalId);
    if (!session) {
      this.emit({
        type: "capture_terminal_response",
        payload: {
          terminalId: msg.terminalId,
          lines: [],
          totalLines: 0,
          requestId: msg.requestId,
        },
      });
      return;
    }

    this.ensureTerminalExitSubscription(session);

    try {
      const capture = captureTerminalLines(session, {
        start: msg.start,
        end: msg.end,
        stripAnsi: msg.stripAnsi,
      });
      this.emit({
        type: "capture_terminal_response",
        payload: {
          terminalId: msg.terminalId,
          lines: capture.lines,
          totalLines: capture.totalLines,
          requestId: msg.requestId,
        },
      });
    } catch (error: any) {
      this.sessionLogger.error(
        { err: error, terminalId: msg.terminalId },
        "Failed to capture terminal",
      );
      this.emit({
        type: "capture_terminal_response",
        payload: {
          terminalId: msg.terminalId,
          lines: [],
          totalLines: 0,
          requestId: msg.requestId,
        },
      });
    }
  }

  private bindActiveTerminalStream(terminal: TerminalSession): number | null {
    if (!this.onBinaryMessage) {
      return null;
    }

    const existingSlot = this.terminalIdToSlot.get(terminal.id);
    if (typeof existingSlot === "number") {
      const existingStream = this.activeTerminalStreams.get(existingSlot);
      if (existingStream) {
        existingStream.needsSnapshot = true;
        return existingSlot;
      }
      this.terminalIdToSlot.delete(terminal.id);
    }

    const slot = this.allocateTerminalSlot();
    if (slot === null) {
      return null;
    }

    const activeStream: ActiveTerminalStream = {
      terminalId: terminal.id,
      slot,
      unsubscribe: () => {},
      needsSnapshot: true,
    };

    this.activeTerminalStreams.set(slot, activeStream);
    this.terminalIdToSlot.set(terminal.id, slot);

    activeStream.unsubscribe = terminal.subscribe((message) => {
      if (this.activeTerminalStreams.get(slot) !== activeStream) {
        return;
      }
      if (message.type === "snapshot" || message.type === "titleChange") {
        activeStream.needsSnapshot = true;
        this.trySendTerminalSnapshot(activeStream);
        return;
      }
      if (activeStream.needsSnapshot || message.data.length === 0) {
        return;
      }
      this.emitBinary(
        encodeTerminalStreamFrame({
          opcode: TerminalStreamOpcode.Output,
          slot,
          payload: new Uint8Array(Buffer.from(message.data, "utf8")),
        }),
      );
    });
    return slot;
  }

  private trySendTerminalSnapshot(activeStream: ActiveTerminalStream): void {
    if (
      this.activeTerminalStreams.get(activeStream.slot) !== activeStream ||
      !activeStream.needsSnapshot
    ) {
      return;
    }

    const terminal = this.terminalManager?.getTerminal(activeStream.terminalId);
    if (!terminal) {
      this.detachTerminalStream(activeStream.terminalId, { emitExit: true });
      return;
    }

    activeStream.needsSnapshot = false;
    this.emitBinary(
      encodeTerminalStreamFrame({
        opcode: TerminalStreamOpcode.Snapshot,
        slot: activeStream.slot,
        payload: encodeTerminalSnapshotPayload(terminal.getState()),
      }),
    );
  }

  private allocateTerminalSlot(): number | null {
    for (let attempt = 0; attempt < MAX_TERMINAL_STREAM_SLOTS; attempt += 1) {
      const slot = (this.nextTerminalSlot + attempt) % MAX_TERMINAL_STREAM_SLOTS;
      if (this.activeTerminalStreams.has(slot)) {
        continue;
      }
      this.nextTerminalSlot = (slot + 1) % MAX_TERMINAL_STREAM_SLOTS;
      return slot;
    }
    return null;
  }

  private detachTerminalStream(terminalId: string, options?: { emitExit: boolean }): boolean {
    const slot = this.terminalIdToSlot.get(terminalId);
    if (typeof slot !== "number") {
      return false;
    }
    const activeStream = this.activeTerminalStreams.get(slot);
    if (!activeStream) {
      this.terminalIdToSlot.delete(terminalId);
      return false;
    }
    this.activeTerminalStreams.delete(slot);
    this.terminalIdToSlot.delete(terminalId);
    try {
      activeStream.unsubscribe();
    } catch (error) {
      this.sessionLogger.warn({ err: error }, "Failed to unsubscribe terminal stream");
    }
    if (options?.emitExit) {
      this.emit({
        type: "terminal_stream_exit",
        payload: {
          terminalId: activeStream.terminalId,
        },
      });
    }
    return true;
  }

  private disposeTerminalSubscriptions(): void {
    for (const terminalId of [...this.terminalIdToSlot.keys()]) {
      this.detachTerminalStream(terminalId, { emitExit: false });
    }
  }
}

export function normalizeCheckoutPrStatusPayload(
  status: WorkspaceGitRuntimeSnapshot["github"]["pullRequest"],
): CheckoutPrStatusPayloadStatus | null {
  if (!status) {
    return null;
  }
  return {
    number: status.number,
    url: status.url,
    title: status.title,
    state: status.state,
    repoOwner: status.repoOwner,
    repoName: status.repoName,
    baseRefName: status.baseRefName,
    headRefName: status.headRefName,
    isMerged: status.isMerged,
    isDraft: status.isDraft ?? false,
    checks: status.checks ?? [],
    checksStatus: status.checksStatus,
    reviewDecision: status.reviewDecision,
  };
}

function isValidPullRequestTimelineIdentity(options: {
  prNumber: number;
  repoOwner: string;
  repoName: string;
}): boolean {
  if (!Number.isInteger(options.prNumber) || options.prNumber <= 0) {
    return false;
  }
  return isValidGitHubRepoSegment(options.repoOwner) && isValidGitHubRepoSegment(options.repoName);
}

function isValidGitHubRepoSegment(value: string): boolean {
  return /^[A-Za-z0-9._-]+$/.test(value);
}

function toPullRequestTimelinePayloadItem(
  item: PullRequestTimelineItem,
): PullRequestTimelinePayloadItem {
  const { authorUrl: _authorUrl, ...payload } = item;
  return payload;
}
